import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useAccount } from "../../context/AccountContext";
import { useAuth } from "../../context/AuthContext";

const DAYS_OPTIONS = [1, 3, 7, 30];

const STATUS_META = {
  PENDING:   { label: "Pending Approval", color: "#fd7e14", bg: "#fff3cd" },
  SNOOZED:   { label: "Snoozed",          color: "#856404", bg: "#fff3cd" },
  APPROVED:  { label: "Approved",         color: "#007bff", bg: "#cce5ff" },
  EXECUTING: { label: "Executing",        color: "#6f42c1", bg: "#e8d5ff" },
  COMPLETED: { label: "Completed",        color: "#28a745", bg: "#d4edda" },
  FAILED:    { label: "Failed",           color: "#dc3545", bg: "#fde2e2" },
  REJECTED:  { label: "Rejected",         color: "#6c757d", bg: "#e2e3e5" },
};

function humanizeMinutes(m) {
  if (m < 60) return `${m} minutes`;
  if (m === 60) return "1 hour";
  if (m % 60 === 0) return `${m / 60} hours`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

const HOLD_REASONS = [
  "Price move likely temporary — expecting reversal",
  "Adjustment not profitable enough at this point",
  "Increasing exposure would be too risky here",
  "Waiting for clearer market direction",
];

const ACTION_DESC = {
  SQUARE_OFF_FUTURES:   "Partial de-risk: closes exactly 1 lot of the future leg with the highest unrealised P&L (most profitable, or smallest loss), leaving the call and every other leg untouched.",
  SELL_INITIAL_CALL:    "Sell call hedge — either the initial placement or additional lots at the current strike to increase hedge and premium.",
  SELL_INITIAL_CSP:     "Sell the initial Cash-Secured Put (CSP) as configured in the strategy.",
  SELL_INITIAL_STRANGLE: "Fresh-start open — no stock held yet. Sells the initial call hedge and the initial Cash-Secured Put together in one action so both legs place atomically.",
  ROLL_DOWN_CALL:       "Roll the short call to a lower strike based on the updated pyramid structure.",
  ROLL_OUT_CALL:        "Roll the call (and CSP if active) to the next monthly expiry — triggered when premium has decayed and DTE is low.",
  ROLLUP_CALL:          "Close the deep-ITM short call and sell a higher-strike OTM call at the same expiry — maximises extrinsic value captured. May also roll the CSP to a closer, higher-premium put.",
  ROLLUP_CSP:           "Roll the short put to a higher strike (closer to LTP) within the same expiry, capturing higher extrinsic premium. Fires alongside ROLLUP_CALL.",
  ROLLDOWN_ITM_PUT:     "Roll the ITM short put down to an OTM strike that fetches more extrinsic value and keeps the strategy's net potential P&L positive.",
  SQUARE_OFF_ALL_OPTIONS: "Close all open option positions for this symbol.",
  SQUARE_OFF_CALLS:            "Close all open call (CE) option positions for this symbol.",
  SQUARE_OFF_CALL_AND_STOCK:   "Close the short call (at a loss) and sell all stock holdings to lock in the net profit before the call becomes ITM.",
  SQUARE_OFF_STOCKS:           "Sell the entire stock position for this symbol.",
  SITUATION_BRIEFING:        "Price band breached. System has evaluated rebalance options and selected the best action.",
  SQUARE_OFF_MAX_LOTS_DECAY: "Max lots reached on one side. Closes minimum lots needed to restore delta neutrality and book profit.",
  SQUARE_OFF_MAX_DECAY_POSITION: "Money management: exits the full position (stock/future + call + profitable puts) of the strategy with the highest net profit across legs that meets the minimum net profit threshold.",
  BUY_STOCK:            "Buy the next tranche of shares per the pyramid ladder.",
  BUY_FUTURE:           "Buy the next futures lot per the pyramid ladder — see Contract/Future Expiry below for which monthly contract this targets.",
  REBUILD_NEXT_CYCLE:   "Strategy is fully flat and the cycle's expiry has passed — starts a fresh monthly cycle on the chosen contract (current/next/far month).",
  SQUARE_OFF:           "Exit all positions for this strategy.",
  SQUARE_OFF_ONE_CALL_LOT:           "Profit-take: buy back one call lot (reducing the hedge) — ladder remains feasible with one fewer lot.",
  SQUARE_OFF_FUTURE_AND_CALL_LOT:    "Profit-take pair trade: close the most profitable futures leg and buy back one call lot simultaneously — ladder remains feasible after both reductions.",
  ROLL_FUTURE_LEG:                   "Roll expiring futures leg: close the contract nearest expiry and re-open the same quantity in the far-month contract.",
  TRIM_CALL_LOT_ROLLDOWN:            "Margin release: available margin is below the floor. Closes all call lots on the position with the highest calls-to-futures ratio and re-sells one lot fewer at a lower strike, same expiry — the ladder, the expiry profit floor and the hedge cap are all re-checked for the new structure.",
  ROLL_CYCLE_EARLY:                  "Early cycle roll: the call is almost fully decayed, the hedge is below its floor and no same-expiry fix exists — buys back every call lot, rolls every futures leg to the next expiry and sells the proposed next-expiry call, so both legs stay on one expiry.",
  REBUILD_LADDER_ON_DELTA_GAP:       "The calls' delta has largely caught up with the futures+stock already held — rebuilds the ladder (fresh gap/next-buy-price) instead of buying another future lot, since a rebuild still clears the CSM profit floor.",
};

// Display name overrides for action types whose identifier no longer matches their behaviour
const ACTION_DISPLAY_NAMES = {
  SQUARE_OFF_FUTURES:              "Partial De-risk: Trim 1 Future Lot",
  SQUARE_OFF_MAX_DECAY_POSITION:   "Exit Largest Profitable Position",
  SQUARE_OFF_FUTURES_MAX_CALL_DECAY_POSITION: "Exit Most-Decayed Futures Call Position",
  OPEN_STRADDLE:                   "Open Weekly Straddle",
  CLOSE_STRADDLE:                  "Close Weekly Straddle",
  ROLL_STRADDLE_NEXT_EXPIRY:       "Roll Straddle to Next Expiry",
  REBUILD_NEXT_CYCLE:              "Start Next Monthly Cycle",
  SQUARE_OFF_ONE_CALL_LOT:         "Profit-Take: Buy Back Call Lot",
  SQUARE_OFF_FUTURE_AND_CALL_LOT:  "Profit-Take: Close Future + Call Lot",
  ROLL_FUTURE_LEG:                 "Roll Expiring Futures Leg",
  REBUILD_LADDER_ON_DELTA_GAP:     "Rebuild Ladder (Delta Gap)",
  TRIM_CALL_LOT_ROLLDOWN:          "Margin Release: Trim 1 Call Lot + Roll Down",
  ROLL_CYCLE_EARLY:                "Early Cycle Roll: Calls + Futures to Next Expiry",
};

export default function SuggestionsPage() {
  const { selectedAccount, setSelectedAccount } = useAccount();
  const { user } = useAuth();

  const isAllAccounts = selectedAccount === "All Accounts";

  const [rows, setRows]       = useState([]);
  const [stats, setStats]     = useState({});
  const [loading, setLoading] = useState(false);
  const [filter, setFilter]   = useState("PENDING");
  const [days, setDays]       = useState(1);
  const [msg, setMsg]         = useState(null);
  const [acting, setActing]   = useState(null);   // id being actioned
  const [expanded, setExpanded] = useState({});
  const [cycleStatus, setCycleStatus] = useState(null);
  const [cycleRunning, setCycleRunning] = useState(false);
  const [selected, setSelected] = useState(new Set());  // bulk selection
  const [bulkActing, setBulkActing] = useState(false);

  const flash = (text, ok = true) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg(null), 3500);
  };

  const fetchAll = useCallback(async () => {
    if (!selectedAccount) return;
    setLoading(true);
    try {
      const [sRes, stRes, csRes] = await Promise.all([
        axios.get("/automation/suggestions/stats", { params: { username: selectedAccount, days } }),
        axios.get("/automation/suggestions",       { params: { username: selectedAccount, days } }),
        axios.get("/automation/cycle-status"),
      ]);
      setStats(sRes.data);
      setRows(stRes.data);
      setCycleStatus(csRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [selectedAccount, days]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const pollUntilDone = useCallback((intervalRef) => {
    const poll = setInterval(async () => {
      try {
        const res = await axios.get("/automation/cycle-status");
        setCycleStatus(res.data);
        if (!res.data.running) {
          clearInterval(poll);
          if (intervalRef) intervalRef.current = null;
          setCycleRunning(false);
          fetchAll();
        }
      } catch (e) {
        console.error(e);
      }
    }, 2000);
    setTimeout(() => { clearInterval(poll); setCycleRunning(false); }, 60000);
    return poll;
  }, [fetchAll]);

  const fetchCycleStatus = useCallback(async () => {
    try {
      const res = await axios.get("/automation/cycle-status");
      setCycleStatus(res.data);
      if (res.data.running) {
        setCycleRunning(true);
        pollUntilDone(null);
      } else {
        setCycleRunning(false);
      }
    } catch (e) {
      console.error(e);
    }
  }, [pollUntilDone]);

  useEffect(() => { fetchCycleStatus(); }, [fetchCycleStatus]);

  // Auto-refresh every 5 minutes so stats + Last Suggestion date stay current
  useEffect(() => {
    const timer = setInterval(fetchAll, 5 * 60 * 1000);
    return () => clearInterval(timer);
  }, [fetchAll]);

  const runCycle = async () => {
    setCycleRunning(true);
    try {
      await axios.post("/automation/run-cycle", { force: true });
      flash("Cycle started — refresh in a few seconds to see new suggestions.");
      pollUntilDone(null);
    } catch {
      flash("Failed to trigger cycle.", false);
      setCycleRunning(false);
    }
  };

  const doAction = async (id, action, minutes, qtyOverride, futureSymbolOverride, futureExpiryOverride) => {
    setActing(id);
    const row = rows.find(r => r.id === id);
    const acct = row?.username || selectedAccount;
    try {
      if (action === "approve") {
        const payload = { id, username: acct };
        if (qtyOverride !== undefined && qtyOverride !== null && qtyOverride !== "") {
          payload.qty_override = parseInt(qtyOverride, 10);
        }
        if (futureSymbolOverride) {
          payload.selected_future_symbol = futureSymbolOverride;
          if (futureExpiryOverride) payload.selected_future_expiry = futureExpiryOverride;
        }
        await axios.post("/automation/suggestions/approve", payload);
        // Optimistically flip status so the row vanishes from PENDING view immediately
        setRows(prev => prev.map(r => r.id === id ? { ...r, status: "APPROVED" } : r));
        flash("Approved — action queued for execution.");
      } else if (action === "reject") {
        await axios.post("/automation/suggestions/reject", { id, username: acct });
        setRows(prev => prev.map(r => r.id === id ? { ...r, status: "REJECTED" } : r));
        flash("Suggestion rejected.", true);
      } else if (action === "snooze") {
        await axios.post("/automation/suggestions/snooze", { id, minutes, username: acct });
        flash(`Snoozed for ${humanizeMinutes(minutes)}.`);
      }
      fetchAll();
    } catch {
      flash("Action failed.", false);
    } finally {
      setActing(null);
    }
  };

  const isSnoozed = r =>
    r.status === "PENDING" &&
    r.snooze_until &&
    new Date(r.snooze_until.replace(" ", "T") + "Z") > new Date();

  const displayed = filter === "ALL"
    ? rows
    : filter === "SNOOZED"
      ? rows.filter(r => isSnoozed(r))
      : filter === "PENDING"
        ? rows.filter(r => r.status === "PENDING" && !isSnoozed(r))
        : rows.filter(r => r.status === filter);

  // Companion ROLL_OUT_CSP / ROLLUP_CSP rows are hidden inside their call card —
  // exclude them from all counts so stats match what the user sees. Only suppress
  // when the CALL companion is still PENDING too — if it was already rejected/
  // cancelled, the CSP row renders standalone and must still be counted.
  const pairedCompanionIds = new Set(
    rows
      .filter(r => (r.action_type === "ROLL_OUT_CALL" || r.action_type === "ROLLUP_CALL") && r.status === "PENDING")
      .map(callRow => rows.find(c =>
        ((callRow.action_type === "ROLL_OUT_CALL" && c.action_type === "ROLL_OUT_CSP") ||
         (callRow.action_type === "ROLLUP_CALL"   && c.action_type === "ROLLUP_CSP")) &&
        c.status === "PENDING" &&
        c.symbol === callRow.symbol &&
        (c.username || "") === (callRow.username || "")
      ))
      .filter(Boolean)
      .map(c => c.id)
  );

  const pendingCount   = stats.PENDING || 0;
  const pendingVisible = displayed.filter(r => r.status === "PENDING" && !isSnoozed(r) && !pairedCompanionIds.has(r.id));
  const allPendingSelected =
    pendingVisible.length > 0 && pendingVisible.every(r => selected.has(r.id));

  const toggleSelect = (id) =>
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const selectAllPending = () =>
    setSelected(new Set(pendingVisible.map(r => r.id)));

  const clearSelection = () => setSelected(new Set());

  const doBulkAction = async (action, minutes) => {
    if (selected.size === 0) return;
    setBulkActing(true);
    const ids = [...selected];
    const rowMap = Object.fromEntries(rows.map(r => [r.id, r.username || selectedAccount]));
    try {
      await Promise.all(ids.map(id => {
        const acct = rowMap[id] || selectedAccount;
        if (action === "approve") return axios.post("/automation/suggestions/approve", { id, username: acct });
        if (action === "reject")  return axios.post("/automation/suggestions/reject",  { id, username: acct });
        if (action === "snooze")  return axios.post("/automation/suggestions/snooze",  { id, minutes, username: acct });
        return Promise.resolve();
      }));
      flash(
        action === "approve" ? `${ids.length} suggestion(s) approved.`
        : action === "reject" ? `${ids.length} suggestion(s) rejected.`
        : `${ids.length} suggestion(s) snoozed for ${humanizeMinutes(minutes)}.`
      );
      clearSelection();
      fetchAll();
    } catch {
      flash("Bulk action partially failed.", false);
    } finally {
      setBulkActing(false);
    }
  };

  return (
    <div style={{ padding: "16px 16px 30px", background: "#f4f6f9", minHeight: "100vh" }}>

      {msg && (
        <div style={{ ...toast, background: msg.ok ? "#28a745" : "#dc3545" }}>
          {msg.text}
        </div>
      )}

      {/* Header */}
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, gap: 10 }}>
        <div>
          <h2 style={pageTitle}>Trade Suggestions</h2>
          <p style={{ color: "#888", fontSize: 13, margin: 0 }}>
            Actions generated by the rule engine — review and approve before execution
          </p>
          {cycleStatus && (
            <div style={{ fontSize: 12, color: "#888", marginTop: 6 }}>
              Last cycle: {cycleStatus.last_run_at || "Never"}
              {cycleStatus.last_result && (
                <span style={{
                  marginLeft: 8,
                  padding: "2px 8px",
                  borderRadius: 10,
                  fontSize: 11,
                  fontWeight: 600,
                  background: cycleStatus.last_result === "ok" ? "#d4edda" : cycleStatus.last_result === "skipped" ? "#fff3cd" : "#fde2e2",
                  color:      cycleStatus.last_result === "ok" ? "#28a745" : cycleStatus.last_result === "skipped" ? "#856404" : "#dc3545",
                }}>
                  {cycleStatus.last_result.toUpperCase()}
                </span>
              )}
              {cycleStatus.last_error && cycleStatus.last_result !== "skipped" && (
                <span style={{ marginLeft: 8, color: "#dc3545", fontSize: 11 }}>
                  {cycleStatus.last_error.slice(0, 80)}
                </span>
              )}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={runCycle} style={{ ...refreshBtn, background: cycleRunning ? "#e9ecef" : "#2c3e50", color: cycleRunning ? "#888" : "#fff", border: "none" }} disabled={cycleRunning}>
            {cycleRunning ? "Running…" : "Run Cycle"}
          </button>
          <button onClick={fetchAll} style={refreshBtn} disabled={loading}>
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {/* Stats strip */}
      <div style={statsGrid}>
        <StatTile label="Last Suggestion" value={stats.last_rule_fire ? toIST(stats.last_rule_fire) : "Never"} color="#6c757d" />
        <StatTile label="Pending"   value={rows.filter(r => r.status === "PENDING" && !isSnoozed(r) && !pairedCompanionIds.has(r.id)).length} color="#fd7e14" highlight={pendingCount > 0} />
        <StatTile label="Approved"  value={stats.APPROVED  || 0} color="#007bff" />
        <StatTile label="Completed" value={stats.COMPLETED || 0} color="#28a745" />
        <StatTile label="Failed"    value={stats.FAILED    || 0} color="#dc3545" />
        <StatTile label="Rejected"  value={stats.REJECTED  || 0} color="#6c757d" />
      </div>

      {/* Filter row: status tabs + date range */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center", rowGap: 8 }}>
        <div style={filterBar}>
          {["ALL", "PENDING", "SNOOZED", "APPROVED", "EXECUTING", "COMPLETED", "FAILED", "REJECTED"].map(s => (
            <button
              key={s}
              onClick={() => { setFilter(s); clearSelection(); }}
              style={{
                ...filterTab,
                background: filter === s ? "#2c3e50" : "#fff",
                color:      filter === s ? "#fff"    : "#555",
                borderColor: filter === s ? "#2c3e50" : "#dee2e6"
              }}
            >
              {s === "ALL"
                ? `All (${rows.filter(r => !pairedCompanionIds.has(r.id)).length})`
                : s === "PENDING"
                  ? `PENDING (${rows.filter(r => r.status === "PENDING" && !isSnoozed(r) && !pairedCompanionIds.has(r.id)).length})`
                  : s === "SNOOZED"
                    ? `SNOOZED (${rows.filter(r => isSnoozed(r)).length})`
                    : `${s} (${stats[s] || 0})`}
            </button>
          ))}
        </div>

        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 12, color: "#888" }}>Show last</span>
          {DAYS_OPTIONS.map(d => (
            <button key={d} onClick={() => setDays(d)} style={{
              ...filterTab, padding: "5px 10px",
              background:  days === d ? "#2c3e50" : "#fff",
              color:       days === d ? "#fff"    : "#555",
              borderColor: days === d ? "#2c3e50" : "#dee2e6",
            }}>{d}d</button>
          ))}
        </div>
      </div>

      {/* Bulk action bar — only visible when something is selected */}
      {selected.size > 0 && (
        <div style={bulkBar}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 600, fontSize: 13, color: "#2c3e50" }}>
              {selected.size} selected
            </span>
            {!allPendingSelected && pendingVisible.length > 0 && (
              <button onClick={selectAllPending} style={bulkLinkBtn}>
                Select all {pendingVisible.length} pending
              </button>
            )}
            <button onClick={clearSelection} style={bulkLinkBtn}>Clear</button>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button onClick={() => doBulkAction("approve")} disabled={bulkActing} style={btnApprove}>
              {bulkActing ? "…" : `✓ Approve (${selected.size})`}
            </button>
            <button onClick={() => doBulkAction("reject")} disabled={bulkActing} style={btnReject}>
              {bulkActing ? "…" : `✕ Reject (${selected.size})`}
            </button>
            <button onClick={() => doBulkAction("snooze", 60)} disabled={bulkActing} style={btnSnooze}>
              Snooze 1h
            </button>
            <button onClick={() => doBulkAction("snooze", 480)} disabled={bulkActing} style={btnSnooze}>
              Snooze 8h
            </button>
          </div>
        </div>
      )}

      {/* Suggestion cards */}
      {loading && rows.length === 0 ? (
        <div style={emptyBox}>Loading suggestions…</div>
      ) : displayed.length === 0 ? (
        <div style={emptyBox}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>✓</div>
          <div style={{ fontWeight: 600, color: "#555" }}>
            {filter === "PENDING" ? "No pending suggestions" : "No suggestions yet"}
          </div>
          <div style={{ fontSize: 13, color: "#999", marginTop: 6 }}>
            Suggestions appear here after each rule engine cycle
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {(() => {
            // ROLLUP_CSP and ROLL_OUT_CSP rows are rendered inside their companion CALL card — skip them in the main loop.
            const pairedCspIds = new Set(
              displayed
                .filter(r => r.action_type === "ROLLUP_CALL" || r.action_type === "ROLL_OUT_CALL")
                .map(r => displayed.find(c =>
                  ((r.action_type === "ROLLUP_CALL"   && c.action_type === "ROLLUP_CSP") ||
                   (r.action_type === "ROLL_OUT_CALL" && c.action_type === "ROLL_OUT_CSP")) &&
                  c.symbol === r.symbol &&
                  (c.username || "") === (r.username || "")
                ))
                .filter(Boolean)
                .map(c => c.id)
            );
            return displayed.filter(r => !pairedCspIds.has(r.id));
          })().map(row => {
            const cspCompanion = row.action_type === "ROLLUP_CALL"
              ? (displayed.find(r =>
                  r.action_type === "ROLLUP_CSP" &&
                  r.symbol === row.symbol &&
                  (r.username || "") === (row.username || "")
                ) || null)
              : null;
            const rollOutCspCompanion = row.action_type === "ROLL_OUT_CALL"
              ? (displayed.find(r =>
                  r.action_type === "ROLL_OUT_CSP" &&
                  r.symbol === row.symbol &&
                  (r.username || "") === (row.username || "")
                ) || null)
              : null;
            return (
            row.action_type === "SITUATION_BRIEFING" ? (
              <SituationBriefingCard
                key={row.id}
                row={row}
                expanded={!!expanded[row.id]}
                onToggle={() => setExpanded(e => ({ ...e, [row.id]: !e[row.id] }))}
                onReject={() => doAction(row.id, "reject")}
                acting={acting === row.id}
                username={row.username || selectedAccount}
                showAccount={isAllAccounts}
                flash={flash}
                fetchAll={fetchAll}
              />
            ) : row.action_type === "SELL_INITIAL_STRANGLE" && row.metadata?.call && row.metadata?.csp ? (
              <SellInitialStrangleCard
                key={row.id}
                row={row}
                expanded={!!expanded[row.id]}
                onToggle={() => setExpanded(e => ({ ...e, [row.id]: !e[row.id] }))}
                onReject={() => doAction(row.id, "reject")}
                onSnooze={(m) => doAction(row.id, "snooze", m)}
                onOptimisticApprove={() => setRows(prev => prev.map(r => r.id === row.id ? { ...r, status: "APPROVED" } : r))}
                acting={acting === row.id}
                checked={selected.has(row.id)}
                onCheck={() => toggleSelect(row.id)}
                username={row.username || selectedAccount}
                showAccount={isAllAccounts}
                flash={flash}
                fetchAll={fetchAll}
              />
            ) : row.action_type === "SELL_INITIAL_CALL" && row.metadata?.call_ltp != null ? (
              <SellInitialCallCard
                key={row.id}
                row={row}
                expanded={!!expanded[row.id]}
                onToggle={() => setExpanded(e => ({ ...e, [row.id]: !e[row.id] }))}
                onReject={() => doAction(row.id, "reject")}
                onSnooze={(m) => doAction(row.id, "snooze", m)}
                onOptimisticApprove={() => setRows(prev => prev.map(r => r.id === row.id ? { ...r, status: "APPROVED" } : r))}
                acting={acting === row.id}
                checked={selected.has(row.id)}
                onCheck={() => toggleSelect(row.id)}
                username={row.username || selectedAccount}
                showAccount={isAllAccounts}
                flash={flash}
                fetchAll={fetchAll}
              />
            ) : row.action_type === "SELL_INITIAL_CSP" && row.metadata?.put_ltp != null ? (
              <SellInitialCspCard
                key={row.id}
                row={row}
                expanded={!!expanded[row.id]}
                onToggle={() => setExpanded(e => ({ ...e, [row.id]: !e[row.id] }))}
                onReject={() => doAction(row.id, "reject")}
                onSnooze={(m) => doAction(row.id, "snooze", m)}
                onOptimisticApprove={() => setRows(prev => prev.map(r => r.id === row.id ? { ...r, status: "APPROVED" } : r))}
                acting={acting === row.id}
                checked={selected.has(row.id)}
                onCheck={() => toggleSelect(row.id)}
                username={row.username || selectedAccount}
                showAccount={isAllAccounts}
                flash={flash}
                fetchAll={fetchAll}
              />
            ) : row.action_type === "ROLL_OUT_CALL" && (row.metadata?.proposed_call || row.metadata?.precompute_error || row.metadata?.partial_rollout || row.metadata?.current_call) ? (
              <RollOutCallCard
                key={row.id}
                row={row}
                cspCompanion={rollOutCspCompanion}
                expanded={!!expanded[row.id]}
                onToggle={() => setExpanded(e => ({ ...e, [row.id]: !e[row.id] }))}
                onReject={() => doAction(row.id, "reject")}
                onRejectCsp={rollOutCspCompanion ? () => doAction(rollOutCspCompanion.id, "reject") : null}
                onSnooze={(m) => doAction(row.id, "snooze", m)}
                onSnoozeCsp={rollOutCspCompanion ? (m) => doAction(rollOutCspCompanion.id, "snooze", m) : null}
                onOptimisticApprove={() => setRows(prev => prev.map(r => r.id === row.id ? { ...r, status: "APPROVED" } : r))}
                onOptimisticApproveCsp={rollOutCspCompanion ? () => setRows(prev => prev.map(r => r.id === rollOutCspCompanion.id ? { ...r, status: "APPROVED" } : r)) : null}
                acting={acting === row.id || (rollOutCspCompanion && acting === rollOutCspCompanion.id)}
                checked={selected.has(row.id)}
                onCheck={() => toggleSelect(row.id)}
                username={row.username || selectedAccount}
                showAccount={isAllAccounts}
                flash={flash}
                fetchAll={fetchAll}
              />
            ) : row.action_type === "ROLL_OUT_CSP" ? (
              <RollOutCspCard
                key={row.id}
                row={row}
                expanded={!!expanded[row.id]}
                onToggle={() => setExpanded(e => ({ ...e, [row.id]: !e[row.id] }))}
                onReject={() => doAction(row.id, "reject")}
                onSnooze={(m) => doAction(row.id, "snooze", m)}
                onOptimisticApprove={() => setRows(prev => prev.map(r => r.id === row.id ? { ...r, status: "APPROVED" } : r))}
                acting={acting === row.id}
                checked={selected.has(row.id)}
                onCheck={() => toggleSelect(row.id)}
                username={row.username || selectedAccount}
                showAccount={isAllAccounts}
                flash={flash}
                fetchAll={fetchAll}
              />
            ) : row.action_type === "SQUARE_OFF_STOCKS" && row.metadata?.has_active_call && row.metadata?.sell_qty ? (
              <SquareOffStocksCard
                key={row.id}
                row={row}
                expanded={!!expanded[row.id]}
                onToggle={() => setExpanded(e => ({ ...e, [row.id]: !e[row.id] }))}
                onReject={() => doAction(row.id, "reject")}
                onSnooze={(m) => doAction(row.id, "snooze", m)}
                onOptimisticApprove={() => setRows(prev => prev.map(r => r.id === row.id ? { ...r, status: "APPROVED" } : r))}
                acting={acting === row.id}
                checked={selected.has(row.id)}
                onCheck={() => toggleSelect(row.id)}
                username={row.username || selectedAccount}
                showAccount={isAllAccounts}
                flash={flash}
                fetchAll={fetchAll}
              />
            ) : row.action_type === "ROLLUP_CSP" && row.metadata?.new_put_symbol ? (
              <RollUpOptionsCard
                key={row.id}
                callRow={null}
                cspRow={row}
                expanded={!!expanded[row.id]}
                onToggle={() => setExpanded(e => ({ ...e, [row.id]: !e[row.id] }))}
                onRejectCall={null}
                onRejectCsp={() => doAction(row.id, "reject")}
                onSnoozeCall={null}
                onSnoozeCsp={(m) => doAction(row.id, "snooze", m)}
                onOptimisticApproveCall={null}
                onOptimisticApproveCsp={() => setRows(prev => prev.map(r => r.id === row.id ? { ...r, status: "APPROVED" } : r))}
                acting={acting === row.id}
                checked={selected.has(row.id)}
                onCheck={() => toggleSelect(row.id)}
                username={row.username || selectedAccount}
                showAccount={isAllAccounts}
                flash={flash}
                fetchAll={fetchAll}
              />
            ) : row.action_type === "ROLLUP_CALL" && row.metadata?.new_call_symbol ? (
              <RollUpOptionsCard
                key={row.id}
                callRow={row}
                cspRow={cspCompanion}
                expanded={!!expanded[row.id]}
                onToggle={() => setExpanded(e => ({ ...e, [row.id]: !e[row.id] }))}
                onRejectCall={() => doAction(row.id, "reject")}
                onRejectCsp={cspCompanion ? () => doAction(cspCompanion.id, "reject") : null}
                onSnoozeCall={(m) => doAction(row.id, "snooze", m)}
                onSnoozeCsp={cspCompanion ? (m) => doAction(cspCompanion.id, "snooze", m) : null}
                onOptimisticApproveCall={() => setRows(prev => prev.map(r => r.id === row.id ? { ...r, status: "APPROVED" } : r))}
                onOptimisticApproveCsp={cspCompanion ? () => setRows(prev => prev.map(r => r.id === cspCompanion.id ? { ...r, status: "APPROVED" } : r)) : null}
                acting={acting === row.id || (cspCompanion && acting === cspCompanion.id)}
                checked={selected.has(row.id)}
                onCheck={() => toggleSelect(row.id)}
                username={row.username || selectedAccount}
                showAccount={isAllAccounts}
                flash={flash}
                fetchAll={fetchAll}
              />
            ) : row.action_type === "ROLLDOWN_ITM_PUT" ? (
              <RolldownItmPutCard
                key={row.id}
                row={row}
                expanded={!!expanded[row.id]}
                onToggle={() => setExpanded(e => ({ ...e, [row.id]: !e[row.id] }))}
                onReject={() => doAction(row.id, "reject")}
                onSnooze={(m) => doAction(row.id, "snooze", m)}
                onOptimisticApprove={() => setRows(prev => prev.map(r => r.id === row.id ? { ...r, status: "APPROVED" } : r))}
                acting={acting === row.id}
                checked={selected.has(row.id)}
                onCheck={() => toggleSelect(row.id)}
                username={row.username || selectedAccount}
                showAccount={isAllAccounts}
                flash={flash}
                fetchAll={fetchAll}
              />
            ) : row.action_type === "ROLL_DOWN_CALL" && (row.metadata?.proposed_call || row.metadata?.precompute_error) ? (
              <RollDownCallCard
                key={row.id}
                row={row}
                expanded={!!expanded[row.id]}
                onToggle={() => setExpanded(e => ({ ...e, [row.id]: !e[row.id] }))}
                onReject={() => doAction(row.id, "reject")}
                onSnooze={(m) => doAction(row.id, "snooze", m)}
                onOptimisticApprove={() => setRows(prev => prev.map(r => r.id === row.id ? { ...r, status: "APPROVED" } : r))}
                acting={acting === row.id}
                checked={selected.has(row.id)}
                onCheck={() => toggleSelect(row.id)}
                username={row.username || selectedAccount}
                showAccount={isAllAccounts}
                flash={flash}
                fetchAll={fetchAll}
              />
            ) : row.action_type === "ADD_CALL_LOT" ? (
              <AddCallLotCard
                key={row.id}
                row={row}
                expanded={!!expanded[row.id]}
                onToggle={() => setExpanded(e => ({ ...e, [row.id]: !e[row.id] }))}
                onReject={() => doAction(row.id, "reject")}
                onSnooze={(m) => doAction(row.id, "snooze", m)}
                onOptimisticApprove={() => setRows(prev => prev.map(r => r.id === row.id ? { ...r, status: "APPROVED" } : r))}
                acting={acting === row.id}
                checked={selected.has(row.id)}
                onCheck={() => toggleSelect(row.id)}
                username={row.username || selectedAccount}
                showAccount={isAllAccounts}
                flash={flash}
                fetchAll={fetchAll}
              />
            ) : row.action_type === "SQUARE_OFF_ITM_ROLLUP" ? (
              <ItmRollupCard
                key={row.id}
                row={row}
                expanded={!!expanded[row.id]}
                onToggle={() => setExpanded(e => ({ ...e, [row.id]: !e[row.id] }))}
                onReject={() => doAction(row.id, "reject")}
                onSnooze={(m) => doAction(row.id, "snooze", m)}
                onOptimisticApprove={() => setRows(prev => prev.map(r => r.id === row.id ? { ...r, status: "APPROVED" } : r))}
                acting={acting === row.id}
                checked={selected.has(row.id)}
                onCheck={() => toggleSelect(row.id)}
                username={row.username || selectedAccount}
                showAccount={isAllAccounts}
                flash={flash}
                fetchAll={fetchAll}
              />
            ) : row.action_type === "ROLLOUT_DHSS_STRANGLE" ? (
              <RolloutDhssStrangleCard
                key={row.id}
                row={row}
                expanded={!!expanded[row.id]}
                onToggle={() => setExpanded(e => ({ ...e, [row.id]: !e[row.id] }))}
                onReject={() => doAction(row.id, "reject")}
                onSnooze={(m) => doAction(row.id, "snooze", m)}
                onOptimisticApprove={() => setRows(prev => prev.map(r => r.id === row.id ? { ...r, status: "APPROVED" } : r))}
                acting={acting === row.id}
                checked={selected.has(row.id)}
                onCheck={() => toggleSelect(row.id)}
                username={row.username || selectedAccount}
                showAccount={isAllAccounts}
                flash={flash}
                fetchAll={fetchAll}
              />
            ) : (
              <SuggestionCard
                key={row.id}
                row={row}
                expanded={!!expanded[row.id]}
                onToggle={() => setExpanded(e => ({ ...e, [row.id]: !e[row.id] }))}
                onApprove={(qty, futureSymbol, futureExpiry) => doAction(row.id, "approve", undefined, qty, futureSymbol, futureExpiry)}
                onReject={() => doAction(row.id, "reject")}
                onSnooze={(m) => doAction(row.id, "snooze", m)}
                acting={acting === row.id}
                checked={selected.has(row.id)}
                onCheck={() => toggleSelect(row.id)}
                username={row.username || selectedAccount}
                showAccount={isAllAccounts}
              />
            )
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── Card ────────────────────────────────────────────────────── */

// Action types that close existing positions (show position preview when pending)
const SQUAREOFF_TYPES = new Set([
  "SQUARE_OFF", "SQUARE_OFF_ALL_OPTIONS", "SQUARE_OFF_CALLS", "SQUARE_OFF_CALL_AND_STOCK", "SQUARE_OFF_STOCKS",
  "SQUARE_OFF_DECAYED_OPTION", "SQUARE_OFF_DEEPLY_ITM", "SQUARE_OFF_EXPIRY",
  "SQUARE_OFF_ITM_ROLLUP", "SQUARE_OFF_MAX_DECAY_POSITION", "SQUARE_OFF_MAX_LOTS",
  "SQUARE_OFF_MAX_LOTS_DECAY", "SQUARE_OFF_DELTA_EXCESS", "SQUARE_OFF_ITM_ONLY",
  "SQUARE_OFF_STRANGLE", "CLOSE_AND_ROLL_FAR_OTM",
  "MAKE_DELTA_NEUTRAL", "CLOSE_STRADDLE",
  "SQUARE_OFF_ONE_CALL_LOT", "SQUARE_OFF_FUTURE_AND_CALL_LOT", "ROLL_FUTURE_LEG",
]);

// ─────────────────────────────────────────────────────────────────────────────
// SELL INITIAL CALL — rich card
// ─────────────────────────────────────────────────────────────────────────────
function SellInitialCallCard({ row, expanded, onToggle, onReject, onSnooze, onOptimisticApprove, acting, checked, onCheck, username, showAccount, flash, fetchAll }) {
  const isActivelySnoozed = row.status === "PENDING"
    && row.snooze_until
    && new Date(row.snooze_until.replace(" ", "T") + "Z") > new Date();
  const displayStatus = isActivelySnoozed ? "SNOOZED" : row.status;
  const sm = STATUS_META[displayStatus] || STATUS_META.PENDING;
  const [submitting, setSubmitting] = useState(false);

  const meta   = row.metadata || {};
  const ladder = meta.ladder  || [];
  const ltp    = meta.underlying_ltp;

  const fmtRs = v => v != null ? `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}` : "—";
  const fmtN  = (v, d = 3) => v != null ? Number(v).toFixed(d) : "—";

  const callQty    = (meta.quantity || 1) * (meta.lot_size || 1);
  const callLtp    = meta.call_ltp    || 0;
  const callStrike = meta.call_strike || 0;

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      await axios.post("/automation/suggestions/approve", { id: row.id, username });
      onOptimisticApprove?.();
      flash("Call hedge approved — order queued");
      fetchAll();
    } catch { flash("Approval failed.", false); }
    finally   { setSubmitting(false); }
  };

  const thS = { padding: "6px 10px", textAlign: "left", fontWeight: 600, fontSize: 11, borderBottom: "2px solid #dee2e6", color: "#555" };
  const tdS = { padding: "5px 10px", fontSize: 12, borderBottom: "1px solid #f0f0f0" };

  return (
    <div style={{ ...card, border: "2px solid #28a745", outline: checked ? "2px solid #007bff" : "none" }}>
      <div style={cardTop}>
        {row.status === "PENDING" && (
          <input type="checkbox" checked={!!checked} onChange={onCheck} onClick={e => e.stopPropagation()}
            style={{ marginRight: 10, width: 15, height: 15, cursor: "pointer", flexShrink: 0, accentColor: "#007bff" }} />
        )}
        {row.status !== "PENDING" && <div style={{ width: 25, flexShrink: 0 }} />}
        <div style={{ display: "flex", alignItems: "center", flex: 1, gap: 12, cursor: "pointer" }} onClick={onToggle}>
          <div style={{ ...actionIcon, background: "#d4edda", color: "#155724", fontSize: 22 }}>📞</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: "#155724" }}>
              Sell Call Hedge — {row.symbol}
            </div>
            <div style={{ fontSize: 12, color: "#555", marginTop: 2 }}>
              {showAccount && row.username && (
                <span style={{ marginRight: 8, padding: "1px 7px", borderRadius: 8, background: "#e9ecef", color: "#495057", fontSize: 11, fontWeight: 600 }}>
                  {row.username}
                </span>
              )}
              {meta.call_symbol && <span style={{ color: "#0055aa", fontWeight: 600 }}>{meta.call_symbol}</span>}
              {meta.call_strike != null && <span style={{ marginLeft: 8, color: "#6c757d" }}>Strike ₹{meta.call_strike}</span>}
              {ltp != null && <span style={{ marginLeft: 8, color: "#888" }}>LTP ₹{ltp}</span>}
            </div>
          </div>
          <div style={{ textAlign: "right", minWidth: 140 }}>
            <span style={{ ...statusBadge, background: sm.bg, color: sm.color }}>{sm.label}</span>
            <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>{toIST(row.created_at)}</div>
          </div>
          <div style={{ marginLeft: 12, color: "#ccc", fontSize: 18 }}>{expanded ? "▲" : "▼"}</div>
        </div>
        <NavButtons symbol={row.symbol} />
      </div>

      {expanded && (
        <div style={cardBody}>
          {row.status === "FAILED" && row.error_message && (
            <div style={{ background: "#fde2e2", border: "1px solid #dc3545", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#dc3545" }}>
              <strong>Failed:</strong> {row.error_message}
            </div>
          )}

          {/* Call details */}
          <div style={{ background: "#d4edda", border: "1px solid #b1dfbb", borderRadius: 8, padding: "12px 14px", marginBottom: 14 }}>
            <div style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, color: "#0a5c25", marginBottom: 8, letterSpacing: 0.6 }}>
              Call Hedge to Sell
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#222", marginBottom: 6 }}>{meta.call_symbol || meta.symbol || "—"}</div>
            <div style={{ display: "flex", gap: 20, flexWrap: "wrap", fontSize: 13, color: "#333" }}>
              {meta.call_strike  != null && <span><strong>Strike:</strong> {fmtRs(meta.call_strike)}</span>}
              {meta.call_ltp     != null && <span><strong>LTP:</strong> <span style={{ color: "#28a745", fontWeight: 700 }}>{fmtRs(meta.call_ltp)}</span></span>}
              {meta.quantity     != null && <span><strong>Lots:</strong> {meta.quantity}</span>}
              {meta.call_delta   != null && <span><strong>Delta:</strong> {fmtN(meta.call_delta)}</span>}
              {meta.hedge_percent!= null && (
                <span style={{ fontWeight: 600, color: "#0055aa" }}><strong>Hedge:</strong> {Number(meta.hedge_percent).toFixed(1)}%</span>
              )}
              {meta.existing_stock_lots != null && (
                <span><strong>Stock Qty:</strong> {meta.existing_stock_lots} lots{meta.existing_stock_qty != null && meta.existing_stock_qty !== meta.existing_stock_lots ? ` (${meta.existing_stock_qty} shares)` : ""}</span>
              )}
              {meta.reference_avg_price != null && <span><strong>Avg Price:</strong> {fmtRs(meta.reference_avg_price)}</span>}
            </div>
          </div>

          {meta.stock_excluded && (
            <div style={{ background: "#fde2e2", border: "1px solid #f5c6cb", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#842029" }}>
              ⚠️ <strong>Stock excluded from this calculation.</strong> {meta.stock_excluded_note || "The existing stock position's loss made a blended calculation infeasible, so this ladder was built treating the position as pure futures+options."}
            </div>
          )}

          {/* Accumulation ladder */}
          {ladder.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#555", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
                Accumulation Ladder
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 14 }}>
                <thead>
                  <tr style={{ background: "#f5f7fa" }}>
                    <th style={thS}>Step</th>
                    <th style={thS}>Buy Price</th>
                    <th style={thS}>% from Spot</th>
                    <th style={thS}>Step Gap</th>
                    <th style={thS}>Qty</th>
                    <th style={thS}>Investment</th>
                    <th style={thS}>Avg After Step</th>
                    <th style={{ ...thS, color: "#6c757d" }}>Stock P&amp;L</th>
                    <th style={{ ...thS, color: "#6c757d" }}>Option P&amp;L</th>
                    <th style={{ ...thS, color: "#17a2b8" }}>P&amp;L at Buy Price</th>
                  </tr>
                </thead>
                <tbody>
                  {ladder.map((s, i) => {
                    const pctFromLtp  = ltp ? ((s.buy_price - ltp) / ltp * 100) : null;
                    const stepGapPct  = i === 0
                      ? (ltp ? ((ltp - s.buy_price) / ltp * 100) : null)
                      : ((ladder[i - 1].buy_price - s.buy_price) / ladder[i - 1].buy_price * 100);
                    const investment  = s.buy_price * s.qty;
                    // Mark-to-market at this step's OWN buy price, not the fixed call
                    // strike — stock P&L must grow with price, and option P&L must
                    // erode once price passes the strike (ITM), or every row shows
                    // the same misleading snapshot (confirmed live: EXIDEIND).
                    const stockPnl    = (s.buy_price - s.avg_price) * s.total_qty;
                    const callIntrinsic = callStrike > 0 ? Math.max(0, s.buy_price - callStrike) * callQty : 0;
                    const callPnl     = callLtp * callQty - callIntrinsic;
                    const netPnl      = stockPnl + callPnl;
                    const pnlClr      = netPnl >= 0 ? "#28a745" : "#dc3545";
                    const fmtPnl      = v => `${v >= 0 ? "+" : "-"}₹${Math.abs(Math.round(v)).toLocaleString("en-IN")}`;
                    return (
                      <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                        <td style={tdS}>{s.step ?? i + 1}</td>
                        <td style={{ ...tdS, fontWeight: 600 }}>{fmtRs(s.buy_price)}</td>
                        <td style={{ ...tdS, color: pctFromLtp == null ? "#999" : pctFromLtp >= 0 ? "#28a745" : "#dc3545" }}>
                          {pctFromLtp != null ? `${pctFromLtp >= 0 ? "+" : ""}${pctFromLtp.toFixed(1)}%` : "—"}
                        </td>
                        <td style={{ ...tdS, color: "#6c757d" }}>
                          {stepGapPct != null ? `${stepGapPct.toFixed(2)}%` : "—"}
                        </td>
                        <td style={tdS}>{s.qty}</td>
                        <td style={tdS}>₹{Math.round(investment).toLocaleString("en-IN")}</td>
                        <td style={tdS}>{fmtRs(s.avg_price)}</td>
                        <td style={{ ...tdS, color: stockPnl >= 0 ? "#28a745" : "#dc3545" }}>{fmtPnl(stockPnl)}</td>
                        <td style={{ ...tdS, color: "#28a745" }}>{fmtPnl(callPnl)}</td>
                        <td style={{ ...tdS, fontWeight: 700, color: pnlClr }}>{fmtPnl(netPnl)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}

          {/* Tail risk summary */}
          {meta.tail_risk_pnl != null && (
            <div style={{ background: meta.tail_risk_pnl >= 0 ? "#d4edda" : "#fde2e2", border: `1px solid ${meta.tail_risk_pnl >= 0 ? "#b1dfbb" : "#f5c6cb"}`, borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>
              <strong>Tail Risk P&amp;L</strong> (if fully accumulated &amp; assigned at strike):{" "}
              <span style={{ fontWeight: 700, color: meta.tail_risk_pnl >= 0 ? "#155724" : "#721c24", fontSize: 14 }}>
                {meta.tail_risk_pnl >= 0 ? "+" : "-"}₹{Math.abs(Math.round(meta.tail_risk_pnl)).toLocaleString("en-IN")}
              </span>
            </div>
          )}

          {row.status === "PENDING" && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button onClick={handleApprove} disabled={submitting}
                style={{ ...btnApprove, background: "#28a745", opacity: submitting ? 0.5 : 1 }}>
                {submitting ? "Executing…" : "✓ Approve Sell Call"}
              </button>
              <button onClick={onReject} disabled={submitting} style={btnReject}>✕ Reject</button>
              <button onClick={() => onSnooze(60)}  disabled={submitting} style={btnSnooze}>Snooze 1h</button>
              <button onClick={() => onSnooze(480)} disabled={submitting} style={btnSnooze}>Snooze 8h</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SELL INITIAL CSP — rich card
// ─────────────────────────────────────────────────────────────────────────────
function SellInitialCspCard({ row, expanded, onToggle, onReject, onSnooze, onOptimisticApprove, acting, checked, onCheck, username, showAccount, flash, fetchAll }) {
  const isActivelySnoozed = row.status === "PENDING"
    && row.snooze_until
    && new Date(row.snooze_until.replace(" ", "T") + "Z") > new Date();
  const displayStatus = isActivelySnoozed ? "SNOOZED" : row.status;
  const sm = STATUS_META[displayStatus] || STATUS_META.PENDING;
  const [submitting, setSubmitting] = useState(false);

  const meta = row.metadata || {};
  const ltp  = meta.underlying_ltp;

  const fmtRs = v => v != null ? `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}` : "—";
  const fmtN  = (v, d = 3) => v != null ? Number(v).toFixed(d) : "—";

  const distPct      = meta.put_distance_pct;
  const distColor    = distPct != null && distPct < 5 ? "#dc3545" : "#155724";

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      await axios.post("/automation/suggestions/approve", { id: row.id, username });
      onOptimisticApprove?.();
      flash("CSP approved — order queued");
      fetchAll();
    } catch { flash("Approval failed.", false); }
    finally   { setSubmitting(false); }
  };

  return (
    <div style={{ ...card, border: "2px solid #20c997", outline: checked ? "2px solid #007bff" : "none" }}>
      <div style={cardTop}>
        {row.status === "PENDING" && (
          <input type="checkbox" checked={!!checked} onChange={onCheck} onClick={e => e.stopPropagation()}
            style={{ marginRight: 10, width: 15, height: 15, cursor: "pointer", flexShrink: 0, accentColor: "#007bff" }} />
        )}
        {row.status !== "PENDING" && <div style={{ width: 25, flexShrink: 0 }} />}
        <div style={{ display: "flex", alignItems: "center", flex: 1, gap: 12, cursor: "pointer" }} onClick={onToggle}>
          <div style={{ ...actionIcon, background: "#d1f5ee", color: "#0d6e54", fontSize: 22 }}>📤</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: "#0d6e54" }}>
              Sell Cash-Secured Put — {row.symbol}
            </div>
            <div style={{ fontSize: 12, color: "#555", marginTop: 2 }}>
              {showAccount && row.username && (
                <span style={{ marginRight: 8, padding: "1px 7px", borderRadius: 8, background: "#e9ecef", color: "#495057", fontSize: 11, fontWeight: 600 }}>
                  {row.username}
                </span>
              )}
              {meta.csp_symbol && <span style={{ color: "#0d6e54", fontWeight: 600 }}>{meta.csp_symbol}</span>}
              {meta.put_strike != null && <span style={{ marginLeft: 8, color: "#6c757d" }}>Strike ₹{meta.put_strike}</span>}
              {distPct        != null && (
                <span style={{ marginLeft: 8, fontWeight: 600, color: distColor }}>
                  {distPct.toFixed(1)}% below spot
                </span>
              )}
              {meta.put_bid != null && meta.put_ask != null && (
                <span style={{ marginLeft: 8, color: "#6c757d" }}>
                  Bid <strong>₹{meta.put_bid}</strong> / Ask <strong>₹{meta.put_ask}</strong>
                  {meta.put_spread_pct != null && (
                    <span style={{ marginLeft: 4, color: meta.put_spread_pct > 8 ? "#dc3545" : "#28a745", fontWeight: 600 }}>
                      ({meta.put_spread_pct.toFixed(1)}% spread)
                    </span>
                  )}
                </span>
              )}
            </div>
          </div>
          <div style={{ textAlign: "right", minWidth: 140 }}>
            <span style={{ ...statusBadge, background: sm.bg, color: sm.color }}>{sm.label}</span>
            <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>{toIST(row.created_at)}</div>
          </div>
          <div style={{ marginLeft: 12, color: "#ccc", fontSize: 18 }}>{expanded ? "▲" : "▼"}</div>
        </div>
        <NavButtons symbol={row.symbol} />
      </div>

      {expanded && (
        <div style={cardBody}>
          {row.status === "FAILED" && row.error_message && (
            <div style={{ background: "#fde2e2", border: "1px solid #dc3545", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#dc3545" }}>
              <strong>Failed:</strong> {row.error_message}
            </div>
          )}

          {/* Put details */}
          <div style={{ background: "#d1f5ee", border: "1px solid #9ee8d1", borderRadius: 8, padding: "12px 14px", marginBottom: 14 }}>
            <div style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, color: "#0d6e54", marginBottom: 8, letterSpacing: 0.6 }}>
              Cash-Secured Put to Sell
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#222", marginBottom: 10 }}>{meta.csp_symbol || meta.symbol || "—"}</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: "8px 20px", fontSize: 13 }}>
              {meta.put_strike  != null && (
                <div>
                  <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", fontWeight: 600 }}>Strike</div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{fmtRs(meta.put_strike)}</div>
                </div>
              )}
              {meta.put_ltp     != null && (
                <div>
                  <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", fontWeight: 600 }}>Option LTP (Premium)</div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: "#28a745" }}>{fmtRs(meta.put_ltp)}</div>
                  {meta.lot_size != null && (
                    <div style={{ fontSize: 11, color: "#555" }}>
                      = ₹{Math.round(meta.put_ltp * meta.lot_size).toLocaleString("en-IN")} / lot
                    </div>
                  )}
                </div>
              )}
              {meta.put_delta   != null && (
                <div>
                  <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", fontWeight: 600 }}>Delta</div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{fmtN(meta.put_delta)}</div>
                </div>
              )}
              {meta.put_distance_pct != null && (
                <div>
                  <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", fontWeight: 600 }}>Distance from Spot</div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: distColor }}>
                    {meta.put_distance_pct.toFixed(1)}%
                  </div>
                  {ltp != null && meta.put_strike != null && (
                    <div style={{ fontSize: 11, color: "#555" }}>
                      Spot ₹{ltp} → Strike ₹{meta.put_strike}
                    </div>
                  )}
                </div>
              )}
              {ltp              != null && (
                <div>
                  <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", fontWeight: 600 }}>Spot Price</div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{fmtRs(ltp)}</div>
                </div>
              )}
              {meta.quantity    != null && (
                <div>
                  <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", fontWeight: 600 }}>Lots</div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{meta.quantity}</div>
                  {meta.lot_size != null && (
                    <div style={{ fontSize: 11, color: "#555" }}>{meta.quantity * meta.lot_size} shares</div>
                  )}
                </div>
              )}
              {meta.put_bid != null && (
                <div>
                  <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", fontWeight: 600 }}>Bid</div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{fmtRs(meta.put_bid)}</div>
                </div>
              )}
              {meta.put_ask != null && (
                <div>
                  <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", fontWeight: 600 }}>Ask</div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{fmtRs(meta.put_ask)}</div>
                </div>
              )}
              {meta.put_spread_pct != null && (
                <div>
                  <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", fontWeight: 600 }}>Bid-Ask Spread</div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: meta.put_spread_pct > 8 ? "#dc3545" : "#28a745" }}>
                    {fmtN(meta.put_spread_pct, 1)}%
                  </div>
                </div>
              )}
              {meta.put_oi != null && (
                <div>
                  <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", fontWeight: 600 }}>Open Interest</div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{Number(meta.put_oi).toLocaleString("en-IN")}</div>
                </div>
              )}
            </div>
          </div>

          {row.status === "PENDING" && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button onClick={handleApprove} disabled={submitting}
                style={{ ...btnApprove, background: "#20c997", opacity: submitting ? 0.5 : 1 }}>
                {submitting ? "Executing…" : "✓ Approve Sell Put"}
              </button>
              <button onClick={onReject} disabled={submitting} style={btnReject}>✕ Reject</button>
              <button onClick={() => onSnooze(60)}  disabled={submitting} style={btnSnooze}>Snooze 1h</button>
              <button onClick={() => onSnooze(480)} disabled={submitting} style={btnSnooze}>Snooze 8h</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SELL INITIAL STRANGLE (fresh-start: call + CSP together) — rich card
// ─────────────────────────────────────────────────────────────────────────────
function SellInitialStrangleCard({ row, expanded, onToggle, onReject, onSnooze, onOptimisticApprove, acting, checked, onCheck, username, showAccount, flash, fetchAll }) {
  const isActivelySnoozed = row.status === "PENDING"
    && row.snooze_until
    && new Date(row.snooze_until.replace(" ", "T") + "Z") > new Date();
  const displayStatus = isActivelySnoozed ? "SNOOZED" : row.status;
  const sm = STATUS_META[displayStatus] || STATUS_META.PENDING;
  const [submitting, setSubmitting] = useState(false);

  const raw    = row.metadata || {};
  const call   = raw.call || {};
  const csp    = raw.csp  || {};
  const ladder = call.ladder || [];
  const ltp    = call.underlying_ltp ?? csp.underlying_ltp;

  const fmtRs = v => v != null ? `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}` : "—";
  const fmtN  = (v, d = 3) => v != null ? Number(v).toFixed(d) : "—";

  const callQty    = (call.quantity || 1) * (call.lot_size || 1);
  const callLtp    = call.call_ltp    || 0;
  const callStrike = call.call_strike || 0;

  const distPct   = csp.put_distance_pct;
  const distColor = distPct != null && distPct < 5 ? "#dc3545" : "#155724";

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      await axios.post("/automation/suggestions/approve", { id: row.id, username });
      onOptimisticApprove?.();
      flash("Strangle approved — call + CSP orders queued");
      fetchAll();
    } catch { flash("Approval failed.", false); }
    finally   { setSubmitting(false); }
  };

  const thS = { padding: "6px 10px", textAlign: "left", fontWeight: 600, fontSize: 11, borderBottom: "2px solid #dee2e6", color: "#555" };
  const tdS = { padding: "5px 10px", fontSize: 12, borderBottom: "1px solid #f0f0f0" };

  return (
    <div style={{ ...card, border: "2px solid #6f42c1", outline: checked ? "2px solid #007bff" : "none" }}>
      <div style={cardTop}>
        {row.status === "PENDING" && (
          <input type="checkbox" checked={!!checked} onChange={onCheck} onClick={e => e.stopPropagation()}
            style={{ marginRight: 10, width: 15, height: 15, cursor: "pointer", flexShrink: 0, accentColor: "#007bff" }} />
        )}
        {row.status !== "PENDING" && <div style={{ width: 25, flexShrink: 0 }} />}
        <div style={{ display: "flex", alignItems: "center", flex: 1, gap: 12, cursor: "pointer" }} onClick={onToggle}>
          <div style={{ ...actionIcon, background: "#e6dbfa", color: "#4a2b8c", fontSize: 22 }}>🎯</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: "#4a2b8c" }}>
              Sell Initial Strangle (Call + Put) — {row.symbol}
            </div>
            <div style={{ fontSize: 12, color: "#555", marginTop: 2 }}>
              {showAccount && row.username && (
                <span style={{ marginRight: 8, padding: "1px 7px", borderRadius: 8, background: "#e9ecef", color: "#495057", fontSize: 11, fontWeight: 600 }}>
                  {row.username}
                </span>
              )}
              {call.call_symbol && <span style={{ color: "#0055aa", fontWeight: 600 }}>{call.call_symbol}</span>}
              {call.call_strike != null && <span style={{ marginLeft: 8, color: "#6c757d" }}>Call ₹{call.call_strike}</span>}
              {csp.csp_symbol && <span style={{ marginLeft: 12, color: "#0d6e54", fontWeight: 600 }}>{csp.csp_symbol}</span>}
              {csp.put_strike != null && <span style={{ marginLeft: 8, color: "#6c757d" }}>Put ₹{csp.put_strike}</span>}
              {ltp != null && <span style={{ marginLeft: 8, color: "#888" }}>LTP ₹{ltp}</span>}
            </div>
          </div>
          <div style={{ textAlign: "right", minWidth: 140 }}>
            <span style={{ ...statusBadge, background: sm.bg, color: sm.color }}>{sm.label}</span>
            <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>{toIST(row.created_at)}</div>
          </div>
          <div style={{ marginLeft: 12, color: "#ccc", fontSize: 18 }}>{expanded ? "▲" : "▼"}</div>
        </div>
        <NavButtons symbol={row.symbol} />
      </div>

      {expanded && (
        <div style={cardBody}>
          {row.status === "FAILED" && row.error_message && (
            <div style={{ background: "#fde2e2", border: "1px solid #dc3545", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#dc3545" }}>
              <strong>Failed:</strong> {row.error_message}
            </div>
          )}

          {/* Call details */}
          <div style={{ background: "#d4edda", border: "1px solid #b1dfbb", borderRadius: 8, padding: "12px 14px", marginBottom: 14 }}>
            <div style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, color: "#0a5c25", marginBottom: 8, letterSpacing: 0.6 }}>
              Call Hedge to Sell
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#222", marginBottom: 6 }}>{call.call_symbol || "—"}</div>
            <div style={{ display: "flex", gap: 20, flexWrap: "wrap", fontSize: 13, color: "#333" }}>
              {call.call_strike  != null && <span><strong>Strike:</strong> {fmtRs(call.call_strike)}</span>}
              {call.call_ltp     != null && <span><strong>LTP:</strong> <span style={{ color: "#28a745", fontWeight: 700 }}>{fmtRs(call.call_ltp)}</span></span>}
              {call.quantity     != null && <span><strong>Lots:</strong> {call.quantity}</span>}
              {call.call_delta   != null && <span><strong>Delta:</strong> {fmtN(call.call_delta)}</span>}
              {call.hedge_percent!= null && (
                <span style={{ fontWeight: 600, color: "#0055aa" }}><strong>Hedge:</strong> {Number(call.hedge_percent).toFixed(1)}%</span>
              )}
              {call.reference_avg_price != null && <span><strong>Avg Price:</strong> {fmtRs(call.reference_avg_price)}</span>}
            </div>
          </div>

          {/* Put details */}
          <div style={{ background: "#d1f5ee", border: "1px solid #9ee8d1", borderRadius: 8, padding: "12px 14px", marginBottom: 14 }}>
            <div style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, color: "#0d6e54", marginBottom: 8, letterSpacing: 0.6 }}>
              Cash-Secured Put to Sell
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#222", marginBottom: 10 }}>{csp.csp_symbol || "—"}</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: "8px 20px", fontSize: 13 }}>
              {csp.put_strike  != null && (
                <div>
                  <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", fontWeight: 600 }}>Strike</div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{fmtRs(csp.put_strike)}</div>
                </div>
              )}
              {csp.put_ltp     != null && (
                <div>
                  <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", fontWeight: 600 }}>Option LTP (Premium)</div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: "#28a745" }}>{fmtRs(csp.put_ltp)}</div>
                  {csp.lot_size != null && (
                    <div style={{ fontSize: 11, color: "#555" }}>
                      = ₹{Math.round(csp.put_ltp * csp.lot_size).toLocaleString("en-IN")} / lot
                    </div>
                  )}
                </div>
              )}
              {csp.put_delta   != null && (
                <div>
                  <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", fontWeight: 600 }}>Delta</div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{fmtN(csp.put_delta)}</div>
                </div>
              )}
              {distPct != null && (
                <div>
                  <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", fontWeight: 600 }}>Distance from Spot</div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: distColor }}>{distPct.toFixed(1)}%</div>
                  {ltp != null && csp.put_strike != null && (
                    <div style={{ fontSize: 11, color: "#555" }}>Spot ₹{ltp} → Strike ₹{csp.put_strike}</div>
                  )}
                </div>
              )}
              {csp.quantity    != null && (
                <div>
                  <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", fontWeight: 600 }}>Lots</div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{csp.quantity}</div>
                  {csp.lot_size != null && (
                    <div style={{ fontSize: 11, color: "#555" }}>{csp.quantity * csp.lot_size} shares</div>
                  )}
                </div>
              )}
              {csp.put_bid != null && (
                <div>
                  <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", fontWeight: 600 }}>Bid</div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{fmtRs(csp.put_bid)}</div>
                </div>
              )}
              {csp.put_ask != null && (
                <div>
                  <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", fontWeight: 600 }}>Ask</div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{fmtRs(csp.put_ask)}</div>
                </div>
              )}
              {csp.put_spread_pct != null && (
                <div>
                  <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", fontWeight: 600 }}>Bid-Ask Spread</div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: csp.put_spread_pct > 8 ? "#dc3545" : "#28a745" }}>
                    {fmtN(csp.put_spread_pct, 1)}%
                  </div>
                </div>
              )}
              {csp.put_oi != null && (
                <div>
                  <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", fontWeight: 600 }}>Open Interest</div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{Number(csp.put_oi).toLocaleString("en-IN")}</div>
                </div>
              )}
            </div>
          </div>

          {/* Accumulation ladder (from the call leg's structure) */}
          {ladder.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#555", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
                Accumulation Ladder
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 14 }}>
                <thead>
                  <tr style={{ background: "#f5f7fa" }}>
                    <th style={thS}>Step</th>
                    <th style={thS}>Buy Price</th>
                    <th style={thS}>% from Spot</th>
                    <th style={thS}>Step Gap</th>
                    <th style={thS}>Qty</th>
                    <th style={thS}>Investment</th>
                    <th style={thS}>Avg After Step</th>
                    <th style={{ ...thS, color: "#6c757d" }}>Stock P&amp;L</th>
                    <th style={{ ...thS, color: "#6c757d" }}>Option P&amp;L</th>
                    <th style={{ ...thS, color: "#17a2b8" }}>P&amp;L at Buy Price</th>
                  </tr>
                </thead>
                <tbody>
                  {ladder.map((s, i) => {
                    const pctFromLtp  = ltp ? ((s.buy_price - ltp) / ltp * 100) : null;
                    const stepGapPct  = i === 0
                      ? (ltp ? ((ltp - s.buy_price) / ltp * 100) : null)
                      : ((ladder[i - 1].buy_price - s.buy_price) / ladder[i - 1].buy_price * 100);
                    const investment  = s.buy_price * s.qty;
                    // Mark-to-market at this step's OWN buy price — see the matching
                    // comment on the SELL_INITIAL_CALL card above.
                    const stockPnl    = (s.buy_price - s.avg_price) * s.total_qty;
                    const callIntrinsic = callStrike > 0 ? Math.max(0, s.buy_price - callStrike) * callQty : 0;
                    const callPnl     = callLtp * callQty - callIntrinsic;
                    const netPnl      = stockPnl + callPnl;
                    const pnlClr      = netPnl >= 0 ? "#28a745" : "#dc3545";
                    const fmtPnl      = v => `${v >= 0 ? "+" : "-"}₹${Math.abs(Math.round(v)).toLocaleString("en-IN")}`;
                    return (
                      <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                        <td style={tdS}>{s.step ?? i + 1}</td>
                        <td style={{ ...tdS, fontWeight: 600 }}>{fmtRs(s.buy_price)}</td>
                        <td style={{ ...tdS, color: pctFromLtp == null ? "#999" : pctFromLtp >= 0 ? "#28a745" : "#dc3545" }}>
                          {pctFromLtp != null ? `${pctFromLtp >= 0 ? "+" : ""}${pctFromLtp.toFixed(1)}%` : "—"}
                        </td>
                        <td style={{ ...tdS, color: "#6c757d" }}>
                          {stepGapPct != null ? `${stepGapPct.toFixed(2)}%` : "—"}
                        </td>
                        <td style={tdS}>{s.qty}</td>
                        <td style={tdS}>₹{Math.round(investment).toLocaleString("en-IN")}</td>
                        <td style={tdS}>{fmtRs(s.avg_price)}</td>
                        <td style={{ ...tdS, color: stockPnl >= 0 ? "#28a745" : "#dc3545" }}>{fmtPnl(stockPnl)}</td>
                        <td style={{ ...tdS, color: "#28a745" }}>{fmtPnl(callPnl)}</td>
                        <td style={{ ...tdS, fontWeight: 700, color: pnlClr }}>{fmtPnl(netPnl)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}

          {/* Tail risk summary */}
          {call.tail_risk_pnl != null && (
            <div style={{ background: call.tail_risk_pnl >= 0 ? "#d4edda" : "#fde2e2", border: `1px solid ${call.tail_risk_pnl >= 0 ? "#b1dfbb" : "#f5c6cb"}`, borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>
              <strong>Tail Risk P&amp;L</strong> (if fully accumulated &amp; assigned at strike):{" "}
              <span style={{ fontWeight: 700, color: call.tail_risk_pnl >= 0 ? "#155724" : "#721c24", fontSize: 14 }}>
                {call.tail_risk_pnl >= 0 ? "+" : "-"}₹{Math.abs(Math.round(call.tail_risk_pnl)).toLocaleString("en-IN")}
              </span>
            </div>
          )}

          {row.status === "PENDING" && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button onClick={handleApprove} disabled={submitting}
                style={{ ...btnApprove, background: "#6f42c1", opacity: submitting ? 0.5 : 1 }}>
                {submitting ? "Executing…" : "✓ Approve Strangle (Call + Put)"}
              </button>
              <button onClick={onReject} disabled={submitting} style={btnReject}>✕ Reject</button>
              <button onClick={() => onSnooze(60)}  disabled={submitting} style={btnSnooze}>Snooze 1h</button>
              <button onClick={() => onSnooze(480)} disabled={submitting} style={btnSnooze}>Snooze 8h</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── ItmRollupCard ────────────────────────────────────────────── */
function ItmRollupCard({ row, expanded, onToggle, onReject, onSnooze, onOptimisticApprove, acting, checked, onCheck, username, showAccount, flash, fetchAll }) {
  const isActivelySnoozed = row.status === "PENDING"
    && row.snooze_until
    && new Date(row.snooze_until.replace(" ", "T") + "Z") > new Date();
  const displayStatus = isActivelySnoozed ? "SNOOZED" : row.status;
  const sm = STATUS_META[displayStatus] || STATUS_META.PENDING;
  const [submitting, setSubmitting] = useState(false);

  const raw      = row.metadata || {};
  // data may be at top-level or nested under raw.metadata (old format)
  const meta     = raw.itm_legs ? raw : (raw.metadata || raw);
  const legs     = meta.itm_legs || [];
  const ul       = meta.underlying_ltp;
  const thr      = meta.itm_low_extrinsic_pct != null ? Math.round(meta.itm_low_extrinsic_pct * 100) : 20;

  const fmtRs  = v => v != null ? `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}` : "—";
  const fmtPct = v => v != null ? `${Number(v).toFixed(1)}%` : "—";

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      await axios.post("/automation/suggestions/approve", { id: row.id, username });
      onOptimisticApprove?.();
      flash("Roll-up approved — order queued");
      fetchAll();
    } catch { flash("Approval failed.", false); }
    finally   { setSubmitting(false); }
  };

  // Summary line for collapsed header
  const legSummary = legs.length > 0
    ? legs.map(l => `${l.optiontype} ${l.strike} (${fmtPct(l.extrinsic_pct)} extrinsic)`).join(", ")
    : "ITM option extrinsic below threshold";

  const thS = { padding: "6px 10px", textAlign: "left", fontWeight: 600, fontSize: 11, borderBottom: "2px solid #dee2e6", color: "#555" };
  const tdS = { padding: "6px 10px", fontSize: 12, borderBottom: "1px solid #f0f0f0" };

  return (
    <div style={{ ...card, border: "2px solid #fd7e14", outline: checked ? "2px solid #007bff" : "none" }}>
      <div style={cardTop}>
        {row.status === "PENDING" && (
          <input type="checkbox" checked={!!checked} onChange={onCheck} onClick={e => e.stopPropagation()}
            style={{ marginRight: 10, width: 15, height: 15, cursor: "pointer", flexShrink: 0, accentColor: "#007bff" }} />
        )}
        {row.status !== "PENDING" && <div style={{ width: 25, flexShrink: 0 }} />}
        <div style={{ display: "flex", alignItems: "center", flex: 1, gap: 12, cursor: "pointer" }} onClick={onToggle}>
          <div style={{ ...actionIcon, background: "#fff3cd", color: "#856404", fontSize: 22 }}>🔄</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: "#7d3c00" }}>
              Roll ITM Option — {row.symbol}
            </div>
            <div style={{ fontSize: 12, color: "#555", marginTop: 2 }}>
              {showAccount && row.username && (
                <span style={{ marginRight: 8, padding: "1px 7px", borderRadius: 8, background: "#e9ecef", color: "#495057", fontSize: 11, fontWeight: 600 }}>
                  {row.username}
                </span>
              )}
              <span style={{ color: "#dc3545", fontWeight: 600 }}>{legSummary}</span>
              {ul != null && <span style={{ marginLeft: 8, color: "#888" }}>Spot ₹{Number(ul).toLocaleString("en-IN")}</span>}
            </div>
          </div>
          <div style={{ flex: 2, fontSize: 12, color: "#6c757d", padding: "0 12px" }}>
            Buy back ITM leg (extrinsic &lt;{thr}%) and re-sell farther OTM to collect higher extrinsic premium
          </div>
          <div style={{ textAlign: "right", minWidth: 140 }}>
            <span style={{ ...statusBadge, background: sm.bg, color: sm.color }}>{sm.label}</span>
            <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>{toIST(row.created_at)}</div>
            {isActivelySnoozed && row.snooze_until && (
              <div style={{ fontSize: 11, color: "#856404", marginTop: 2 }}>⏸ until {toIST(row.snooze_until)}</div>
            )}
            {row.error_message && (
              <div style={{ fontSize: 11, color: "#dc3545", marginTop: 2 }}>{row.error_message.slice(0, 60)}</div>
            )}
          </div>
          <div style={{ marginLeft: 12, color: "#ccc", fontSize: 18 }}>{expanded ? "▲" : "▼"}</div>
        </div>
        <NavButtons symbol={row.symbol} />
      </div>

      {expanded && (
        <div style={cardBody}>
          {/* What triggers this */}
          <div style={{ ...metaBox, marginTop: 14, borderLeft: "3px solid #fd7e14" }}>
            <div style={metaTitle}>Why This Fired</div>
            <div style={{ fontSize: 13, color: "#2c3e50", lineHeight: 1.6 }}>
              An open short option is <strong>in-the-money</strong> and its extrinsic value has dropped below{" "}
              <strong style={{ color: "#dc3545" }}>{thr}%</strong> of its LTP — trading mostly on intrinsic with little
              time premium left. The new strike is chosen to mirror the opposite side's OTM distance, creating a
              symmetric strangle. Lot count is set to match the opposite side's total delta.
            </div>
            {(meta.rule_name) && (
              <div style={{ marginTop: 6, fontSize: 11, color: "#888" }}>Rule: {meta.rule_name.replace(/_/g, " ")}</div>
            )}
          </div>

          {/* ITM legs to close + proposed new leg side by side */}
          {legs.length > 0 && legs.map((l, i) => (
            <div key={i} style={{ ...metaBox, marginTop: 0 }}>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>

                {/* Close side */}
                <div style={{ flex: "1 1 340px", minWidth: 300 }}>
                  <div style={{ ...metaTitle, color: "#dc3545" }}>
                    ① Buy Back (Close ITM)
                    <span style={{ marginLeft: 8, padding: "2px 8px", borderRadius: 4, background: l.optiontype === "PE" ? "#d4edda" : "#cce5ff", color: l.optiontype === "PE" ? "#155724" : "#004085", fontWeight: 700, fontSize: 11 }}>
                      {l.optiontype}
                    </span>
                  </div>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginTop: 4 }}>
                    <tbody>
                      <tr><td style={{ ...tdS, color: "#888", width: "50%" }}>Strike</td><td style={{ ...tdS, fontWeight: 700 }}>{fmtRs(l.strike)}</td></tr>
                      <tr style={{ background: "#fafafa" }}><td style={{ ...tdS, color: "#888" }}>Spot</td><td style={tdS}>{fmtRs(ul)}</td></tr>
                      <tr><td style={{ ...tdS, color: "#888" }}>Option LTP</td><td style={tdS}>{fmtRs(l.ltp)}</td></tr>
                      <tr style={{ background: "#fafafa" }}><td style={{ ...tdS, color: "#888" }}>Intrinsic Value</td><td style={tdS}>{fmtRs(l.intrinsic)}</td></tr>
                      <tr><td style={{ ...tdS, color: "#888" }}>Extrinsic Value</td><td style={{ ...tdS, color: "#dc3545", fontWeight: 700 }}>{fmtRs(l.extrinsic)} <span style={{ fontSize: 10, fontWeight: 400 }}>({fmtPct(l.extrinsic_pct)} of LTP, thr {thr}%)</span></td></tr>
                      <tr style={{ background: "#fafafa" }}><td style={{ ...tdS, color: "#888" }}>Delta</td><td style={tdS}>{l.delta != null ? Number(l.delta).toFixed(3) : "—"}</td></tr>
                      <tr><td style={{ ...tdS, color: "#888" }}>Lots</td><td style={tdS}>{l.lots}</td></tr>
                      <tr style={{ background: "#fafafa" }}><td style={{ ...tdS, color: "#888" }}>Unrealised P&L</td>
                        <td style={{ ...tdS, color: l.unrealised >= 0 ? "#dc3545" : "#28a745", fontWeight: 700 }}>
                          {l.unrealised >= 0 ? "+" : ""}{fmtRs(l.unrealised)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Arrow */}
                <div style={{ display: "flex", alignItems: "center", fontSize: 24, color: "#adb5bd", flexShrink: 0 }}>→</div>

                {/* New leg side */}
                <div style={{ flex: "1 1 340px", minWidth: 300 }}>
                  <div style={{ ...metaTitle, color: "#28a745" }}>
                    ② Sell New (Farther OTM)
                    {l.proposed && (
                      <span style={{ marginLeft: 8, padding: "2px 8px", borderRadius: 4, background: l.optiontype === "PE" ? "#d4edda" : "#cce5ff", color: l.optiontype === "PE" ? "#155724" : "#004085", fontWeight: 700, fontSize: 11 }}>
                        {l.optiontype}
                      </span>
                    )}
                  </div>
                  {l.proposed ? (
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginTop: 4 }}>
                      <tbody>
                        <tr><td style={{ ...tdS, color: "#888", width: "50%" }}>Symbol</td><td style={{ ...tdS, fontWeight: 700, color: "#0055aa" }}>{l.proposed.symbol}</td></tr>
                        <tr style={{ background: "#fafafa" }}><td style={{ ...tdS, color: "#888" }}>Strike</td><td style={{ ...tdS, fontWeight: 700 }}>{fmtRs(l.proposed.strike)}</td></tr>
                        <tr><td style={{ ...tdS, color: "#888" }}>LTP</td><td style={tdS}>{fmtRs(l.proposed.ltp)}</td></tr>
                        <tr style={{ background: "#fafafa" }}><td style={{ ...tdS, color: "#888" }}>Bid / Ask</td><td style={tdS}>{fmtRs(l.proposed.bid)} / {fmtRs(l.proposed.ask)}</td></tr>
                        <tr><td style={{ ...tdS, color: "#888" }}>Strike selection</td>
                          <td style={{ ...tdS, fontSize: 11, color: "#555" }}>
                            {l.proposed.mirror_strike != null
                              ? <>Mirror-distance — opposite side is <strong>{Math.abs(Math.round((l.proposed.mirror_strike != null ? (l.optiontype === "PE" ? (ul - l.proposed.mirror_strike) : (l.proposed.mirror_strike - ul)) : 0)))} pts</strong> from spot → target ≤ ₹{Number(l.proposed.mirror_strike).toLocaleString("en-IN")}</>
                              : <>Delta band (target Δ {l.proposed.target_delta})</>
                            }
                          </td>
                        </tr>
                        <tr style={{ background: "#fafafa" }}><td style={{ ...tdS, color: "#888" }}>Delta</td><td style={{ ...tdS, fontWeight: 700, color: "#28a745" }}>{l.proposed.delta != null ? Number(l.proposed.delta).toFixed(3) : "—"}</td></tr>
                        {l.proposed.theta != null && <tr style={{ background: "#fafafa" }}><td style={{ ...tdS, color: "#888" }}>Theta</td><td style={tdS}>{Number(l.proposed.theta).toFixed(2)}/day</td></tr>}
                        <tr><td style={{ ...tdS, color: "#888" }}>Lots</td>
                          <td style={tdS}>
                            <strong>{l.proposed.lots}</strong>
                            {l.proposed.opp_delta_total != null && (
                              <span style={{ fontSize: 11, color: "#555", marginLeft: 6 }}>
                                (delta-matched: {l.proposed.opp_delta_total} opp Δ ÷ {l.proposed.delta} = {l.proposed.lots_uncapped} lots
                                {l.proposed.capped_at_max ? <span style={{ color: "#dc3545", fontWeight: 600 }}>, capped at max {l.proposed.lots}</span> : null})
                              </span>
                            )}
                          </td>
                        </tr>
                        <tr style={{ background: "#fafafa" }}><td style={{ ...tdS, color: "#888" }}>Premium Collected</td>
                          <td style={{ ...tdS, fontWeight: 700, color: "#28a745" }}>
                            {fmtRs(l.proposed.premium_total)}
                            <span style={{ fontSize: 10, color: "#888", marginLeft: 4 }}>({l.proposed.lots} × {l.proposed.lot_size} × {fmtRs(l.proposed.ltp)})</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  ) : (
                    <div style={{ fontSize: 12, color: "#888", marginTop: 8, padding: "8px 10px", background: "#fafafa", borderRadius: 6 }}>
                      New contract will be selected at execution time based on delta {thr > 0 ? `(target ~${Math.round((0.10 + 0.25) / 2 * 100)}Δ)` : ""}.
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          {legs.length === 0 && (
            <div style={{ ...metaBox, marginTop: 0, color: "#888", fontSize: 13 }}>
              No position data available at time of rule fire. The cycle will re-evaluate on next run.
            </div>
          )}

          {/* Net trade summary */}
          {legs.some(l => l.proposed) && (() => {
            const buybackCost  = legs.reduce((s, l) => s + (l.ltp * l.lots * l.lotsize), 0);
            // Use bid-based premium (realistic fill for sell) — falls back to ltp-based if bid not present
            const newPremium   = legs.filter(l => l.proposed).reduce((s, l) => {
              const bidBased = l.proposed.bid_premium_total;
              const ltpBased = l.proposed.premium_total || 0;
              return s + (bidBased != null ? bidBased : ltpBased);
            }, 0);
            const netCredit    = newPremium - buybackCost;
            const isNetDebit   = netCredit < 0;
            const extraExtr    = legs.filter(l => l.proposed).reduce((s, l) => {
              const newExtr = l.proposed.ltp * (1 - l.proposed.delta);  // rough extrinsic estimate
              const oldExtr = l.extrinsic * l.lots * l.lotsize;
              return s + (newExtr * l.proposed.lots * l.proposed.lot_size - oldExtr);
            }, 0);
            return (
              <div style={{ ...metaBox, marginTop: 0, background: isNetDebit ? "#fff0f0" : "#f0fff4", borderLeft: `3px solid ${isNetDebit ? "#dc3545" : "#28a745"}` }}>
                <div style={metaTitle}>Trade Economics</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 32px", fontSize: 13 }}>
                  <span><strong>Buy-back cost:</strong> <span style={{ color: "#dc3545", fontWeight: 700 }}>{fmtRs(buybackCost)}</span></span>
                  <span><strong>New premium collected (bid):</strong> <span style={{ color: "#28a745", fontWeight: 700 }}>{fmtRs(newPremium)}</span></span>
                  <span><strong>Net {isNetDebit ? "debit" : "credit"}:</strong> <span style={{ color: isNetDebit ? "#dc3545" : "#28a745", fontWeight: 700 }}>{isNetDebit ? "" : "+"}{fmtRs(netCredit)}</span></span>
                  <span style={{ color: "#888", fontSize: 12 }}>Extrinsic gained: <span style={{ color: extraExtr >= 0 ? "#28a745" : "#dc3545", fontWeight: 600 }}>{extraExtr >= 0 ? "+" : ""}{fmtRs(extraExtr)}</span></span>
                </div>
                {isNetDebit && (
                  <div style={{ marginTop: 8, padding: "6px 10px", background: "#fde2e2", borderRadius: 4, fontSize: 12, color: "#7b1515", fontWeight: 600 }}>
                    ⚠ Net debit at current prices — execution will be blocked unless market prices improve before approval.
                  </div>
                )}
              </div>
            );
          })()}

          {/* Actions */}
          {row.status === "PENDING" && (
            <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
              <button onClick={handleApprove} disabled={submitting || acting} style={{ ...btnApprove, background: "#fd7e14", borderColor: "#fd7e14" }}>
                {submitting ? "Approving…" : "✓ Approve Roll-Up"}
              </button>
              <button onClick={onReject}  disabled={submitting} style={btnReject}>✕ Reject</button>
              <button onClick={() => onSnooze(60)}  disabled={submitting} style={btnSnooze}>Snooze 1h</button>
              <button onClick={() => onSnooze(480)} disabled={submitting} style={btnSnooze}>Snooze 8h</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SuggestionCard({ row, expanded, onToggle, onApprove, onReject, onSnooze, acting, checked, onCheck, username, showAccount }) {
  // Detect active snooze: status is still PENDING but snooze_until is in the future (UTC)
  const isActivelySnoozed = row.status === "PENDING"
    && row.snooze_until
    && new Date(row.snooze_until.replace(" ", "T") + "Z") > new Date();
  const displayStatus = isActivelySnoozed ? "SNOOZED" : row.status;
  const sm = STATUS_META[displayStatus] || STATUS_META.PENDING;
  const meta       = row.metadata || {};
  const isBuyStock = row.action_type === "BUY_STOCK";
  const isSquareOff = SQUAREOFF_TYPES.has(row.action_type);
  const defaultQty = (row.metadata && row.metadata.quantity) ? String(row.metadata.quantity) : "";
  const [editQty, setEditQty] = useState(defaultQty);
  // Current/next/far month choice — only present on BUY_FUTURE (first buy of a
  // fresh ladder) and REBUILD_NEXT_CYCLE (monthly rollover), and only when more
  // than one contract is actually available to choose from.
  const futureMonthOptions = row.metadata?.future_month_options || [];
  const hasFutureMonthChoice =
    (row.action_type === "BUY_FUTURE" || row.action_type === "REBUILD_NEXT_CYCLE") &&
    futureMonthOptions.length > 1;
  const [editFutureSymbol, setEditFutureSymbol] = useState(row.metadata?.future_symbol || "");
  const selectedFutureOpt = hasFutureMonthChoice
    ? futureMonthOptions.find(o => o.symbol === editFutureSymbol)
    : null;
  const [orders, setOrders] = useState(null);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Re-fetch positions each time the card is expanded (expanded flipping false→true triggers fetch)
  const prevExpanded = useRef(false);
  useEffect(() => {
    const justOpened = expanded && !prevExpanded.current;
    prevExpanded.current = expanded;
    if (!justOpened || row.status !== "PENDING" || !isSquareOff || !username || !row.symbol) return;
    setPreviewLoading(true);
    setPreview(null);
    axios.get("/automation/suggestions/preview", {
      params: {
        username, symbol: row.symbol, action_type: row.action_type,
        ...(row.action_type === "SQUARE_OFF_FUTURE_AND_CALL_LOT" && row.metadata?.future_symbol
          ? { future_symbol: row.metadata.future_symbol } : {}),
      },
    })
      .then(res => setPreview(res.data))
      .catch(() => setPreview([]))
      .finally(() => setPreviewLoading(false));
  }, [expanded, row.status, row.symbol, row.action_type, username, isSquareOff]);

  useEffect(() => {
    if (!expanded || row.status === "PENDING" || row.status === "REJECTED" || !username) return;
    if (orders !== null) return; // already fetched
    setOrdersLoading(true);
    axios.get("/automation/orders", { params: { username, days: 7 } })
      .then(res => {
        const matched = res.data.filter(
          o => o.strategy_name === row.strategy_name && o.symbol === row.symbol
        );
        setOrders(matched);
      })
      .catch(() => setOrders([]))
      .finally(() => setOrdersLoading(false));
  }, [expanded, row.status, row.strategy_name, row.symbol, username, orders]);

  return (
    <div style={{ ...card, outline: checked ? "2px solid #007bff" : "none" }}>
      {/* Top row */}
      <div style={cardTop}>

        {/* Checkbox — only for PENDING */}
        {row.status === "PENDING" && (
          <input
            type="checkbox"
            checked={checked}
            onChange={onCheck}
            onClick={e => e.stopPropagation()}
            style={{ marginRight: 10, width: 15, height: 15, cursor: "pointer", flexShrink: 0, accentColor: "#007bff" }}
          />
        )}
        {row.status !== "PENDING" && <div style={{ width: 25, flexShrink: 0 }} />}

        {/* Rest of row is the expand click area */}
        <div style={{ display: "flex", alignItems: "center", flex: 1, cursor: "pointer" }} onClick={onToggle}>

          {/* Left: action type + symbol */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1 }}>
            <div style={{ ...actionIcon, background: sm.bg, color: sm.color }}>
              {actionEmoji(row.action_type)}
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: "#2c3e50" }}>
                {ACTION_DISPLAY_NAMES[row.action_type] || row.action_type?.replace(/_/g, " ")}
              </div>
              <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
                <strong>{row.symbol}</strong>
                {row.strategy_name && ` · ${row.strategy_name}`}
                {showAccount && row.username && (
                  <span style={{ marginLeft: 8, padding: "1px 7px", borderRadius: 8, background: "#e9ecef", color: "#495057", fontSize: 11, fontWeight: 600 }}>
                    {row.username}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Middle: description */}
          <div style={{ flex: 2, fontSize: 13, color: "#555", padding: "0 12px" }}>
            {ACTION_DESC[row.action_type] || ""}
          </div>

          {/* Right: status + time */}
          <div style={{ textAlign: "right", minWidth: 160 }}>
            <span style={{ ...statusBadge, background: sm.bg, color: sm.color }}>
              {sm.label}
            </span>
            <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>
              {toIST(row.created_at)}
            </div>
            {isActivelySnoozed && row.snooze_until && (
              <div style={{ fontSize: 11, color: "#856404", marginTop: 2 }}>
                ⏸ until {toIST(row.snooze_until)}
              </div>
            )}
            {row.error_message && (
              <div style={{ fontSize: 11, color: "#dc3545", marginTop: 2 }}>
                {row.error_message.slice(0, 60)}
              </div>
            )}
          </div>

          <div style={{ marginLeft: 12, color: "#ccc", fontSize: 18 }}>
            {expanded ? "▲" : "▼"}
          </div>

        </div>
        <NavButtons symbol={row.symbol} />
      </div>

      {/* Expanded: metadata + actions */}
      {expanded && (
        <div style={cardBody}>

          {/* Metadata detail */}
          <div style={{ ...metaBox, marginTop: 14 }}>
            <div style={metaTitle}>Action Details</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 24px", fontSize: 13, color: "#2c3e50" }}>
              <span><strong>Symbol:</strong> {row.symbol}</span>
              {row.metadata?.quantity  && row.action_type !== "BUY_STOCK" && <span><strong>Qty (lots):</strong> {row.metadata.quantity}</span>}
              {row.metadata?.lots_squared != null && (
                <span><strong>No of Lots Squared:</strong> {row.metadata.lots_squared}</span>
              )}
              {(row.action_type === "BUY_FUTURE" || row.action_type === "REBUILD_NEXT_CYCLE") && (selectedFutureOpt?.symbol || row.metadata?.future_symbol) && (
                <span><strong>Contract:</strong> {selectedFutureOpt?.symbol || row.metadata.future_symbol}</span>
              )}
              {(row.action_type === "BUY_FUTURE" || row.action_type === "REBUILD_NEXT_CYCLE") && (selectedFutureOpt?.expiry || row.metadata?.future_expiry) && (
                <span><strong>Future Expiry:</strong> {String(selectedFutureOpt?.expiry || row.metadata.future_expiry).slice(0, 10)}</span>
              )}
              {row.metadata?.call_strike != null && <span><strong>Strike:</strong> ₹{row.metadata.call_strike}</span>}
              {row.metadata?.underlying_ltp != null && (
                <span><strong>Stock LTP:</strong> <span style={{ color: "#0055aa", fontWeight: 700 }}>₹{Number(row.metadata.underlying_ltp).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span></span>
              )}
              {row.metadata?.next_buy_price != null && (
                <span><strong>Next Buy Price:</strong> ₹{Number(row.metadata.next_buy_price).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
              )}
              {row.metadata?.call_ltp   != null && <span><strong>Call LTP:</strong> ₹{row.metadata.call_ltp}</span>}
              {row.metadata?.call_delta != null && <span><strong>Delta:</strong> {Number(row.metadata.call_delta).toFixed(3)}</span>}
              {row.metadata?.extra_premium != null && (
                <span><strong>Extra Premium:</strong> <span style={{ color: "#28a745", fontWeight: 700 }}>₹{row.metadata.extra_premium.toLocaleString("en-IN")}</span></span>
              )}
              {(row.metadata?.rule_name || row.metadata?.rule_id) && (
                <span>
                  <strong>Rule:</strong>{" "}
                  {row.metadata.rule_name
                    ? row.metadata.rule_name.replace(/_/g, " ")
                    : `#${row.metadata.rule_id}`}
                </span>
              )}
              {row.metadata?.side      && <span><strong>Side:</strong> {row.metadata.side}</span>}
              {row.strategy_name       && <span><strong>Strategy:</strong> {row.strategy_name}</span>}
              <span><strong>Created:</strong> {toIST(row.created_at)}</span>
              {row.updated_at && row.updated_at !== row.created_at && (
                <span><strong>Updated:</strong> {toIST(row.updated_at)}</span>
              )}
            </div>
            {row.metadata?.reason && (
              <div style={{ marginTop: 8, fontSize: 12, color: "#555", background: "#fff8e1", border: "1px solid #ffe082", borderRadius: 6, padding: "6px 10px" }}>
                <strong>Reason:</strong> {row.metadata.reason}
              </div>
            )}
            {row.error_message && (
              <div style={{ marginTop: 8, fontSize: 12, color: "#dc3545", background: "#fde2e2", borderRadius: 6, padding: "6px 10px" }}>
                <strong>Error:</strong> {row.error_message}
              </div>
            )}
          </div>

          {/* Position Context — BUY_STOCK and REBUILD_LADDER */}
          {(row.action_type === "BUY_STOCK" || row.action_type === "REBUILD_LADDER") &&
           (row.metadata?.gap_pct != null || row.metadata?.existing_stock_lots != null ||
            row.metadata?.existing_investment != null || row.metadata?.trade_pnl != null) && (
            <div style={{ ...metaBox, marginTop: 0 }}>
              <div style={metaTitle}>Position Context</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 32px", fontSize: 13 }}>
                {row.metadata.underlying_ltp != null && (
                  <span><strong>Stock LTP:</strong> <span style={{ color: "#0055aa", fontWeight: 700 }}>₹{Number(row.metadata.underlying_ltp).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span></span>
                )}
                {row.metadata.call_strike != null && (
                  <span><strong>Call Strike:</strong> <span style={{ color: "#6f42c1", fontWeight: 700 }}>₹{Number(row.metadata.call_strike).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</span></span>
                )}
                {row.metadata.gap_pct != null && (
                  <span><strong>Gap %:</strong> {Number(row.metadata.gap_pct).toFixed(2)}%</span>
                )}
                {row.metadata.existing_stock_lots != null && (
                  <span>
                    <strong>Existing Qty:</strong>{" "}
                    {row.metadata.existing_stock_lots} lots
                    {row.metadata.existing_stock_qty != null && ` (${row.metadata.existing_stock_qty} shares)`}
                  </span>
                )}
                {row.metadata.existing_stock_lots != null && row.metadata.quantity != null && row.metadata.lot_size != null && (() => {
                  const newShares  = Number(row.metadata.quantity);
                  const lotSize    = Number(row.metadata.lot_size);
                  const existingShares = Number(row.metadata.existing_stock_qty || 0);
                  const totalShares = existingShares + newShares;
                  const totalLots  = (totalShares / lotSize).toFixed(2);
                  return (
                    <span>
                      <strong>Total After Buy:</strong>{" "}
                      <span style={{ color: "#0055aa", fontWeight: 700 }}>{totalLots} lots</span>
                      <span style={{ color: "#888", fontSize: 11 }}> ({totalShares} shares, +{newShares} new)</span>
                    </span>
                  );
                })()}
                {row.metadata.existing_investment != null && (
                  <span>
                    <strong>Existing Investment:</strong>{" "}
                    ₹{Number(row.metadata.existing_investment).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                  </span>
                )}
                {row.metadata.trade_pnl != null && (
                  <span>
                    <strong>Trade P&amp;L:</strong>{" "}
                    <span style={{ color: row.metadata.trade_pnl >= 0 ? "#28a745" : "#dc3545", fontWeight: 700 }}>
                      {row.metadata.trade_pnl >= 0 ? "+" : "-"}₹{Math.abs(Number(row.metadata.trade_pnl)).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                    </span>
                  </span>
                )}
                {row.metadata.tail_risk_pnl != null && (
                  <span>
                    <strong>Tail Risk P&amp;L:</strong>{" "}
                    <span style={{ color: row.metadata.tail_risk_pnl >= 0 ? "#28a745" : "#dc3545", fontWeight: 700 }}>
                      {row.metadata.tail_risk_pnl >= 0 ? "+" : "-"}₹{Math.abs(Number(row.metadata.tail_risk_pnl)).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                    </span>
                  </span>
                )}
                {row.metadata.tail_risk_distance_pct != null && (
                  <span>
                    <strong>Tail Risk Distance:</strong>{" "}
                    <span style={{ color: row.metadata.tail_risk_distance_pct >= 0 ? "#28a745" : "#dc3545", fontWeight: 700 }}>
                      {row.metadata.tail_risk_distance_pct >= 0 ? "+" : ""}{Number(row.metadata.tail_risk_distance_pct).toFixed(2)}%
                    </span>
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Position Context — BUY_FUTURE (Index/Futures Pyramid) */}
          {row.action_type === "BUY_FUTURE" &&
           (row.metadata?.existing_future_lots != null || row.metadata?.stock_offset_qty != null) && (
            <div style={{ ...metaBox, marginTop: 0 }}>
              <div style={metaTitle}>Position Context</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 32px", fontSize: 13 }}>
                {row.metadata.stock_offset_qty != null && row.metadata.stock_offset_qty > 0 && (
                  <span>
                    <strong>Existing Stocks:</strong>{" "}
                    {row.metadata.stock_offset_qty}
                    {row.metadata.stock_offset_lots != null && ` (${row.metadata.stock_offset_lots} lots)`}
                  </span>
                )}
                {row.metadata.existing_future_lots != null && (
                  <span>
                    <strong>Futures Held:</strong>{" "}
                    {row.metadata.existing_future_lots} lots
                    {row.metadata.existing_future_qty != null && ` (${row.metadata.existing_future_qty} shares)`}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Position Context — REBUILD_LADDER_ON_DELTA_GAP (rebuild-instead-of-buy) */}
          {row.action_type === "REBUILD_LADDER_ON_DELTA_GAP" && (
            <div style={{ ...metaBox, marginTop: 0 }}>
              <div style={metaTitle}>Delta Gap Rebuild</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 32px", fontSize: 13 }}>
                {row.metadata?.new_next_buy_price != null && (
                  <span>
                    <strong>New Next Buy Price:</strong>{" "}
                    <span style={{ color: "#0055aa", fontWeight: 700 }}>
                      ₹{Number(row.metadata.new_next_buy_price).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                    </span>
                  </span>
                )}
                {row.metadata?.new_tail_risk_pnl != null && (
                  <span>
                    <strong>New Tail Risk P&amp;L:</strong>{" "}
                    <span style={{ color: row.metadata.new_tail_risk_pnl >= 0 ? "#28a745" : "#dc3545", fontWeight: 700 }}>
                      {row.metadata.new_tail_risk_pnl >= 0 ? "+" : "-"}₹{Math.abs(Number(row.metadata.new_tail_risk_pnl)).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                    </span>
                  </span>
                )}
                {row.metadata?.new_gap_pct != null && (
                  <span>
                    <strong>New Step Gap:</strong>{" "}
                    <span style={{ color: "#0055aa", fontWeight: 700 }}>
                      {Number(row.metadata.new_gap_pct).toFixed(2)}%
                    </span>
                    {row.metadata.current_gap_pct != null && ` (now ${Number(row.metadata.current_gap_pct).toFixed(2)}%)`}
                  </span>
                )}
                {row.metadata?.current_futures_delta != null && (
                  <span><strong>Current Futures Delta:</strong> {Number(row.metadata.current_futures_delta).toFixed(3)}</span>
                )}
                {row.metadata?.current_call_delta != null && (
                  <span><strong>Current Call Delta:</strong> {Number(row.metadata.current_call_delta).toFixed(3)}</span>
                )}
              </div>
            </div>
          )}

          {/* Position Context — SQUARE_OFF_CALLS (e.g. Call Close Profit rule) */}
          {row.action_type === "SQUARE_OFF_CALLS" &&
           (row.metadata?.stock_pnl != null || row.metadata?.call_pnl != null) && (() => {
            const fmtRs = v => `${v >= 0 ? "+" : "-"}₹${Math.abs(Number(v)).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
            return (
              <div style={{ ...metaBox, marginTop: 0 }}>
                <div style={metaTitle}>Position Context</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 32px", fontSize: 13 }}>
                  {row.metadata.stock_pnl != null && (
                    <span>
                      <strong>Stock P&amp;L:</strong>{" "}
                      <span style={{ color: row.metadata.stock_pnl >= 0 ? "#28a745" : "#dc3545", fontWeight: 700 }}>
                        {fmtRs(row.metadata.stock_pnl)}
                      </span>
                    </span>
                  )}
                  {row.metadata.call_pnl != null && (
                    <span>
                      <strong>Call P&amp;L:</strong>{" "}
                      <span style={{ color: row.metadata.call_pnl >= 0 ? "#28a745" : "#dc3545", fontWeight: 700 }}>
                        {fmtRs(row.metadata.call_pnl)}
                      </span>
                    </span>
                  )}
                  {row.metadata.stock_to_call_distance_pct != null && (
                    <span>
                      <strong>Stock to Call Distance:</strong>{" "}
                      {Number(row.metadata.stock_to_call_distance_pct).toFixed(2)}%
                    </span>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Rich card for MAKE_DELTA_NEUTRAL — shows pre-computed rebalance plan */}
          {row.action_type === "MAKE_DELTA_NEUTRAL" && meta.call_delta != null && (
            <div style={{ ...metaBox, marginTop: 0 }}>
              <div style={metaTitle}>Current Delta State</div>
              <div style={{ display: "flex", gap: 20, flexWrap: "wrap", fontSize: 12, color: "#555", marginBottom: 10 }}>
                <span><strong>Call Δ:</strong> {Number(meta.call_delta).toFixed(4)}</span>
                <span><strong>Put Δ:</strong>  {Number(meta.put_delta).toFixed(4)}</span>
                <span><strong>Imbalance:</strong> <span style={{ color: "#dc3545", fontWeight: 700 }}>{Number(meta.delta_difference).toFixed(4)}</span></span>
                <span><strong>Threshold:</strong> {Number(meta.threshold).toFixed(4)}</span>
                <span><strong>Heavier side:</strong> {meta.heavier_side}</span>
                {meta.underlying_ltp && <span><strong>LTP:</strong> ₹{meta.underlying_ltp}</span>}
              </div>

              {meta.proposed_orders && meta.proposed_orders.length > 0 && (
                <>
                  <div style={metaTitle}>Proposed Trade{meta.proposed_orders.length > 1 ? "s" : ""}</div>
                  {meta.proposed_orders.map((o, i) => {
                    const isSell = o.side === "SELL";
                    const stepLabels = {
                      profitable_squareoff_heavier:        "Square off profitable position",
                      trim_a_profitable_squareoff_heavier: "Square off profitable position",
                      trim_b_heavier_loss:                 "Close heavier-side position (loss)",
                      trim_b_lighter_profit:               "Close lighter-side position (profit)",
                      sell_lighter_side:                   "Sell new contract",
                      roll_close_lighter:                  "Close existing position (roll)",
                      roll_sell_lighter_new_strike:        "Sell new contract (rolled)",
                      roll_squareoff_all:                  "Square off entire strangle",
                    };
                    return (
                      <div key={i} style={{
                        background: isSell ? "#d4edda" : "#e2e3e5",
                        border: `1px solid ${isSell ? "#b1dfbb" : "#cfd2d4"}`,
                        borderRadius: 8, padding: "10px 14px", marginBottom: 8, fontSize: 13,
                      }}>
                        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
                          {o.side} {o.lots} lot{o.lots > 1 ? "s" : ""} — {o.symbol || `${row.symbol} ${o.optiontype} ${o.strike}`}
                        </div>
                        <div style={{ fontSize: 11, color: "#777", marginBottom: 4 }}>
                          {stepLabels[o.step] || o.step}
                        </div>
                        <div style={{ display: "flex", gap: 18, flexWrap: "wrap", fontSize: 12, color: "#555" }}>
                          {o.strike != null && <span><strong>Strike:</strong> ₹{o.strike}</span>}
                          {o.optiontype && <span><strong>Type:</strong> {o.optiontype}</span>}
                          {o.delta != null && <span><strong>Delta:</strong> {Number(o.delta).toFixed(4)}</span>}
                          {o.profit != null && (
                            <span style={{ color: "#28a745" }}>
                              <strong>Profit:</strong> ₹{Number(o.profit).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                            </span>
                          )}
                          {o.loss != null && (
                            <span style={{ color: "#dc3545" }}>
                              <strong>Loss:</strong> ₹{Math.abs(Number(o.loss)).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                            </span>
                          )}
                          {o.expiry_used && <span><strong>Expiry:</strong> {o.expiry_used}</span>}
                          {o.min_dist_pct != null && <span><strong>Min distance:</strong> {o.min_dist_pct}%</span>}
                        </div>
                      </div>
                    );
                  })}
                  {meta.projected_delta_diff != null && (
                    <div style={{ fontSize: 12, color: "#555", marginBottom: 8 }}>
                      <strong>Projected imbalance after trade:</strong>{" "}
                      <span style={{ color: meta.projected_delta_diff <= (meta.threshold || 0) ? "#28a745" : "#fd7e14", fontWeight: 700 }}>
                        {Number(meta.projected_delta_diff).toFixed(4)}
                      </span>
                      {meta.projected_delta_diff <= (meta.threshold || 0)
                        ? " ✓ within threshold"
                        : " (further rebalancing may be needed)"}
                    </div>
                  )}
                </>
              )}
              {(!meta.proposed_orders || meta.proposed_orders.length === 0) && (
                <div style={{ fontSize: 12, color: "#888" }}>
                  {meta.plan_reason
                    ? `No qualifying rebalance trade found (${meta.plan_reason}).`
                    : `Rebalance plan: close profitable positions on the ${meta.heavier_side} side.`}
                </div>
              )}
            </div>
          )}

          {/* Rich card for OPEN_STRADDLE — shows pairs, today's ATM strikes/deltas, net delta diff */}
          {row.action_type === "OPEN_STRADDLE" && (
            <div style={{ ...metaBox, marginTop: 0 }}>
              <div style={metaTitle}>Today's Straddle Plan</div>
              <div style={{ display: "flex", gap: 20, flexWrap: "wrap", fontSize: 12, color: "#555" }}>
                <span><strong>Pairs:</strong> {meta.pairs ?? "—"}</span>
                {meta.expiry && <span><strong>Expiry:</strong> {meta.expiry}</span>}
                {meta.call_strike != null && <span><strong>Call Strike:</strong> ₹{meta.call_strike}</span>}
                {meta.call_delta != null && <span><strong>Call Δ:</strong> {Number(meta.call_delta).toFixed(4)}</span>}
                {meta.put_strike != null && <span><strong>Put Strike:</strong> ₹{meta.put_strike}</span>}
                {meta.put_delta != null && <span><strong>Put Δ:</strong> {Number(meta.put_delta).toFixed(4)}</span>}
                {meta.net_delta_diff != null && (
                  <span><strong>Net Δ Diff:</strong> {Number(meta.net_delta_diff).toFixed(4)}</span>
                )}
              </div>
              {meta.call_strike == null && (
                <div style={{ fontSize: 12, color: "#888", marginTop: 6 }}>
                  Preview unavailable (no live quote at suggestion time) — strikes/deltas will be resolved fresh at execution.
                </div>
              )}
            </div>
          )}

          {/* Rich card for ROLL_STRADDLE_NEXT_EXPIRY — shows which leg decayed and the current expiry being left */}
          {row.action_type === "ROLL_STRADDLE_NEXT_EXPIRY" && (
            <div style={{ ...metaBox, marginTop: 0 }}>
              <div style={metaTitle}>Low Premium — Roll to Next Expiry</div>
              <div style={{ display: "flex", gap: 20, flexWrap: "wrap", fontSize: 12, color: "#555" }}>
                {meta.current_expiry && <span><strong>Current Expiry:</strong> {meta.current_expiry}</span>}
                {meta.current_strike != null && <span><strong>Current Strike:</strong> ₹{meta.current_strike}</span>}
                {meta.call_ltp != null && <span><strong>Call LTP:</strong> ₹{meta.call_ltp}</span>}
                {meta.put_ltp != null && <span><strong>Put LTP:</strong> ₹{meta.put_ltp}</span>}
                {meta.min_leg_premium != null && <span><strong>Threshold:</strong> ₹{meta.min_leg_premium}</span>}
              </div>
              <div style={{ fontSize: 12, color: "#888", marginTop: 6 }}>
                Closes both legs and reopens a fresh ATM straddle at the next weekly expiry — strike/expiry resolved fresh at execution.
              </div>
            </div>
          )}

          {/* Live position preview — shown for pending square-off actions */}
          {row.status === "PENDING" && isSquareOff && (
            <div style={{ ...metaBox, marginTop: 0 }}>
              <div style={metaTitle}>{row.action_type === "MAKE_DELTA_NEUTRAL" ? "Positions to close (heavier side)" : "Positions that will be affected"}</div>
              {previewLoading && <div style={{ fontSize: 12, color: "#888" }}>Loading positions…</div>}
              {!previewLoading && preview && preview.length === 0 && row.action_type !== "MAKE_DELTA_NEUTRAL" && (
                <div style={{ fontSize: 12, color: "#aaa" }}>No open positions found for {row.symbol}.</div>
              )}
              {!previewLoading && preview && preview.length === 0 && row.action_type === "MAKE_DELTA_NEUTRAL" && (
                <div style={{ fontSize: 12, color: "#888" }}>No profitable heavier-side positions to close — will sell on lighter side only.</div>
              )}
              {!previewLoading && preview && preview.length > 0 && (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                      <tr style={{ color: "#6c757d", borderBottom: "1px solid #e9ecef" }}>
                        <th style={th}>Type</th>
                        <th style={th}>Strike</th>
                        <th style={th}>Expiry</th>
                        <th style={th}>Lots</th>
                        <th style={th}>LTP</th>
                        <th style={th}>Unrealised P&amp;L</th>
                        <th style={th}>Delta</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.map((p, i) => {
                        const isStock  = p.optiontype === "STOCK";
                        const isFuture = p.optiontype === "FUT";
                        const typeBadgeStyle = isStock
                          ? { background: "#e2e3e5", color: "#383d41" }
                          : isFuture
                            ? { background: "#fff3cd", color: "#856404" }
                            : p.optiontype === "CE"
                              ? { background: "#cce5ff", color: "#004085" }
                              : { background: "#fde2e2", color: "#721c24" };
                        return (
                          <tr key={i} style={{ borderBottom: "1px solid #f0f0f0", background: isStock || isFuture ? "#f8f9fb" : "transparent" }}>
                            <td style={td}>
                              <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 700, ...typeBadgeStyle }}>
                                {p.optiontype}
                              </span>
                            </td>
                            <td style={{ ...td, fontWeight: 600 }}>
                              {(isStock || isFuture)
                                ? (p.strikeprice != null ? `Avg ₹${p.strikeprice.toFixed(2)}` : "—")
                                : p.strikeprice?.toLocaleString("en-IN") ?? "—"}
                            </td>
                            <td style={td}>{isStock ? "—" : (p.expirydate || "—")}</td>
                            <td style={{ ...td, fontWeight: 600 }}>{p.lots}</td>
                            <td style={td}>{p.ltp != null ? `₹${p.ltp.toFixed(2)}` : "—"}</td>
                            <td style={{
                              ...td, fontWeight: 600,
                              color: (p.unrealised ?? 0) >= 0 ? "#28a745" : "#dc3545",
                            }}>
                              {p.unrealised != null ? `₹${p.unrealised.toLocaleString("en-IN", { maximumFractionDigits: 0 })}` : "—"}
                            </td>
                            <td style={td}>{(isStock || isFuture) ? "—" : (p.delta?.toFixed(3) ?? "—")}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── ROLL_FUTURE_LEG: outgoing + incoming panels ── */}
          {row.action_type === "ROLL_FUTURE_LEG" && (() => {
            const out    = meta.outgoing_leg || {};
            const lotSz  = meta.lot_size || 1;
            const outLots = out.lots ?? (out.netqty != null ? Math.round(Math.abs(out.netqty) / lotSz) : null);
            const fmtPnl = v => v != null
              ? <span style={{ fontWeight: 700, color: v >= 0 ? "#28a745" : "#dc3545" }}>
                  {v >= 0 ? "+" : ""}₹{Math.abs(v).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                </span>
              : "—";
            return (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
                {/* Outgoing (expiring) future */}
                <div style={{ background: "#fde2e2", border: "1px solid #f5c6cb", borderRadius: 8, padding: "12px 14px" }}>
                  <div style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, color: "#842029", marginBottom: 8, letterSpacing: 0.6 }}>
                    Close (SELL) · Expiring Future
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#222", marginBottom: 6 }}>{out.symbol || meta.future_symbol || "—"}</div>
                  <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12, color: "#555" }}>
                    {out.expiry     && <span><strong>Expiry:</strong> {out.expiry}</span>}
                    {meta.future_dte != null && <span><strong>DTE:</strong> {meta.future_dte}d</span>}
                    {outLots        != null && <span><strong>Lots:</strong> {outLots}</span>}
                    {out.netqty     != null && <span style={{ color: "#888" }}>({Math.abs(out.netqty)} shares)</span>}
                    {out.avg_price  != null && <span><strong>Avg Price:</strong> ₹{Number(out.avg_price).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>}
                  </div>
                  {out.unrealised != null && (
                    <div style={{ marginTop: 8, fontSize: 12 }}>
                      <strong>Unrealised P&amp;L:</strong> {fmtPnl(out.unrealised)}
                    </div>
                  )}
                </div>
                {/* Incoming (far-month) future */}
                <div style={{ background: "#d4edda", border: "1px solid #c3e6cb", borderRadius: 8, padding: "12px 14px" }}>
                  <div style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, color: "#155724", marginBottom: 8, letterSpacing: 0.6 }}>
                    Open (BUY) · Far-Month Future
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#222", marginBottom: 6 }}>
                    {row.symbol} — next month
                  </div>
                  <div style={{ fontSize: 12, color: "#555" }}>
                    Same quantity as outgoing leg{outLots != null ? ` (${outLots} lots)` : ""}.
                    Contract symbol resolved at execution.
                  </div>
                </div>
              </div>
            );
          })()}

          {/* PARTIAL_FUTURE_DERISK_ON_UNRESCUABLE_LOSS — trim 1 lot of the best-performing future leg */}
          {row.action_type === "SQUARE_OFF_FUTURES" && (() => {
            const d = meta.metadata || {};
            if (!d.future_symbol) return null;
            const fmtPnl = v => v != null
              ? <span style={{ fontWeight: 700, color: v >= 0 ? "#28a745" : "#dc3545" }}>
                  {v >= 0 ? "+" : "-"}₹{Math.abs(Number(v)).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                </span>
              : "—";
            return (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
                <div style={{ background: "#fde2e2", border: "1px solid #f5c6cb", borderRadius: 8, padding: "12px 14px" }}>
                  <div style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, color: "#842029", marginBottom: 8, letterSpacing: 0.6 }}>
                    Close (SELL) · {d.lots_to_close || 1} Lot — Best-Performing Leg
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#222", marginBottom: 6 }}>{d.future_symbol}</div>
                  <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12, color: "#555" }}>
                    {d.future_expiry && <span><strong>Expiry:</strong> {d.future_expiry}</span>}
                    {d.total_future_lots != null && <span><strong>Of:</strong> {d.total_future_lots} open lot{d.total_future_lots === 1 ? "" : "s"}</span>}
                  </div>
                  {d.unrealised != null && (
                    <div style={{ marginTop: 8, fontSize: 12 }}>
                      <strong>Leg Unrealised P&amp;L:</strong> {fmtPnl(d.unrealised)}
                      <span style={{ color: "#888", marginLeft: 6 }}>(highest of all open legs — crystallized, not the worst one)</span>
                    </div>
                  )}
                </div>
                <div style={{ background: "#d4edda", border: "1px solid #c3e6cb", borderRadius: 8, padding: "12px 14px" }}>
                  <div style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, color: "#155724", marginBottom: 8, letterSpacing: 0.6 }}>
                    Unchanged
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#222", marginBottom: 6 }}>
                    {d.current_call_symbol || "Active call"}{d.current_call_lots != null ? ` (${d.current_call_lots} lots)` : ""}
                  </div>
                  <div style={{ fontSize: 12, color: "#555" }}>
                    Every other future leg and the full call position stay open —
                    only this one lot is being trimmed.
                  </div>
                </div>
                <div style={{ gridColumn: "1 / -1", background: "#f8f9fb", borderRadius: 8, padding: "10px 14px" }}>
                  <div style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, color: "#6c757d", marginBottom: 6, letterSpacing: 0.6 }}>
                    Why this fired
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 24px", fontSize: 12 }}>
                    {d.futures_only_hedge_pct != null && (
                      <span><strong>Futures-Only Hedge:</strong> {d.futures_only_hedge_pct}% <span style={{ color: "#888" }}>(floor {d.hedge_exit_floor_pct}%)</span></span>
                    )}
                    {d.worst_case_expiry_profit != null && (
                      <span><strong>Worst-Case P&amp;L:</strong> {fmtPnl(d.worst_case_expiry_profit)} <span style={{ color: "#888" }}>(floor {fmtPnl(d.max_loss_csm)})</span></span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: "#888", marginTop: 6 }}>
                    No rolldown, add-lot, or rollout could restore the hedge — this trims exposure
                    gradually instead of squaring off the whole position.
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Net P&L summary for SQUARE_OFF_CALL_AND_STOCK */}
          {row.action_type === "SQUARE_OFF_CALL_AND_STOCK" && (() => {
            // Use live preview data when available (PENDING + loaded) so values
            // match the positions table exactly. Fall back to metadata for
            // completed/historical actions.
            const fmtRs = v => v != null ? `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 0 })}` : "—";
            let stockPnl = null, callPnl = null, putPnl = null;
            if (preview && preview.length > 0) {
              preview.forEach(p => {
                if (p.optiontype === "STOCK") stockPnl = (stockPnl || 0) + (p.unrealised || 0);
                else if (p.optiontype === "CE")  callPnl  = (callPnl  || 0) + (p.unrealised || 0);
                else if (p.optiontype === "PE")  putPnl   = (putPnl   || 0) + (p.unrealised || 0);
              });
            } else {
              const pnl = row.metadata?.metadata || {};
              stockPnl = pnl.stock_profit ?? null;
              callPnl  = pnl.call_profit  ?? null;
              putPnl   = pnl.will_close_put ? (pnl.put_unrealised ?? null) : null;
            }
            const net = (stockPnl != null || callPnl != null || putPnl != null)
              ? (stockPnl || 0) + (callPnl || 0) + (putPnl || 0)
              : null;
            if (stockPnl == null && callPnl == null) return null;
            return (
              <div style={{ ...metaBox, marginTop: 0 }}>
                <div style={metaTitle}>Net Profit After Squareoff</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 32px", fontSize: 13 }}>
                  {stockPnl != null && (
                    <span>
                      <strong>Stock P&amp;L:</strong>{" "}
                      <span style={{ color: stockPnl >= 0 ? "#28a745" : "#dc3545", fontWeight: 700 }}>{fmtRs(stockPnl)}</span>
                    </span>
                  )}
                  {callPnl != null && (
                    <span>
                      <strong>Call P&amp;L:</strong>{" "}
                      <span style={{ color: callPnl >= 0 ? "#28a745" : "#dc3545", fontWeight: 700 }}>{fmtRs(callPnl)}</span>
                    </span>
                  )}
                  {putPnl != null && (
                    <span>
                      <strong>Put P&amp;L:</strong>{" "}
                      <span style={{ color: putPnl >= 0 ? "#28a745" : "#dc3545", fontWeight: 700 }}>{fmtRs(putPnl)}</span>
                    </span>
                  )}
                  {net != null && (
                    <span style={{ borderLeft: "2px solid #dee2e6", paddingLeft: 16 }}>
                      <strong>Net:</strong>{" "}
                      <span style={{ color: net >= 0 ? "#28a745" : "#dc3545", fontWeight: 700, fontSize: 14 }}>{fmtRs(net)}</span>
                    </span>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Rich panel for TRIM_CALL_LOT_ROLLDOWN (Margin Release) */}
          {row.action_type === "TRIM_CALL_LOT_ROLLDOWN" && meta.proposed_call && (() => {
            const cur = meta.current_call || {};
            const prop = meta.proposed_call || {};
            const fmtRs = v => v != null ? `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 0 })}` : "—";
            const pnlClr = v => v == null ? "#888" : v >= 0 ? "#28a745" : "#dc3545";
            return (
              <div style={{ ...metaBox, marginTop: 0 }}>
                <div style={metaTitle}>Margin Release — {row.symbol}</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 32px", fontSize: 13, marginBottom: 8 }}>
                  <span><strong>Close:</strong> {cur.symbol} × {cur.lots} @ {cur.ltp} (δ {cur.delta}/lot)</span>
                  <span><strong>Sell:</strong> {prop.symbol} × {prop.lots} @ {prop.ltp} (δ {prop.delta}/lot)</span>
                  <span><strong>Call P&amp;L booked:</strong> <span style={{ color: pnlClr(cur.unrealised), fontWeight: 700 }}>{fmtRs(cur.unrealised)}</span></span>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 32px", fontSize: 13, marginBottom: 8 }}>
                  <span><strong>Calls : hedged lots:</strong> {meta.ratio_before} → {meta.ratio_after}</span>
                  <span><strong>Hedge:</strong> {cur.hedge_pct}% → {prop.hedge_pct}% (target {meta.target_hedge_pct}%)</span>
                  {meta.target_delta_miss != null && meta.target_delta_miss > 0.03 && (
                    <span style={{ color: "#b8860b" }}><strong>Target δ miss:</strong> {meta.target_delta_miss} (target {meta.target_delta_per_lot}/lot)</span>
                  )}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 32px", fontSize: 13 }}>
                  <span><strong>Buyback:</strong> {fmtRs(meta.buyback_cost)}</span>
                  <span><strong>New premium:</strong> {fmtRs(meta.new_premium)}</span>
                  <span><strong>Expiry potential (incl. booked):</strong> <span style={{ color: pnlClr(meta.potential_before) }}>{fmtRs(meta.potential_before)}</span> → <span style={{ color: pnlClr(meta.potential_after), fontWeight: 700 }}>{fmtRs(meta.potential_after)}</span> (floor {fmtRs(meta.min_profit_csm)})</span>
                  <span style={{ borderLeft: "2px solid #dee2e6", paddingLeft: 16 }}>
                    <strong>Est. margin released:</strong>{" "}
                    <span style={{ color: "#28a745", fontWeight: 700, fontSize: 14 }}>{fmtRs(meta.estimated_margin_release)}</span>
                  </span>
                </div>
              </div>
            );
          })()}

          {/* Rich panel for SQUARE_OFF_MAX_DECAY_POSITION */}
          {row.action_type === "SQUARE_OFF_MAX_DECAY_POSITION" && (() => {
            const pnl = row.metadata?.metadata || {};
            if (!pnl.total_investment && !pnl.net_profit) return null;
            const fmtRs = v => v != null ? `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 0 })}` : "—";
            return (
              <div style={{ ...metaBox, marginTop: 0 }}>
                <div style={metaTitle}>Position Summary — {pnl.symbol || row.symbol}</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 32px", fontSize: 13, marginBottom: 8 }}>
                  {pnl.position_type != null && (
                    <span><strong>Position Type:</strong> {pnl.position_type === "FUTURES" ? "Futures" : "Stock"}</span>
                  )}
                  {pnl.stock_qty != null && pnl.avg_price != null && (
                    <span><strong>Stock:</strong> {pnl.stock_qty} shares @ {fmtRs(pnl.avg_price)}</span>
                  )}
                  {pnl.total_investment != null && (
                    <span><strong>Capital Deployed:</strong> <span style={{ fontWeight: 700 }}>{fmtRs(pnl.total_investment)}</span></span>
                  )}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 32px", fontSize: 13 }}>
                  {pnl.stock_unrealised != null && pnl.stock_unrealised !== 0 && (
                    <span>
                      <strong>Stock P&amp;L:</strong>{" "}
                      <span style={{ color: pnl.stock_unrealised >= 0 ? "#28a745" : "#dc3545", fontWeight: 700 }}>
                        {fmtRs(pnl.stock_unrealised)}
                      </span>
                    </span>
                  )}
                  {pnl.future_unrealised != null && pnl.future_unrealised !== 0 && (
                    <span>
                      <strong>Future P&amp;L:</strong>{" "}
                      <span style={{ color: pnl.future_unrealised >= 0 ? "#28a745" : "#dc3545", fontWeight: 700 }}>
                        {fmtRs(pnl.future_unrealised)}
                      </span>
                    </span>
                  )}
                  {pnl.call_unrealised != null && (
                    <span>
                      <strong>Call P&amp;L:</strong>{" "}
                      <span style={{ color: pnl.call_unrealised >= 0 ? "#28a745" : "#dc3545", fontWeight: 700 }}>
                        {fmtRs(pnl.call_unrealised)}
                      </span>
                    </span>
                  )}
                  {pnl.csp_unrealised != null && pnl.csp_unrealised !== 0 && (
                    <span>
                      <strong>Put P&amp;L:</strong>{" "}
                      <span style={{ color: pnl.csp_unrealised >= 0 ? "#28a745" : "#dc3545", fontWeight: 700 }}>
                        {fmtRs(pnl.csp_unrealised)}
                      </span>
                    </span>
                  )}
                  {pnl.net_profit != null && (
                    <span style={{ borderLeft: "2px solid #dee2e6", paddingLeft: 16 }}>
                      <strong>Net P&amp;L:</strong>{" "}
                      <span style={{ color: pnl.net_profit >= 0 ? "#28a745" : "#dc3545", fontWeight: 700, fontSize: 14 }}>
                        {fmtRs(pnl.net_profit)}
                      </span>
                    </span>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Rich panel for SQUARE_OFF_MIN_STOCK_MAX_DECAY_POSITION (Cash Guard Strangle First) */}
          {row.action_type === "SQUARE_OFF_MIN_STOCK_MAX_DECAY_POSITION" && (() => {
            const pnl = row.metadata?.metadata || {};
            if (!pnl.symbol && !pnl.net_profit) return null;
            const fmtRs = v => v != null ? `${v >= 0 ? "+" : "-"}₹${Math.abs(Number(v)).toLocaleString("en-IN", { maximumFractionDigits: 0 })}` : "—";
            const pnlClr = v => v == null ? "#888" : v >= 0 ? "#28a745" : "#dc3545";
            return (
              <div style={{ ...metaBox, marginTop: 0 }}>
                <div style={metaTitle}>Position to Exit — {pnl.symbol || row.symbol}</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 32px", fontSize: 13, marginBottom: 8 }}>
                  {pnl.stock_qty != null && <span><strong>Stock Qty:</strong> {pnl.stock_qty} shares ({pnl.stock_lots != null ? `${pnl.stock_lots} lots` : ""})</span>}
                  {pnl.avg_price != null && <span><strong>Avg Price:</strong> ₹{Number(pnl.avg_price).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>}
                  {pnl.total_investment != null && <span><strong>Capital Deployed:</strong> <span style={{ fontWeight: 700 }}>₹{Number(pnl.total_investment).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</span></span>}
                  {pnl.decay_pct != null && <span><strong>Option Decay:</strong> <span style={{ color: "#28a745", fontWeight: 700 }}>{(Number(pnl.decay_pct) * 100).toFixed(1)}%</span></span>}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 32px", fontSize: 13 }}>
                  {pnl.stock_unrealised != null && (
                    <span><strong>Stock P&amp;L:</strong> <span style={{ color: pnlClr(pnl.stock_unrealised), fontWeight: 700 }}>{fmtRs(pnl.stock_unrealised)}</span></span>
                  )}
                  {pnl.call_unrealised != null && (
                    <span><strong>Call P&amp;L:</strong> <span style={{ color: pnlClr(pnl.call_unrealised), fontWeight: 700 }}>{fmtRs(pnl.call_unrealised)}</span></span>
                  )}
                  {pnl.put_unrealised != null && pnl.put_unrealised !== 0 && (
                    <span><strong>Put P&amp;L:</strong> <span style={{ color: pnlClr(pnl.put_unrealised), fontWeight: 700 }}>{fmtRs(pnl.put_unrealised)}</span></span>
                  )}
                  {pnl.net_profit != null && (
                    <span style={{ borderLeft: "2px solid #dee2e6", paddingLeft: 16 }}>
                      <strong>Net P&amp;L:</strong> <span style={{ color: pnlClr(pnl.net_profit), fontWeight: 700, fontSize: 14 }}>{fmtRs(pnl.net_profit)}</span>
                    </span>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Rich panel for SQUARE_OFF_FUTURES_MAX_CALL_DECAY_POSITION (Cash Guard Futures Call Decay) */}
          {row.action_type === "SQUARE_OFF_FUTURES_MAX_CALL_DECAY_POSITION" && (() => {
            const pnl = row.metadata?.metadata || {};
            if (!pnl.symbol && !pnl.net_profit) return null;
            const fmtRs = v => v != null ? `${v >= 0 ? "+" : "-"}₹${Math.abs(Number(v)).toLocaleString("en-IN", { maximumFractionDigits: 0 })}` : "—";
            const pnlClr = v => v == null ? "#888" : v >= 0 ? "#28a745" : "#dc3545";
            return (
              <div style={{ ...metaBox, marginTop: 0 }}>
                <div style={metaTitle}>Position to Exit (Futures + Options Only) — {pnl.symbol || row.symbol}</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 32px", fontSize: 13, marginBottom: 8 }}>
                  {pnl.decay_pct != null && <span><strong>Call Decay:</strong> <span style={{ color: "#28a745", fontWeight: 700 }}>{(Number(pnl.decay_pct) * 100).toFixed(1)}%</span></span>}
                  {pnl.call_potential_pnl != null && <span><strong>Call Potential P&amp;L:</strong> {fmtRs(pnl.call_potential_pnl)}</span>}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 32px", fontSize: 13 }}>
                  {pnl.future_unrealised != null && (
                    <span><strong>Future P&amp;L:</strong> <span style={{ color: pnlClr(pnl.future_unrealised), fontWeight: 700 }}>{fmtRs(pnl.future_unrealised)}</span></span>
                  )}
                  {pnl.call_unrealised != null && (
                    <span><strong>Call P&amp;L:</strong> <span style={{ color: pnlClr(pnl.call_unrealised), fontWeight: 700 }}>{fmtRs(pnl.call_unrealised)}</span></span>
                  )}
                  {pnl.csp_unrealised != null && pnl.csp_unrealised !== 0 && (
                    <span><strong>Put P&amp;L:</strong> <span style={{ color: pnlClr(pnl.csp_unrealised), fontWeight: 700 }}>{fmtRs(pnl.csp_unrealised)}</span></span>
                  )}
                  {pnl.net_profit != null && (
                    <span style={{ borderLeft: "2px solid #dee2e6", paddingLeft: 16 }}>
                      <strong>Net P&amp;L:</strong> <span style={{ color: pnlClr(pnl.net_profit), fontWeight: 700, fontSize: 14 }}>{fmtRs(pnl.net_profit)}</span>
                    </span>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Orders placed by this action */}
          {row.status !== "PENDING" && row.status !== "REJECTED" && (
            <div style={{ ...metaBox, marginTop: 0 }}>
              <div style={metaTitle}>Orders Placed</div>
              {ordersLoading && <div style={{ fontSize: 12, color: "#888" }}>Loading orders…</div>}
              {!ordersLoading && orders && orders.length === 0 && (
                <div style={{ fontSize: 12, color: "#aaa" }}>No orders found for this action yet.</div>
              )}
              {!ordersLoading && orders && orders.length > 0 && (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ color: "#6c757d", borderBottom: "1px solid #e9ecef" }}>
                      <th style={th}>Symbol</th>
                      <th style={th}>Side</th>
                      <th style={th}>Qty</th>
                      <th style={th}>Type</th>
                      <th style={th}>Status</th>
                      <th style={th}>Time</th>
                      <th style={th}>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map(o => {
                      const oSm = STATUS_META[o.status] || {};
                      return (
                        <tr key={o.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                          <td style={td}>{o.symbol}</td>
                          <td style={{ ...td, color: o.side === "BUY" ? "#28a745" : "#dc3545", fontWeight: 600 }}>{o.side}</td>
                          <td style={td}>{o.quantity}</td>
                          <td style={td}>{o.order_type}</td>
                          <td style={td}>
                            <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600, background: oSm.bg || "#eee", color: oSm.color || "#555" }}>
                              {o.status}
                            </span>
                          </td>
                          <td style={td}>{toIST(o.executed_at || o.created_at)}</td>
                          <td style={{ ...td, color: "#dc3545", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {o.error_message || "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {isActivelySnoozed && row.snooze_until && (
            <div style={{ fontSize: 12, color: "#856404", background: "#fff3cd", borderRadius: 6, padding: "6px 10px", marginBottom: 10 }}>
              ⏸ Snoozed until: <strong>{toIST(row.snooze_until)}</strong>
            </div>
          )}

          {/* Action buttons */}
          {row.status === "PENDING" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {/* Qty override for BUY_STOCK */}
              {isBuyStock && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, background: "#f0f8ff", borderRadius: 8, padding: "8px 12px" }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#2c3e50" }}>Qty to buy:</span>
                  <input
                    type="number"
                    min="1"
                    value={editQty}
                    onChange={e => setEditQty(e.target.value)}
                    style={{ width: 90, padding: "5px 8px", borderRadius: 6, border: "1px solid #ced4da", fontSize: 13, textAlign: "right" }}
                    placeholder={defaultQty || "qty"}
                  />
                  {editQty !== defaultQty && editQty !== "" && (
                    <span style={{ fontSize: 11, color: "#856404", background: "#fff3cd", padding: "2px 8px", borderRadius: 10 }}>
                      Modified (original: {defaultQty})
                    </span>
                  )}
                </div>
              )}
              {/* Month choice for BUY_FUTURE / REBUILD_NEXT_CYCLE */}
              {hasFutureMonthChoice && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, background: "#f0f8ff", borderRadius: 8, padding: "8px 12px" }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#2c3e50" }}>Contract:</span>
                  <select
                    value={editFutureSymbol}
                    onChange={e => setEditFutureSymbol(e.target.value)}
                    style={{ padding: "5px 8px", borderRadius: 6, border: "1px solid #ced4da", fontSize: 13 }}
                  >
                    {futureMonthOptions.map((opt, i) => (
                      <option key={opt.symbol} value={opt.symbol}>
                        {["Current month", "Next month", "Far month"][i] || `Month ${i + 1}`}
                        {" — "}{opt.symbol} ({String(opt.expiry).slice(0, 10)})
                      </option>
                    ))}
                  </select>
                  {editFutureSymbol !== (row.metadata?.future_symbol || "") && (
                    <span style={{ fontSize: 11, color: "#856404", background: "#fff3cd", padding: "2px 8px", borderRadius: 10 }}>
                      Modified
                    </span>
                  )}
                </div>
              )}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  onClick={() => onApprove(
                    isBuyStock ? editQty : undefined,
                    hasFutureMonthChoice ? editFutureSymbol : undefined,
                    hasFutureMonthChoice ? futureMonthOptions.find(o => o.symbol === editFutureSymbol)?.expiry : undefined,
                  )}
                  disabled={acting}
                  style={btnApprove}
                >
                  {acting ? "…" : "✓ Approve"}
                </button>
                <button onClick={onReject} disabled={acting} style={btnReject}>
                  {acting ? "…" : "✕ Reject"}
                </button>
                <button onClick={() => onSnooze(60)} disabled={acting} style={btnSnooze}>
                  Snooze 1h
                </button>
                <button onClick={() => onSnooze(480)} disabled={acting} style={btnSnooze}>
                  Snooze 8h
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Auto Rebalance Card (DHSS v2.0) ─────────────────────────── */

const RULE_META = {
  a: { letter: "A", color: "#28a745", bg: "#d4edda", label: "Close heavier-side option" },
  b: { letter: "B", color: "#007bff", bg: "#cce5ff", label: "Sell on lighter side" },
  c: { letter: "C", color: "#fd7e14", bg: "#fff3cd", label: "Extend losing-side band" },
  d: { letter: "D", color: "#6f42c1", bg: "#e8d5ff", label: "Close most-decayed option" },
};

/* ── SpreadBadge helper ───────────────────────────────────────── */

function NavButtons({ symbol }) {
  return (
    <div
      style={{ display: "flex", gap: 6, marginLeft: 10, flexShrink: 0 }}
      onClick={e => e.stopPropagation()}
    >
      <a
        href={`/strategy/manage?symbol=${encodeURIComponent(symbol)}`}
        target="_blank"
        rel="noreferrer"
        style={navBtnStyle}
      >
        View Strategy
      </a>
      <a
        href={`/trading/${encodeURIComponent(symbol)}`}
        target="_blank"
        rel="noreferrer"
        style={{ ...navBtnStyle, background: "#e8f4fd", color: "#004085", borderColor: "#b8d9f0" }}
      >
        View Position
      </a>
    </div>
  );
}

function SpreadBadge({ pct }) {
  if (pct == null) return <span style={{ color: "#aaa", fontSize: 11 }}>spread N/A</span>;
  const color = pct < 5 ? "#28a745" : pct < 10 ? "#856404" : "#dc3545";
  const bg    = pct < 5 ? "#d4edda" : pct < 10 ? "#fff3cd" : "#fde2e2";
  const label = pct < 5 ? "liquid" : pct < 10 ? "marginal" : "wide";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      <span style={{ padding: "1px 7px", borderRadius: 10, fontSize: 11, fontWeight: 700, background: bg, color }}>
        {pct.toFixed(1)}% spread
      </span>
      <span style={{ fontSize: 11, color }}>{label}</span>
    </span>
  );
}

function LiquidityRow({ bid, ask, spread_pct, oi }) {
  return (
    <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12, color: "#555", marginTop: 6 }}>
      {bid        != null && <span><strong>Bid:</strong> ₹{Number(bid).toFixed(2)}</span>}
      {ask        != null && <span><strong>Ask:</strong> ₹{Number(ask).toFixed(2)}</span>}
      {spread_pct != null && <span><strong>Spread:</strong> <SpreadBadge pct={spread_pct} /></span>}
      {oi         != null && <span><strong>OI:</strong> {Number(oi).toLocaleString("en-IN")}</span>}
    </div>
  );
}

/* ── RollOutCallCard ──────────────────────────────────────────── */

function RollUpOptionsCard({
  callRow, cspRow,
  expanded, onToggle,
  onRejectCall, onRejectCsp,
  onSnoozeCall, onSnoozeCsp,
  onOptimisticApproveCall, onOptimisticApproveCsp,
  acting, checked, onCheck,
  username, showAccount, flash, fetchAll,
}) {
  const row = callRow || cspRow;   // primary row for status/header (cspRow when call-only)
  const isActivelySnoozed = row.status === "PENDING"
    && row.snooze_until
    && new Date(row.snooze_until.replace(" ", "T") + "Z") > new Date();
  const displayStatus = isActivelySnoozed ? "SNOOZED" : row.status;
  const sm = STATUS_META[displayStatus] || STATUS_META.PENDING;
  const [submittingCall, setSubmittingCall] = useState(false);
  const [submittingCsp,  setSubmittingCsp]  = useState(false);
  const meta    = callRow?.metadata || {};
  const cspMeta = cspRow?.metadata  || {};

  const fmtRs  = v => v != null ? `₹${Math.abs(Number(v)).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—";
  const fmtPnl = v => v == null ? "—" : `${Number(v) >= 0 ? "+" : "−"}₹${Math.abs(Number(v)).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
  const fmtDec = (v, d = 2) => v != null ? Number(v).toFixed(d) : "—";
  const pnlClr = v => v == null ? "#888" : Number(v) >= 0 ? "#28a745" : "#dc3545";

  const approveCall = async () => {
    setSubmittingCall(true);
    try {
      await axios.post("/automation/suggestions/approve", { id: callRow.id, username });
      onOptimisticApproveCall?.();
      flash("Call rollup approved — orders queued");
      fetchAll();
    } catch { flash("Approval failed.", false); }
    finally { setSubmittingCall(false); }
  };

  const approveCsp = async () => {
    if (!cspRow) return;
    setSubmittingCsp(true);
    try {
      await axios.post("/automation/suggestions/approve", { id: cspRow.id, username });
      onOptimisticApproveCsp?.();
      flash("Put rollup approved — orders queued");
      fetchAll();
    } catch { flash("Approval failed.", false); }
    finally { setSubmittingCsp(false); }
  };

  const approveBoth = async () => {
    setSubmittingCall(true); setSubmittingCsp(true);
    try {
      await axios.post("/automation/suggestions/approve", { id: callRow.id, username });
      if (cspRow) await axios.post("/automation/suggestions/approve", { id: cspRow.id, username });
      onOptimisticApproveCall?.(); onOptimisticApproveCsp?.();
      flash("Both legs approved — orders queued");
      fetchAll();
    } catch { flash("Approval failed.", false); }
    finally { setSubmittingCall(false); setSubmittingCsp(false); }
  };

  const thS = { padding: "5px 10px", fontSize: 11, fontWeight: 600, color: "#555", borderBottom: "2px solid #dee2e6", textAlign: "left" };
  const tdS = { padding: "5px 10px", fontSize: 12, borderBottom: "1px solid #f0f0f0" };
  const subHdr = (txt, clr) => (
    <div style={{ fontSize: 10, fontWeight: 700, color: clr, textTransform: "uppercase", letterSpacing: 0.7, marginBottom: 8 }}>{txt}</div>
  );

  // Use whichever action has the current PP (call takes precedence; fall back to csp)
  const currentPP       = meta.current_expiry_potential_profit ?? cspMeta.current_expiry_potential_profit ?? null;
  const callImprovement = meta.net_pp_improvement   ?? 0;
  const putImprovement  = cspMeta.put_premium_gain  ?? 0;
  const netPPImprovement = callImprovement + putImprovement;
  const newPP = currentPP != null ? currentPP + netPPImprovement : null;

  return (
    <div style={{ ...card, border: "2px solid #6f42c1", outline: checked ? "2px solid #007bff" : "none" }}>

      {/* ── HEADER ── */}
      <div style={cardTop}>
        {row.status === "PENDING" && (
          <input type="checkbox" checked={!!checked} onChange={onCheck} onClick={e => e.stopPropagation()}
            style={{ marginRight: 10, width: 15, height: 15, cursor: "pointer", flexShrink: 0, accentColor: "#007bff" }} />
        )}
        {row.status !== "PENDING" && <div style={{ width: 25, flexShrink: 0 }} />}
        <div style={{ display: "flex", alignItems: "center", flex: 1, gap: 12, cursor: "pointer" }} onClick={onToggle}>
          <div style={{ ...actionIcon, background: "#f0ebff", color: "#6f42c1", fontSize: 20 }}>↑☎</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: "#4b0082" }}>
              {callRow && cspRow ? "Roll Up Deep-ITM Options" : callRow ? "Roll Up Call (Same Month)" : "Roll Up CSP (Same Month)"} — {row.symbol}
            </div>
            <div style={{ fontSize: 12, color: "#555", marginTop: 3, display: "flex", flexWrap: "wrap", gap: "4px 10px" }}>
              {showAccount && row.username && (
                <span style={{ padding: "1px 7px", borderRadius: 8, background: "#e9ecef", fontSize: 11, fontWeight: 600 }}>
                  {row.username}
                </span>
              )}
              <span><span style={{ color: "#c0392b", fontWeight: 600 }}>Call:</span> {meta.current_call_symbol || "—"} → <span style={{ color: "#27ae60", fontWeight: 600 }}>{meta.new_call_symbol || "—"}</span></span>
              {cspRow && (
                <span><span style={{ color: "#c0392b", fontWeight: 600 }}>Put:</span> strike {fmtRs(cspMeta.current_put_strike)} → <span style={{ color: "#27ae60", fontWeight: 600 }}>{fmtRs(cspMeta.new_put_strike)}</span></span>
              )}
              {netPPImprovement > 0 && (
                <span style={{ color: "#28a745", fontWeight: 700 }}>+{fmtRs(netPPImprovement)} net PP gain</span>
              )}
            </div>
          </div>
          <div style={{ textAlign: "right", minWidth: 130 }}>
            <span style={{ ...statusBadge, background: sm.bg, color: sm.color }}>{sm.label}</span>
            <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>{toIST(row.created_at)}</div>
          </div>
          <div style={{ marginLeft: 12, color: "#ccc", fontSize: 18 }}>{expanded ? "▲" : "▼"}</div>
        </div>
        <NavButtons symbol={row.symbol} />
      </div>

      {/* ── BODY ── */}
      {expanded && (
        <div style={cardBody}>
          {row.status === "FAILED" && row.error_message && (
            <div style={{ background: "#fde2e2", border: "1px solid #dc3545", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#dc3545" }}>
              <strong>Failed:</strong> {row.error_message}
            </div>
          )}

          {/* ── CALL LEG ── */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#4b0082", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
              📞 Call Leg
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div style={{ background: "#fde2e2", border: "1px solid #f5c6cb", borderRadius: 8, padding: "12px 14px" }}>
                {subHdr("✕ Current (Buy Back)", "#721c24")}
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6, fontFamily: "monospace" }}>{meta.current_call_symbol || "—"}</div>
                <div style={{ fontSize: 12, display: "flex", flexWrap: "wrap", gap: "4px 16px" }}>
                  {meta.current_call_extrinsic != null && (
                    <span>Extrinsic: <strong style={{ color: "#dc3545" }}>{fmtRs(meta.current_call_extrinsic)}/sh</strong></span>
                  )}
                  <span>Unrealised: <strong style={{ color: "#dc3545" }}>{fmtPnl(meta.current_call_loss)}</strong></span>
                </div>
              </div>
              <div style={{ background: "#d4edda", border: "1px solid #b1dfbb", borderRadius: 8, padding: "12px 14px" }}>
                {subHdr("✓ New Call (Sell)", "#155724")}
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6, fontFamily: "monospace" }}>{meta.new_call_symbol || "—"}</div>
                <div style={{ fontSize: 12, display: "flex", flexWrap: "wrap", gap: "4px 16px" }}>
                  {meta.new_call_strike != null && <span>Strike: <strong>{fmtRs(meta.new_call_strike)}</strong></span>}
                  {meta.new_call_ltp    != null && <span>LTP: <strong style={{ color: "#28a745" }}>{fmtRs(meta.new_call_ltp)}</strong></span>}
                  {meta.new_call_delta  != null && <span>Δ {fmtDec(meta.new_call_delta, 3)}</span>}
                  {meta.new_call_lots   != null && <span>Lots: {meta.new_call_lots}</span>}
                </div>
                {meta.target_expiry && (
                  <div style={{ fontSize: 11, color: "#555", marginTop: 4 }}>Expiry: {meta.target_expiry}</div>
                )}
                {meta.net_pp_improvement != null && (
                  <div style={{ fontSize: 12, color: "#28a745", fontWeight: 700, marginTop: 6 }}>
                    Net PP gain: +{fmtRs(meta.net_pp_improvement)}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── PUT LEG ── */}
          {cspRow && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#0056b3", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
                📉 Put / CSP Leg
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div style={{ background: "#fff3cd", border: "1px solid #ffc107", borderRadius: 8, padding: "12px 14px" }}>
                  {subHdr("Current Put (Buy Back)", "#856404")}
                  <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6, fontFamily: "monospace" }}>{cspMeta.current_put_symbol || "—"}</div>
                  <div style={{ fontSize: 12, display: "flex", flexWrap: "wrap", gap: "4px 16px" }}>
                    {cspMeta.current_put_strike  != null && <span>Strike: <strong>{fmtRs(cspMeta.current_put_strike)}</strong></span>}
                    {cspMeta.current_put_ltp     != null && <span>LTP: {fmtRs(cspMeta.current_put_ltp)}</span>}
                    {cspMeta.current_put_dist    != null && <span>Dist: {fmtDec(cspMeta.current_put_dist)}%</span>}
                    {cspMeta.current_put_profit  != null && <span>Profit: <strong style={{ color: "#28a745" }}>{fmtPnl(cspMeta.current_put_profit)}</strong></span>}
                  </div>
                </div>
                <div style={{ background: "#cce5ff", border: "1px solid #b8daff", borderRadius: 8, padding: "12px 14px" }}>
                  {subHdr("✓ New Put (Sell)", "#004085")}
                  <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6, fontFamily: "monospace" }}>{cspMeta.new_put_symbol || "—"}</div>
                  <div style={{ fontSize: 12, display: "flex", flexWrap: "wrap", gap: "4px 16px" }}>
                    {cspMeta.new_put_strike != null && <span>Strike: <strong>{fmtRs(cspMeta.new_put_strike)}</strong></span>}
                    {cspMeta.new_put_ltp    != null && <span>LTP: <strong style={{ color: "#0056b3" }}>{fmtRs(cspMeta.new_put_ltp)}</strong></span>}
                    {cspMeta.new_put_dist   != null && <span>Dist: {fmtDec(cspMeta.new_put_dist)}%</span>}
                    {cspMeta.new_put_lots   != null && <span>Lots: {cspMeta.new_put_lots}</span>}
                  </div>
                  {cspMeta.put_premium_gain != null && (
                    <div style={{ fontSize: 12, color: "#0056b3", fontWeight: 700, marginTop: 6 }}>
                      Premium gain: +{fmtRs(cspMeta.put_premium_gain)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── POSITION IMPACT ── */}
          <div style={{ background: "#fff8e1", border: "1px solid #ffe082", borderRadius: 8, padding: "12px 16px", marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#856404", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 10 }}>
              Position Impact
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 32px", fontSize: 13 }}>
              {(meta.underlying_ltp ?? cspMeta.underlying_ltp) != null && (
                <span>Stock LTP: <strong style={{ color: "#0055aa" }}>{fmtRs(meta.underlying_ltp ?? cspMeta.underlying_ltp)}</strong></span>
              )}
              {meta.stock_profit != null && (
                <span>Stock P&amp;L: <strong style={{ color: pnlClr(meta.stock_profit) }}>{fmtPnl(meta.stock_profit)}</strong></span>
              )}
              {meta.net_loss_to_cover != null && (
                <span>Combined MTM: <strong style={{ color: "#dc3545" }}>{fmtPnl(meta.net_loss_to_cover)}</strong></span>
              )}
            </div>

            {/* Current vs New Expiry Potential Profit */}
            {currentPP != null && (
              <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <div style={{ background: "#fff", border: "1px solid #ffe082", borderRadius: 8, padding: "8px 14px", textAlign: "center", minWidth: 140 }}>
                  <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3 }}>Current Expiry PP</div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: pnlClr(currentPP) }}>{fmtPnl(currentPP)}</div>
                </div>
                <div style={{ fontSize: 20, color: "#aaa" }}>→</div>
                <div style={{ background: newPP != null && newPP > currentPP ? "#d4edda" : "#fff", border: `1px solid ${newPP != null && newPP > currentPP ? "#b1dfbb" : "#ffe082"}`, borderRadius: 8, padding: "8px 14px", textAlign: "center", minWidth: 140 }}>
                  <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3 }}>After Rollup (est.)</div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: pnlClr(newPP) }}>{newPP != null ? fmtPnl(newPP) : "—"}</div>
                </div>
                {netPPImprovement > 0 && (
                  <div style={{ fontSize: 13, color: "#28a745", fontWeight: 700 }}>
                    +{fmtRs(netPPImprovement)} improvement
                    {callRow && cspRow && callImprovement > 0 && putImprovement > 0 && (
                      <div style={{ fontSize: 11, color: "#555", fontWeight: 400, marginTop: 2 }}>
                        call +{fmtRs(callImprovement)} · put +{fmtRs(putImprovement)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── TAIL RISK ── */}
          {meta.new_tail_risk != null && (
            <div style={{ background: meta.new_tail_risk >= 0 ? "#d4edda" : "#fde2e2", border: `1px solid ${meta.new_tail_risk >= 0 ? "#b1dfbb" : "#f5c6cb"}`, borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>
              <strong>New Tail Risk P&amp;L</strong> (fully accumulated + called away at new strike):{" "}
              <span style={{ fontWeight: 700, color: meta.new_tail_risk >= 0 ? "#155724" : "#721c24", fontSize: 14 }}>
                {Number(meta.new_tail_risk) >= 0 ? "+" : "-"}₹{Math.abs(Math.round(meta.new_tail_risk)).toLocaleString("en-IN")}
              </span>
            </div>
          )}

          {/* ── NEW LADDER ── */}
          {Array.isArray(meta.new_ladder) && meta.new_ladder.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#555", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
                New Ladder (after rollup)
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#f5f7fa" }}>
                    {["Step", "Buy Price", "Qty", "Total Qty", "Avg After Buy"].map(h => (
                      <th key={h} style={thS}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {meta.new_ladder.map((s, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                      <td style={tdS}>{s.step ?? i + 1}</td>
                      <td style={{ ...tdS, fontWeight: 600 }}>{fmtRs(s.buy_price)}</td>
                      <td style={tdS}>{s.qty}</td>
                      <td style={tdS}>{s.total_qty}</td>
                      <td style={tdS}>{fmtRs(s.avg_price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ── APPROVE / REJECT BUTTONS ── */}
          {((callRow && callRow.status === "PENDING") || (cspRow && cspRow.status === "PENDING")) && (
            <div style={{ borderTop: "1px solid #eee", paddingTop: 14, marginTop: 4 }}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {/* Approve Both — only when both legs exist and are pending */}
                {callRow && callRow.status === "PENDING" && cspRow && cspRow.status === "PENDING" && (
                  <button onClick={approveBoth} disabled={submittingCall || submittingCsp}
                    style={{ flex: 2, padding: "10px 0", background: (submittingCall || submittingCsp) ? "#aaa" : "#6f42c1", color: "#fff", border: "none", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                    {(submittingCall || submittingCsp) ? "Executing…" : "↑ Approve Both Legs"}
                  </button>
                )}
                {/* Approve Call */}
                {callRow && callRow.status === "PENDING" && (
                  <button onClick={approveCall} disabled={submittingCall}
                    style={{ flex: 1, padding: "10px 0", background: submittingCall ? "#aaa" : "#e67e22", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, fontSize: 12, cursor: "pointer" }}>
                    {submittingCall ? "…" : cspRow ? "↑ Call Only" : "↑ Approve Roll Up"}
                  </button>
                )}
                {/* Approve Put */}
                {cspRow && cspRow.status === "PENDING" && (
                  <button onClick={approveCsp} disabled={submittingCsp}
                    style={{ flex: 1, padding: "10px 0", background: submittingCsp ? "#aaa" : "#0056b3", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, fontSize: 12, cursor: "pointer" }}>
                    {submittingCsp ? "…" : callRow ? "↑ Put Only" : "↑ Approve CSP Roll Up"}
                  </button>
                )}
                {/* Reject / Snooze */}
                {callRow && callRow.status === "PENDING" && onRejectCall && (
                  <button onClick={onRejectCall} disabled={submittingCall || submittingCsp}
                    style={{ padding: "10px 14px", background: "#fff", border: "1px solid #dc3545", color: "#dc3545", borderRadius: 8, fontWeight: 600, cursor: "pointer", fontSize: 12 }}>
                    {cspRow ? "Reject Call" : "Reject"}
                  </button>
                )}
                {cspRow && cspRow.status === "PENDING" && onRejectCsp && (
                  <button onClick={onRejectCsp} disabled={submittingCall || submittingCsp}
                    style={{ padding: "10px 14px", background: "#fff", border: "1px solid #6c757d", color: "#6c757d", borderRadius: 8, fontWeight: 600, cursor: "pointer", fontSize: 12 }}>
                    {callRow ? "Reject Put" : "Reject"}
                  </button>
                )}
                {/* Snooze — keyed to whichever primary row is pending */}
                {(callRow && callRow.status === "PENDING" ? onSnoozeCall : onSnoozeCsp) && (
                  <>
                    <button onClick={() => (callRow && callRow.status === "PENDING" ? onSnoozeCall : onSnoozeCsp)(60)}
                      disabled={submittingCall || submittingCsp} style={btnSnooze}>Snooze 1h</button>
                    <button onClick={() => (callRow && callRow.status === "PENDING" ? onSnoozeCall : onSnoozeCsp)(480)}
                      disabled={submittingCall || submittingCsp} style={btnSnooze}>Snooze 8h</button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RollOutCallCard({ row, cspCompanion, expanded, onToggle, onReject, onRejectCsp, onSnooze, onSnoozeCsp, onOptimisticApprove, onOptimisticApproveCsp, acting, checked, onCheck, username, showAccount, flash, fetchAll }) {
  const isActivelySnoozed = row.status === "PENDING"
    && row.snooze_until
    && new Date(row.snooze_until.replace(" ", "T") + "Z") > new Date();
  const displayStatus = isActivelySnoozed ? "SNOOZED" : row.status;
  const sm = STATUS_META[displayStatus] || STATUS_META.PENDING;

  const [submitting, setSubmitting] = useState(false);

  const meta      = row.metadata || {};
  const cur       = meta.current_call  || {};
  const prop      = meta.proposed_call || {};
  const _cspFallback = cspCompanion?.metadata || {};
  const curCsp    = meta.current_csp  || _cspFallback.current_csp  || null;
  const propCsp   = meta.proposed_csp || _cspFallback.proposed_csp || null;
  // ROLLOUT_ON_WEAK_HEDGE (docs/ROLLDOWN_WEAK_HEDGE_FULL_RESTORE_PLAN.md) stashes
  // an {existing_ladder, proposed_ladder} structural comparison (gap %, tail P&L)
  // instead of the flat proposed_ladder array the DTE-driven rollout below builds.
  const isIfpLadder = !!meta.existing_ladder;
  const existingIfp = meta.existing_ladder || {};
  const proposedIfp = meta.proposed_ladder || {};
  const ladder    = isIfpLadder ? (proposedIfp.ladder || []) : (meta.proposed_ladder || []);
  const ltp       = meta.underlying_ltp;
  const refAvg    = meta.reference_avg_price;
  const avgBuy    = meta.avg_buy_price;
  const existingQty = meta.existing_qty;

  const fmtRs = v => v != null ? `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}` : "—";
  const fmtN  = (v, d = 3) => v != null ? Number(v).toFixed(d) : "—";
  const fmtTheta = (dailyRs, dte) => {
    if (dailyRs == null) return null;
    const total = dte ? Math.round(dailyRs * dte) : null;
    return `₹${Number(dailyRs).toLocaleString("en-IN")}/day${total != null ? ` · ₹${total.toLocaleString("en-IN")} over ${dte}d` : ""}`;
  };
  const parseDte = (expStr) => {
    if (!expStr) return null;
    const [m, d, y] = expStr.split("/");
    const exp = new Date(+y, +m - 1, +d);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return Math.max(1, Math.round((exp - today) / 86400000));
  };
  const curDte  = parseDte(meta.current_expiry);
  const nextDte = parseDte(meta.next_expiry);

  // Pre-compute constants for Net Profit column in ladder
  const _callQty    = (prop.lots || 1) * (meta.lot_size || 1);
  const _callLtp    = prop.ltp    || 0;
  const _callStrike = prop.strike || 0;
  const _putLtp     = propCsp?.ltp    || 0;
  const _putStrike  = propCsp?.strike || 0;
  const _putQty     = propCsp ? (meta.lot_size || 1) : 0;

  // Highest spread among proposed contracts — used for overall liquidity warning
  const maxSpread = Math.max(
    prop.spread_pct ?? 0,
    propCsp?.spread_pct ?? 0,
  );

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      const reqs = [axios.post("/automation/suggestions/approve", { id: row.id, username })];
      if (cspCompanion) reqs.push(axios.post("/automation/suggestions/approve", { id: cspCompanion.id, username }));
      await Promise.all(reqs);
      onOptimisticApprove?.();
      onOptimisticApproveCsp?.();
      flash(cspCompanion ? "Roll-out approved (call + CSP) — orders queued" : "Roll-out approved — orders queued");
      fetchAll();
    } catch {
      flash("Approval failed.", false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveCallOnly = async () => {
    setSubmitting(true);
    try {
      const reqs = [axios.post("/automation/suggestions/approve", { id: row.id, username })];
      // Reject the companion CSP — user chose call-only, don't leave it orphaned
      if (cspCompanion) reqs.push(axios.post("/automation/suggestions/reject", { id: cspCompanion.id, username }));
      await Promise.all(reqs);
      onOptimisticApprove?.();
      flash("Call roll-out approved — CSP companion rejected");
      fetchAll();
    } catch {
      flash("Approval failed.", false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveCspOnly = async () => {
    if (!cspCompanion) return;
    setSubmitting(true);
    try {
      await axios.post("/automation/suggestions/approve", { id: cspCompanion.id, username });
      onOptimisticApproveCsp?.();
      flash("CSP roll-out approved — orders queued");
      fetchAll();
    } catch {
      flash("Approval failed.", false);
    } finally {
      setSubmitting(false);
    }
  };

  const thS = { padding: "6px 10px", textAlign: "left", fontWeight: 600, fontSize: 11, borderBottom: "2px solid #dee2e6", color: "#555" };
  const tdS = { padding: "5px 10px", fontSize: 12, borderBottom: "1px solid #f0f0f0" };

  return (
    <div style={{ ...card, border: "2px solid #6f42c1", outline: checked ? "2px solid #007bff" : "none" }}>
      {/* ── Header ── */}
      <div style={{ ...cardTop }}>
        {row.status === "PENDING" && (
          <input type="checkbox" checked={!!checked} onChange={onCheck} onClick={e => e.stopPropagation()}
            style={{ marginRight: 10, width: 15, height: 15, cursor: "pointer", flexShrink: 0, accentColor: "#007bff" }} />
        )}
        {row.status !== "PENDING" && <div style={{ width: 25, flexShrink: 0 }} />}
        <div style={{ display: "flex", alignItems: "center", flex: 1, gap: 12, cursor: "pointer" }} onClick={onToggle}>
          <div style={{ ...actionIcon, background: "#e8d5ff", color: "#6f42c1", fontSize: 22 }}>⇢</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: "#4a2c7a" }}>
              Roll Out to Next Month — {row.symbol}
              {meta.rule_name && (
                <span style={{ marginLeft: 10, fontSize: 11, fontWeight: 500, color: "#888", background: "#f5f0ff", border: "1px solid #ddd0f5", borderRadius: 4, padding: "1px 7px", verticalAlign: "middle" }}>
                  {meta.rule_name.replace(/_/g, " ").replace(/\bPRIMARY\b/gi, "CALL")}
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: "#555", marginTop: 2 }}>
              {showAccount && row.username && (
                <span style={{ marginRight: 8, padding: "1px 7px", borderRadius: 8, background: "#e9ecef", color: "#495057", fontSize: 11, fontWeight: 600 }}>
                  {row.username}
                </span>
              )}
              {meta.current_expiry && meta.next_expiry && (
                <span>
                  <strong>{meta.current_expiry}</strong>
                  <span style={{ margin: "0 6px", color: "#6f42c1", fontWeight: 700 }}>→</span>
                  <strong>{meta.next_expiry}</strong>
                </span>
              )}
              {cur.strike != null && (
                <span style={{ marginLeft: 10, color: "#6c757d" }}>
                  Close CE ₹{cur.strike}
                  {cur.decay_pct != null && (
                    <span style={{ marginLeft: 5, color: "#28a745", fontWeight: 600 }}>
                      ({cur.decay_pct.toFixed(0)}% decayed)
                    </span>
                  )}
                </span>
              )}
              {prop.strike != null && (
                <span style={{ marginLeft: 6, color: "#0055aa", fontWeight: 600 }}>
                  → Sell CE ₹{prop.strike}
                </span>
              )}
            </div>
          </div>
          <div style={{ textAlign: "right", minWidth: 140 }}>
            <span style={{ ...statusBadge, background: sm.bg, color: sm.color }}>{sm.label}</span>
            <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>{toIST(row.created_at)}</div>
          </div>
          <div style={{ marginLeft: 12, color: "#ccc", fontSize: 18 }}>{expanded ? "▲" : "▼"}</div>
        </div>
        <NavButtons symbol={row.symbol} />
      </div>

      {/* ── Expanded body ── */}
      {expanded && (
        <div style={cardBody}>
          <DownsideFloorPanel meta={row.metadata} />

          {row.status === "FAILED" && row.error_message && (
            <div style={{ background: "#fde2e2", border: "1px solid #dc3545", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#dc3545" }}>
              <strong>Failed:</strong> {row.error_message}
            </div>
          )}

          {meta.precompute_error && (
            <div style={{ background: "#fde2e2", border: "1px solid #dc3545", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#dc3545" }}>
              Could not compute next-month structure: {meta.precompute_error}
            </div>
          )}

          {/* Partial rollout: next-month tokens not yet seeded */}
          {meta.partial_rollout && (
            <div style={{ background: "#fff3cd", border: "1px solid #ffc107", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#856404" }}>
              ⏳ <strong>Partial rollout mode</strong> — Next-month ({meta.next_expiry}) CE tokens are not yet fully seeded by the broker.
              Approving will <strong>close the current call only</strong>.
              The new call will be queued automatically by the system (reconcile) once June option tokens are available.
              {meta.structure_note && <span style={{ display: "block", marginTop: 4, color: "#6c757d", fontSize: 11 }}>Technical: {meta.structure_note}</span>}
            </div>
          )}

          {/* Opening delta fallback warning */}
          {meta.opening_delta_warning && (
            <div style={{ background: "#fff3cd", border: "1px solid #ffc107", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#856404" }}>
              ⚠️ {meta.opening_delta_warning}
            </div>
          )}

          {/* Wide spread warning */}
          {maxSpread > 10 && !meta.precompute_error && (
            <div style={{ background: "#fff3cd", border: "1px solid #ffc107", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#856404" }}>
              ⚠️ Wide bid-ask spread detected on next-month contract ({maxSpread.toFixed(1)}%) — execution may involve meaningful slippage. Consider rejecting if the spread is unacceptable.
            </div>
          )}

          {/* Market context */}
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap", fontSize: 12, color: "#555", marginBottom: 14, paddingTop: 6 }}>
            {ltp    != null && <span><strong>Underlying LTP:</strong> {fmtRs(ltp)}</span>}
            {avgBuy != null && (
              <span>
                <strong>Avg Buy Price:</strong> {fmtRs(avgBuy)}
                {refAvg != null && avgBuy !== refAvg && (
                  <span style={{ marginLeft: 6, fontSize: 11, color: "#856404", background: "#fff3cd", padding: "1px 6px", borderRadius: 8 }}>
                    ≠ ref
                  </span>
                )}
              </span>
            )}
            {refAvg != null && <span><strong>Reference Avg:</strong> {fmtRs(refAvg)}</span>}
            {existingQty != null && existingQty > 0 && (
              <span>
                <strong>Stock Qty:</strong>{" "}
                {meta.lot_size ? `${(existingQty / meta.lot_size).toFixed(2)} lots (${existingQty} shares)` : `${existingQty} shares`}
              </span>
            )}
            {meta.lot_size  != null && <span><strong>Lot Size:</strong> {meta.lot_size}</span>}
            {meta.csm_mode  === 1 && (
              <span style={{ padding: "1px 8px", borderRadius: 10, background: "#cce5ff", color: "#004085", fontSize: 11, fontWeight: 700 }}>CSM ON</span>
            )}
          </div>

          {/* ── Affected positions ── */}
          {meta.affected_positions?.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#495057", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
                Affected Option Positions
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: "#f8f9fa" }}>
                      {["Type","Strike","Expiry","Lots","LTP","Sell Avg","Unrealised P&L","Realised","Delta","IV","Theta"].map(h => (
                        <th key={h} style={{ padding: "5px 10px", textAlign: h === "Type" ? "left" : "right", fontWeight: 600, fontSize: 11, color: "#555", borderBottom: "2px solid #dee2e6", whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {meta.affected_positions.map((p, i) => (
                      <tr key={i} style={{ borderBottom: "1px solid #f0f0f0", background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                        <td style={{ padding: "5px 10px" }}>
                          <span style={{ display: "inline-block", padding: "1px 7px", borderRadius: 8, fontSize: 11, fontWeight: 700, background: p.type === "CE" ? "#d4edda" : "#fde2e2", color: p.type === "CE" ? "#155724" : "#721c24" }}>{p.type}</span>
                        </td>
                        <td style={{ padding: "5px 10px", textAlign: "right" }}>{fmtRs(p.strike)}</td>
                        <td style={{ padding: "5px 10px", textAlign: "right", color: "#666" }}>{p.expiry || "—"}</td>
                        <td style={{ padding: "5px 10px", textAlign: "right", fontWeight: 600, color: p.netqty < 0 ? "#dc3545" : "#28a745" }}>{p.lots} <span style={{ fontSize: 10, color: "#999" }}>({p.netqty > 0 ? "+" : ""}{p.netqty})</span></td>
                        <td style={{ padding: "5px 10px", textAlign: "right" }}>{fmtRs(p.ltp)}</td>
                        <td style={{ padding: "5px 10px", textAlign: "right", color: "#666" }}>{p.sell_avg != null ? fmtRs(p.sell_avg) : "—"}</td>
                        <td style={{ padding: "5px 10px", textAlign: "right", fontWeight: 600, color: p.unrealised >= 0 ? "#28a745" : "#dc3545" }}>
                          {p.unrealised >= 0 ? "+" : "-"}₹{Math.abs(p.unrealised).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                        </td>
                        <td style={{ padding: "5px 10px", textAlign: "right", color: p.realised >= 0 ? "#28a745" : "#dc3545" }}>
                          {p.realised !== 0 ? `${p.realised >= 0 ? "+" : ""}₹${Math.abs(p.realised).toLocaleString("en-IN", { maximumFractionDigits: 0 })}` : "—"}
                        </td>
                        <td style={{ padding: "5px 10px", textAlign: "right", color: p.delta < 0 ? "#dc3545" : "#333" }}>{p.delta != null ? fmtN(p.delta) : "—"}</td>
                        <td style={{ padding: "5px 10px", textAlign: "right" }}>{p.iv != null ? `${fmtN(p.iv, 1)}%` : "—"}</td>
                        <td style={{ padding: "5px 10px", textAlign: "right", color: "#888" }}>{p.theta != null ? fmtN(p.theta) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Call comparison ── */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
            {/* Current call — close */}
            <div style={{ background: "#fde2e2", border: "1px solid #f5c6cb", borderRadius: 8, padding: "12px 14px" }}>
              <div style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, color: "#842029", marginBottom: 8, letterSpacing: 0.6 }}>
                Close Call (BUY) · {meta.current_expiry}
              </div>
              {cur.symbol || cur.strike ? (
                <>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#222", marginBottom: 6 }}>{cur.symbol || "—"}</div>
                  <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12, color: "#555" }}>
                    <span><strong>Strike:</strong> {fmtRs(cur.strike)}</span>
                    <span><strong>LTP:</strong>    {fmtRs(cur.ltp)}</span>
                    <span><strong>Lots:</strong>   {cur.lots ?? "—"}</span>
                    {cur.delta != null && <span><strong>Option Δ:</strong> {fmtN(cur.delta)}</span>}
                    {cur.hedge_pct != null && (
                      <span style={{ fontWeight: 600, color: "#6c757d" }}>
                        <strong>Net Hedge:</strong> {Number(cur.hedge_pct).toFixed(1)}%
                      </span>
                    )}
                    {cur.decay_pct != null && (
                      <span style={{ fontWeight: 700, color: "#28a745" }}>
                        <strong>Decayed:</strong> {cur.decay_pct.toFixed(0)}%
                      </span>
                    )}
                  </div>
                  {(cur.theta_daily_rs ?? meta.current_daily_decay) != null && (
                    <div style={{ marginTop: 6, fontSize: 12, color: "#28a745", fontWeight: 600 }}>
                      ⏱ Daily Decay: {fmtTheta(cur.theta_daily_rs ?? meta.current_daily_decay, curDte)}
                    </div>
                  )}
                </>
              ) : (
                <div style={{ fontSize: 12, color: "#856404", background: "#fff3cd", borderRadius: 6, padding: "8px 10px" }}>
                  No active call found for this expiry — approving will only open the new {meta.next_expiry} call without closing anything.
                </div>
              )}
            </div>

            {/* Proposed next-month call */}
            <div style={{ background: "#d4edda", border: "1px solid #b1dfbb", borderRadius: 8, padding: "12px 14px" }}>
              <div style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, color: "#0a5c25", marginBottom: 8, letterSpacing: 0.6 }}>
                New Call Hedge (SELL) · {meta.next_expiry}
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#222", marginBottom: 6 }}>{prop.symbol || "—"}</div>
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12, color: "#555" }}>
                <span><strong>Strike:</strong>  {fmtRs(prop.strike)}</span>
                <span><strong>LTP:</strong>     {fmtRs(prop.ltp)}</span>
                <span><strong>Lots:</strong>    {prop.lots ?? "—"}</span>
                {prop.delta   != null && <span><strong>Option Δ:</strong>   {fmtN(prop.delta)}</span>}
                {prop.hedge_pct != null && (
                  <span style={{ fontWeight: 600, color: "#0055aa" }}>
                    <strong>Net Hedge:</strong> {Number(prop.hedge_pct).toFixed(1)}%
                  </span>
                )}
              </div>
              {(prop.theta_daily_rs ?? meta.next_daily_decay) != null && (
                <div style={{ marginTop: 6, fontSize: 12, color: "#0055aa", fontWeight: 600 }}>
                  ⏱ Daily Decay: {fmtTheta(prop.theta_daily_rs ?? meta.next_daily_decay, nextDte)}
                </div>
              )}
              <LiquidityRow bid={prop.bid} ask={prop.ask} spread_pct={prop.spread_pct} oi={prop.oi} />
            </div>
          </div>

          {/* ── CSP section (if active) ── */}
          {(curCsp || propCsp || cspCompanion) && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
              {/* Current CSP */}
              <div style={{ background: "#fde2e2", border: "1px solid #f5c6cb", borderRadius: 8, padding: "12px 14px" }}>
                <div style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, color: "#842029", marginBottom: 8, letterSpacing: 0.6 }}>
                  Close CSP (BUY) · {meta.current_expiry}
                </div>
                {curCsp ? (
                  <>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#222", marginBottom: 6 }}>{curCsp.symbol || "—"}</div>
                    <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12, color: "#555" }}>
                      <span><strong>Strike:</strong> {fmtRs(curCsp.strike)}</span>
                      <span><strong>LTP:</strong>    {fmtRs(curCsp.ltp)}</span>
                      <span><strong>Lots:</strong>   {curCsp.lots ?? "—"}</span>
                      {curCsp.unrealised != null && (
                        <span style={{ fontWeight: 700, color: curCsp.unrealised >= 0 ? "#28a745" : "#dc3545" }}>
                          <strong>Unrealised:</strong> {curCsp.unrealised >= 0 ? "+" : "-"}₹{Math.abs(curCsp.unrealised).toLocaleString("en-IN")}
                        </span>
                      )}
                      {curCsp.decay_pct != null && (
                        <span style={{ fontWeight: 700, color: "#28a745" }}>
                          <strong>Decayed:</strong> {curCsp.decay_pct.toFixed(0)}%
                        </span>
                      )}
                    </div>
                    {curCsp.theta_daily_rs != null && (
                      <div style={{ marginTop: 6, fontSize: 12, color: "#28a745", fontWeight: 600 }}>
                        ⏱ Daily Decay: {fmtTheta(curCsp.theta_daily_rs, curDte)}
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ fontSize: 12, color: "#aaa" }}>No active CSP this expiry</div>
                )}
              </div>

              {/* Proposed next-month CSP */}
              <div style={{ background: "#d4edda", border: "1px solid #b1dfbb", borderRadius: 8, padding: "12px 14px" }}>
                <div style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, color: "#0a5c25", marginBottom: 8, letterSpacing: 0.6 }}>
                  New CSP (SELL) · {meta.next_expiry}
                </div>
                {propCsp ? (
                  <>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#222", marginBottom: 6 }}>{propCsp.symbol || "—"}</div>
                    <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12, color: "#555" }}>
                      <span><strong>Strike:</strong> {fmtRs(propCsp.strike)}</span>
                      <span><strong>LTP:</strong>    {fmtRs(propCsp.ltp)}</span>
                      {propCsp.delta != null && <span><strong>Delta:</strong> {fmtN(propCsp.delta, 3)}</span>}
                      {(() => {
                        const distPct = propCsp.put_distance_pct ?? (
                          ltp != null && propCsp.strike != null
                            ? Math.abs(ltp - propCsp.strike) / ltp * 100
                            : null
                        );
                        const minDist = meta.min_put_distance_pct || _cspFallback.min_put_distance_pct;
                        const distColor = distPct != null && minDist != null && distPct < minDist ? "#dc3545" : "#28a745";
                        return distPct != null ? (
                          <span style={{ fontWeight: 600, color: distColor }}>
                            <strong>Distance:</strong> {Number(distPct).toFixed(1)}%
                            {minDist != null && (
                              <span style={{ marginLeft: 4, fontSize: 11 }}>
                                ({distPct >= minDist ? "✓" : "⚠ below min"})
                              </span>
                            )}
                          </span>
                        ) : null;
                      })()}
                    </div>
                    {propCsp.theta_daily_rs != null && (
                      <div style={{ marginTop: 6, fontSize: 12, color: "#0055aa", fontWeight: 600 }}>
                        ⏱ Daily Decay: {fmtTheta(propCsp.theta_daily_rs, nextDte)}
                      </div>
                    )}
                    <LiquidityRow bid={propCsp.bid} ask={propCsp.ask} spread_pct={propCsp.spread_pct} oi={propCsp.oi} />
                  </>
                ) : (
                  <div style={{ fontSize: 12, color: "#aaa" }}>
                    {meta.csp_warning
                      ? "Next-month put not yet available"
                      : "CSP not enabled"}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── IFP weak-hedge rollout: existing vs proposed structural comparison ── */}
          {isIfpLadder && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
              <div style={{ background: "#f8f9fa", border: "1px solid #dee2e6", borderRadius: 8, padding: "12px 14px" }}>
                <div style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, color: "#555", marginBottom: 8, letterSpacing: 0.6 }}>
                  Existing
                </div>
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12, color: "#555" }}>
                  <span><strong>Lots:</strong> {existingIfp.call_lots ?? "—"}</span>
                  <span><strong>Step Gap:</strong> {existingIfp.gap_pct != null ? `${Number(existingIfp.gap_pct).toFixed(2)}%` : "—"}</span>
                  <span><strong>Avg Price:</strong> {fmtRs(existingIfp.reference_avg_price)}</span>
                  {existingIfp.tail_pnl != null && (
                    <span style={{ fontWeight: 700, color: existingIfp.tail_pnl >= 0 ? "#28a745" : "#dc3545" }}>
                      <strong>Tail P&amp;L:</strong> {existingIfp.tail_pnl >= 0 ? "+" : "-"}₹{Math.abs(Math.round(existingIfp.tail_pnl)).toLocaleString("en-IN")}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ background: "#d4edda", border: "1px solid #b1dfbb", borderRadius: 8, padding: "12px 14px" }}>
                <div style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, color: "#0a5c25", marginBottom: 8, letterSpacing: 0.6 }}>
                  Proposed (next expiry)
                </div>
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12, color: "#555" }}>
                  <span><strong>Lots:</strong> {proposedIfp.call_lots ?? "—"}</span>
                  <span><strong>Step Gap:</strong> {proposedIfp.gap_pct != null ? `${Number(proposedIfp.gap_pct).toFixed(2)}%` : "—"}</span>
                  <span><strong>Avg Price:</strong> {fmtRs(proposedIfp.reference_avg_price)}</span>
                  {proposedIfp.tail_pnl != null && (
                    <span style={{ fontWeight: 700, color: proposedIfp.tail_pnl >= 0 ? "#28a745" : "#dc3545" }}>
                      <strong>Tail P&amp;L:</strong> {proposedIfp.tail_pnl >= 0 ? "+" : "-"}₹{Math.abs(Math.round(proposedIfp.tail_pnl)).toLocaleString("en-IN")}
                    </span>
                  )}
                </div>
              </div>
              {!proposedIfp.tail_pnl && (
                <div style={{ gridColumn: "1 / -1", background: "#fff3cd", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#856404" }}>
                  Structural detail (step gap / tail P&amp;L) couldn't be computed for the proposed strike — the rollout itself is still valid, just shown without this extra context.
                </div>
              )}
              {proposedIfp.stock_excluded && (
                <div style={{ gridColumn: "1 / -1", background: "#fde2e2", border: "1px solid #f5c6cb", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#842029" }}>
                  ⚠️ <strong>Stock excluded from this calculation.</strong> {proposedIfp.stock_excluded_note || "The existing stock position's loss made a blended calculation infeasible, so this ladder was built treating the position as pure futures+options."}
                </div>
              )}
            </div>
          )}

          {/* ── IFP weak-hedge rollout: per-step Existing vs Proposed ladder detail ── */}
          {isIfpLadder && (existingIfp.ladder?.length > 0 || proposedIfp.ladder?.length > 0) && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
              {[
                { label: "Existing Ladder", data: existingIfp, tint: "#555" },
                { label: "Proposed Ladder", data: proposedIfp, tint: "#0a5c25" },
              ].map(({ label, data, tint }) => (
                <div key={label}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: tint, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
                    {label}
                  </div>
                  {data.ladder?.length > 0 ? (
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11.5 }}>
                      <thead>
                        <tr style={{ background: "#f5f7fa" }}>
                          <th style={thS}>Step</th>
                          <th style={thS}>Buy Price</th>
                          <th style={thS}>Qty</th>
                          <th style={thS}>Total Qty</th>
                          <th style={thS}>Avg After Step</th>
                          <th style={thS}>Hedge %</th>
                          <th style={thS}>Net P&amp;L</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.ladder.map((s, i) => (
                          <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                            <td style={tdS}>{s.step ?? i + 1}</td>
                            <td style={tdS}>{fmtRs(s.buy_price)}</td>
                            <td style={tdS}>{s.qty}</td>
                            <td style={tdS}>{s.total_qty}</td>
                            <td style={{ ...tdS, fontWeight: 600 }}>{fmtRs(s.avg_price)}</td>
                            <td style={tdS}>{s.projected_hedge_pct != null ? `${Number(s.projected_hedge_pct).toFixed(1)}%` : "—"}</td>
                            <td style={{ ...tdS, fontWeight: 700, color: (s.net_pnl ?? 0) >= 0 ? "#28a745" : "#dc3545" }}>
                              {s.net_pnl != null ? `${s.net_pnl >= 0 ? "+" : "-"}₹${Math.abs(Math.round(s.net_pnl)).toLocaleString("en-IN")}` : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div style={{ fontSize: 12, color: "#999" }}>No ladder detail available.</div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ── New accumulation ladder ── */}
          {!isIfpLadder && ladder.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#555", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
                Accumulation Ladder (Next Month){avgBuy && avgBuy !== refAvg ? " — using actual avg buy price" : ""}
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 14 }}>
                <thead>
                  <tr style={{ background: "#f5f7fa" }}>
                    <th style={thS}>Step</th>
                    <th style={thS}>Buy Price</th>
                    <th style={thS}>% from LTP</th>
                    <th style={thS}>Qty</th>
                    <th style={thS}>Investment</th>
                    <th style={thS}>Total Qty</th>
                    <th style={thS}>Avg After Step</th>
                    <th style={{ ...thS, color: "#17a2b8" }}>P&amp;L at Buy Price</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // When actual avg buy price is available, recompute step-by-step avg
                    // using real cost basis instead of reference avg for accurate Net Profit.
                    const useActualAvg = avgBuy != null && existingQty != null && avgBuy > 0;
                    let corrQty = existingQty || 0;
                    let corrAvg = avgBuy || 0;
                    return ladder.map((s, i) => {
                      const pct = ltp ? ((s.buy_price - ltp) / ltp * 100) : null;
                      let displayAvg = s.avg_price;
                      if (useActualAvg) {
                        const newQty = corrQty + s.qty;
                        corrAvg = (corrQty * corrAvg + s.qty * s.buy_price) / newQty;
                        corrQty = newQty;
                        displayAvg = corrAvg;
                      }
                      // Mark-to-market at this step's OWN buy price — see the matching
                      // comment on the SELL_INITIAL_CALL card.
                      const stockPnl = (s.buy_price - displayAvg) * s.total_qty;
                      const callIntrinsic = _callStrike > 0 ? Math.max(0, s.buy_price - _callStrike) * _callQty : 0;
                      const callPnl  = _callLtp * _callQty - callIntrinsic;
                      const putIntrinsic = _putQty > 0 ? Math.max(0, _putStrike - s.buy_price) * _putQty : 0;
                      const putPnl   = _putQty > 0 ? (_putLtp * _putQty - putIntrinsic) : 0;
                      const netPnl   = stockPnl + callPnl + putPnl;
                      const pnlClr   = netPnl >= 0 ? "#28a745" : "#dc3545";
                      const fmtPnl   = v => `${v >= 0 ? "+" : "-"}₹${Math.abs(Math.round(v)).toLocaleString("en-IN")}`;
                      return (
                        <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                          <td style={tdS}>{s.step ?? i + 1}</td>
                          <td style={{ ...tdS, fontWeight: 600 }}>{fmtRs(s.buy_price)}</td>
                          <td style={{ ...tdS, color: pct == null ? "#999" : pct >= 0 ? "#28a745" : "#dc3545" }}>
                            {pct != null ? `${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%` : "—"}
                          </td>
                          <td style={tdS}>{s.qty}</td>
                          <td style={{ ...tdS, color: "#555" }}>₹{Math.round(s.buy_price * s.qty).toLocaleString("en-IN")}</td>
                          <td style={tdS}>{s.total_qty}</td>
                          <td style={tdS}>{fmtRs(displayAvg)}</td>
                          <td style={{ ...tdS, fontWeight: 700, color: pnlClr }}>{fmtPnl(netPnl)}</td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </>
          )}

          <div style={{ fontSize: 11, color: "#aaa", marginBottom: 12 }}>
            Structure computed at queue time. Bid/ask quotes are live at time of suggestion generation.
            {meta.csm_mode === 1 && " CSM: booked profit resets to zero for the new expiry."}
          </div>

          {/* Action buttons */}
          {row.status === "PENDING" && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                onClick={handleApprove}
                disabled={submitting || !!meta.precompute_error}
                style={{ ...btnApprove, background: "#6f42c1", opacity: (submitting || !!meta.precompute_error) ? 0.5 : 1 }}
              >
                {submitting ? "Executing…" : cspCompanion ? "⇢ Approve Roll Out (Call + CSP)" : "⇢ Approve Roll Out"}
              </button>
              {cspCompanion && (
                <>
                  <button
                    onClick={handleApproveCallOnly}
                    disabled={submitting || !!meta.precompute_error}
                    style={{ ...btnApprove, background: "#5a6268", opacity: (submitting || !!meta.precompute_error) ? 0.5 : 1 }}
                  >
                    Call Only
                  </button>
                  <button
                    onClick={handleApproveCspOnly}
                    disabled={submitting}
                    style={{ ...btnApprove, background: "#20c997", opacity: submitting ? 0.5 : 1 }}
                  >
                    CSP Only
                  </button>
                </>
              )}
              <button onClick={() => { onReject(); onRejectCsp?.(); }} disabled={submitting} style={btnReject}>✕ Reject</button>
              <button onClick={() => { onSnooze(60);  onSnoozeCsp?.(60);  }} disabled={submitting} style={btnSnooze}>Snooze 1h</button>
              <button onClick={() => { onSnooze(480); onSnoozeCsp?.(480); }} disabled={submitting} style={btnSnooze}>Snooze 8h</button>
            </div>
          )}

          {row.status !== "PENDING" && (
            <div style={{ fontSize: 13, color: "#555", marginTop: 4 }}>
              {row.status === "COMPLETED" ? "Roll-out executed successfully."
                : row.status === "REJECTED" ? "Rejected."
                : row.status}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── RollOutCspCard ──────────────────────────────────────────── */

function RollOutCspCard({ row, expanded, onToggle, onReject, onSnooze, onOptimisticApprove, acting, checked, onCheck, username, showAccount, flash, fetchAll }) {
  const isActivelySnoozed = row.status === "PENDING"
    && row.snooze_until
    && new Date(row.snooze_until.replace(" ", "T") + "Z") > new Date();
  const displayStatus = isActivelySnoozed ? "SNOOZED" : row.status;
  const sm = STATUS_META[displayStatus] || STATUS_META.PENDING;

  const [submitting, setSubmitting] = useState(false);

  const meta     = row.metadata || {};
  const curCsp   = meta.current_csp   || {};
  const propCsp  = meta.proposed_csp  || {};
  const ltp      = meta.underlying_ltp;

  const fmtRs = v => v != null ? `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}` : "—";
  const fmtN  = (v, d = 2) => v != null ? Number(v).toFixed(d) : "—";
  const fmtTheta = (dailyRs, dte) => {
    if (dailyRs == null) return null;
    const total = dte ? Math.round(dailyRs * dte) : null;
    return `₹${Number(dailyRs).toLocaleString("en-IN")}/day${total != null ? ` · ₹${total.toLocaleString("en-IN")} over ${dte}d` : ""}`;
  };
  const parseDte = (expStr) => {
    if (!expStr) return null;
    const [m, d, y] = expStr.split("/");
    const exp = new Date(+y, +m - 1, +d);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return Math.max(1, Math.round((exp - today) / 86400000));
  };
  const curDte  = parseDte(meta.current_expiry);
  const nextDte = parseDte(meta.next_expiry);
  const distPct = propCsp.put_distance_pct;
  const putDistColor = distPct != null && distPct < (meta.min_put_distance_pct || 5) ? "#dc3545" : "#28a745";

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      await axios.post("/automation/suggestions/approve", { id: row.id, username });
      onOptimisticApprove?.();
      flash("CSP roll-out approved — orders queued");
      fetchAll();
    } catch {
      flash("Approval failed.", false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ ...card, border: "2px solid #20c997", outline: checked ? "2px solid #007bff" : "none" }}>
      {/* ── Header ── */}
      <div style={{ ...cardTop }}>
        {row.status === "PENDING" && (
          <input type="checkbox" checked={!!checked} onChange={onCheck} onClick={e => e.stopPropagation()}
            style={{ marginRight: 10, width: 15, height: 15, cursor: "pointer", flexShrink: 0, accentColor: "#007bff" }} />
        )}
        {row.status !== "PENDING" && <div style={{ width: 25, flexShrink: 0 }} />}
        <div style={{ display: "flex", alignItems: "center", flex: 1, gap: 12, cursor: "pointer" }} onClick={onToggle}>
          <div style={{ ...actionIcon, background: "#d1f5ee", color: "#0d6e54", fontSize: 22 }}>⇢</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: "#0d6e54" }}>
              Roll Out CSP to Next Month — {row.symbol}
              {meta.rule_name && (
                <span style={{ marginLeft: 10, fontSize: 11, fontWeight: 500, color: "#888", background: "#e6faf5", border: "1px solid #b8eed9", borderRadius: 4, padding: "1px 7px", verticalAlign: "middle" }}>
                  {meta.rule_name.replace(/_/g, " ")}
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: "#555", marginTop: 2 }}>
              {showAccount && row.username && (
                <span style={{ marginRight: 8, padding: "1px 7px", borderRadius: 8, background: "#e9ecef", color: "#495057", fontSize: 11, fontWeight: 600 }}>
                  {row.username}
                </span>
              )}
              {meta.current_expiry && meta.next_expiry && (
                <span>
                  <strong>{meta.current_expiry}</strong>
                  <span style={{ margin: "0 6px", color: "#20c997", fontWeight: 700 }}>→</span>
                  <strong>{meta.next_expiry}</strong>
                </span>
              )}
              {curCsp.strike != null && (
                <span style={{ marginLeft: 10, color: "#6c757d" }}>
                  Close PE ₹{curCsp.strike}
                  {curCsp.decay_pct != null && (
                    <span style={{ marginLeft: 5, color: "#28a745", fontWeight: 600 }}>
                      ({curCsp.decay_pct.toFixed(0)}% decayed)
                    </span>
                  )}
                </span>
              )}
              {propCsp.strike != null && (
                <span style={{ marginLeft: 6, color: "#0d6e54", fontWeight: 600 }}>
                  → Sell PE ₹{propCsp.strike}
                </span>
              )}
            </div>
          </div>
          <div style={{ textAlign: "right", minWidth: 140 }}>
            <span style={{ ...statusBadge, background: sm.bg, color: sm.color }}>{sm.label}</span>
            <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>{toIST(row.created_at)}</div>
          </div>
          <div style={{ marginLeft: 12, color: "#ccc", fontSize: 18 }}>{expanded ? "▲" : "▼"}</div>
        </div>
        <NavButtons symbol={row.symbol} />
      </div>

      {expanded && (
        <div style={cardBody}>
          {row.status === "FAILED" && row.error_message && (
            <div style={{ background: "#fde2e2", border: "1px solid #dc3545", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#dc3545" }}>
              <strong>Failed:</strong> {row.error_message}
            </div>
          )}
          {meta.precompute_error && (
            <div style={{ background: "#fde2e2", border: "1px solid #dc3545", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#dc3545" }}>
              Could not compute structure: {meta.precompute_error}
            </div>
          )}

          {/* Market context */}
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap", fontSize: 12, color: "#555", marginBottom: 14, paddingTop: 6 }}>
            {ltp != null && <span><strong>Stock LTP:</strong> {fmtRs(ltp)}</span>}
            {meta.lot_size != null && <span><strong>Lot Size:</strong> {meta.lot_size}</span>}
            {meta.min_put_distance_pct != null && (
              <span><strong>Min Put Distance:</strong> {meta.min_put_distance_pct}%</span>
            )}
          </div>

          {/* CSP comparison */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
            {/* Current CSP — square off */}
            <div style={{ background: "#fde2e2", border: "1px solid #f5c6cb", borderRadius: 8, padding: "12px 14px" }}>
              <div style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, color: "#842029", marginBottom: 8, letterSpacing: 0.6 }}>
                Close CSP (BUY) · {meta.current_expiry}
              </div>
              {curCsp.symbol ? (
                <>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#222", marginBottom: 6 }}>{curCsp.symbol}</div>
                  <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12, color: "#555" }}>
                    <span><strong>Strike:</strong> {fmtRs(curCsp.strike)}</span>
                    <span><strong>LTP:</strong> {fmtRs(curCsp.ltp)}</span>
                    <span><strong>Lots:</strong> {curCsp.lots ?? 1}</span>
                    {curCsp.unrealised != null && (
                      <span style={{ fontWeight: 700, color: curCsp.unrealised >= 0 ? "#28a745" : "#dc3545" }}>
                        <strong>Unrealised:</strong> {curCsp.unrealised >= 0 ? "+" : "-"}₹{Math.abs(curCsp.unrealised).toLocaleString("en-IN")}
                      </span>
                    )}
                    {curCsp.decay_pct != null && (
                      <span style={{ fontWeight: 700, color: "#28a745" }}>
                        <strong>Decayed:</strong> {curCsp.decay_pct.toFixed(0)}%
                      </span>
                    )}
                  </div>
                  {curCsp.theta_daily_rs != null && (
                    <div style={{ marginTop: 6, fontSize: 12, color: "#28a745", fontWeight: 600 }}>
                      ⏱ Daily Decay: {fmtTheta(curCsp.theta_daily_rs, curDte)}
                    </div>
                  )}
                </>
              ) : (
                <div style={{ fontSize: 12, color: "#aaa" }}>No active CSP found in positions.</div>
              )}
            </div>

            {/* Proposed next-month CSP */}
            <div style={{ background: "#d4edda", border: "1px solid #b1dfbb", borderRadius: 8, padding: "12px 14px" }}>
              <div style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, color: "#0a5c25", marginBottom: 8, letterSpacing: 0.6 }}>
                New CSP (SELL) · {meta.next_expiry}
              </div>
              {propCsp.symbol ? (
                <>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#222", marginBottom: 6 }}>{propCsp.symbol}</div>
                  <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12, color: "#555" }}>
                    <span><strong>Strike:</strong> {fmtRs(propCsp.strike)}</span>
                    <span><strong>LTP:</strong> {fmtRs(propCsp.ltp)}</span>
                    {propCsp.delta != null && <span><strong>Delta:</strong> {fmtN(propCsp.delta)}</span>}
                    {propCsp.theta_daily_rs != null && (
                      <span style={{ color: "#0055aa", fontWeight: 600 }}>
                        ⏱ Daily Decay: {fmtTheta(propCsp.theta_daily_rs, nextDte)}
                      </span>
                    )}
                    {ltp != null && propCsp.strike != null && (
                      <span style={{ fontWeight: 600, color: putDistColor }}>
                        <strong>Distance:</strong> {fmtN(Math.abs(ltp - propCsp.strike) / ltp * 100)}%
                        {distPct != null && (
                          <span style={{ marginLeft: 4, fontSize: 11, color: putDistColor }}>
                            ({distPct >= (meta.min_put_distance_pct || 0) ? "✓" : "⚠ below min"})
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                  <LiquidityRow bid={propCsp.bid} ask={propCsp.ask} spread_pct={propCsp.spread_pct} oi={propCsp.oi} />
                </>
              ) : (
                <div style={{ fontSize: 12, color: "#aaa" }}>
                  Next-month CSP not yet available — OptionTokens may not be seeded. Will be queued by reconcile once available.
                </div>
              )}
            </div>
          </div>

          {/* Wide spread warning */}
          {propCsp.spread_pct > 10 && (
            <div style={{ background: "#fff3cd", border: "1px solid #ffc107", borderRadius: 8, padding: "8px 14px", marginBottom: 14, fontSize: 12, color: "#856404" }}>
              ⚠️ Wide spread on next-month put ({propCsp.spread_pct?.toFixed(1)}%) — expect slippage on the sell leg.
            </div>
          )}

          <div style={{ fontSize: 11, color: "#aaa", marginBottom: 12 }}>
            Bid/ask quoted live at suggestion generation time. Strike selected at min-put-distance from spot using normal-mode scoring (CSM constraint reset for fresh expiry).
          </div>

          {row.status === "PENDING" && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                onClick={handleApprove}
                disabled={submitting || !!meta.precompute_error}
                style={{ ...btnApprove, background: "#20c997", opacity: (submitting || !!meta.precompute_error) ? 0.5 : 1 }}
              >
                {submitting ? "Executing…" : "⇢ Approve CSP Roll Out"}
              </button>
              <button onClick={onReject} disabled={submitting} style={btnReject}>✕ Reject</button>
              <button onClick={() => onSnooze(60)} disabled={submitting} style={btnSnooze}>Snooze 1h</button>
              <button onClick={() => onSnooze(480)} disabled={submitting} style={btnSnooze}>Snooze 8h</button>
            </div>
          )}
          {row.status !== "PENDING" && (
            <div style={{ fontSize: 13, color: "#555", marginTop: 4 }}>
              {row.status === "COMPLETED" ? "CSP roll-out executed successfully."
                : row.status === "REJECTED" ? "Rejected."
                : row.status}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── RolldownItmPutCard ───────────────────────────────────────── */

function RolldownItmPutCard({ row, expanded, onToggle, onReject, onSnooze, onOptimisticApprove, acting, checked, onCheck, username, showAccount, flash, fetchAll }) {
  const isActivelySnoozed = row.status === "PENDING"
    && row.snooze_until
    && new Date(row.snooze_until.replace(" ", "T") + "Z") > new Date();
  const displayStatus = isActivelySnoozed ? "SNOOZED" : row.status;
  const sm = STATUS_META[displayStatus] || STATUS_META.PENDING;
  const [submitting, setSubmitting] = useState(false);

  const meta = row.metadata || {};
  const cur  = meta.current_put   || {};
  const prop = meta.proposed_put  || null;
  const ltp  = meta.underlying_ltp;

  const fmtRs = v => v != null ? `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}` : "—";
  const fmtN  = (v, d = 2) => v != null ? Number(v).toFixed(d) : "—";

  const pnlDelta = meta.post_roll_potential != null && meta.current_potential_profit != null
    ? meta.post_roll_potential - meta.current_potential_profit
    : null;

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      await axios.post("/automation/suggestions/approve", { id: row.id, username });
      onOptimisticApprove?.();
      flash("Roll-down approved — orders queued");
      fetchAll();
    } catch {
      flash("Approval failed.", false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ ...card, border: "2px solid #6f42c1", outline: checked ? "2px solid #007bff" : "none" }}>
      {/* ── Header ── */}
      <div style={cardTop}>
        {row.status === "PENDING" && (
          <input type="checkbox" checked={!!checked} onChange={onCheck} onClick={e => e.stopPropagation()}
            style={{ marginRight: 10, width: 15, height: 15, cursor: "pointer", flexShrink: 0, accentColor: "#007bff" }} />
        )}
        {row.status !== "PENDING" && <div style={{ width: 25, flexShrink: 0 }} />}
        <div style={{ display: "flex", alignItems: "center", flex: 1, gap: 12, cursor: "pointer" }} onClick={onToggle}>
          <div style={{ ...actionIcon, background: "#ede7f6", color: "#6f42c1", fontSize: 22 }}>↓</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: "#4a1f8e" }}>
              Roll Down ITM Put — {row.symbol}
              {meta.rule_name && (
                <span style={{ marginLeft: 10, fontSize: 11, fontWeight: 500, color: "#888", background: "#f0ebfb", border: "1px solid #d4bfff", borderRadius: 4, padding: "1px 7px", verticalAlign: "middle" }}>
                  {meta.rule_name.replace(/_/g, " ")}
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: "#555", marginTop: 2 }}>
              {showAccount && row.username && (
                <span style={{ marginRight: 8, padding: "1px 7px", borderRadius: 8, background: "#e9ecef", color: "#495057", fontSize: 11, fontWeight: 600 }}>
                  {row.username}
                </span>
              )}
              Close PE <strong>{cur.strike != null ? `₹${cur.strike}` : "—"}</strong>
              {" → "}
              Sell PE <strong>{prop?.strike != null ? `₹${prop.strike}` : "—"}</strong>
              {ltp != null && (
                <span style={{ marginLeft: 8, color: "#6c757d" }}>· LTP {fmtRs(ltp)}</span>
              )}
            </div>
          </div>
          <div style={{ textAlign: "right", minWidth: 140 }}>
            <span style={{ ...statusBadge, background: sm.bg, color: sm.color }}>{sm.label}</span>
            <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>{toIST(row.created_at)}</div>
          </div>
          <div style={{ marginLeft: 12, color: "#ccc", fontSize: 18 }}>{expanded ? "▲" : "▼"}</div>
        </div>
        <NavButtons symbol={row.symbol} />
      </div>

      {/* ── Expanded body ── */}
      {expanded && (
        <div style={cardBody}>
          {row.status === "FAILED" && row.error_message && (
            <div style={{ background: "#fde2e2", border: "1px solid #dc3545", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#dc3545" }}>
              <strong>Failed:</strong> {row.error_message}
            </div>
          )}

          {/* Market context bar */}
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap", fontSize: 12, color: "#555", marginBottom: 14, paddingTop: 6 }}>
            {ltp           != null && <span><strong>Underlying LTP:</strong> {fmtRs(ltp)}</span>}
            {meta.lot_size != null && <span><strong>Lot Size:</strong> {meta.lot_size}</span>}
          </div>

          {/* Put comparison grid */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
            {/* Current ITM put */}
            <div style={{ background: "#fde2e2", border: "1px solid #f5c6cb", borderRadius: 8, padding: "12px 14px" }}>
              <div style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, color: "#842029", marginBottom: 8, letterSpacing: 0.6 }}>
                Close (BUY) — ITM Put
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#222", marginBottom: 8 }}>
                Strike: {cur.strike != null ? fmtRs(cur.strike) : "—"}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 12, color: "#555" }}>
                <span><strong>Option LTP:</strong> {fmtRs(cur.ltp)}</span>
                <span><strong>Extrinsic value:</strong> {fmtRs(cur.extrinsic)}<span style={{ color: "#888", marginLeft: 4 }}>(time value remaining)</span></span>
                <span><strong>Lots:</strong> {cur.lots ?? "—"}</span>
                {cur.unrealised != null && (
                  <span style={{ fontWeight: 700, color: "#dc3545" }}>
                    <strong>Unrealised P&L:</strong> -₹{Math.abs(Number(cur.unrealised)).toLocaleString("en-IN")}
                  </span>
                )}
              </div>
            </div>

            {/* Proposed OTM put */}
            <div style={{ background: prop ? "#d4edda" : "#f8f9fa", border: `1px solid ${prop ? "#b1dfbb" : "#dee2e6"}`, borderRadius: 8, padding: "12px 14px" }}>
              <div style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, color: prop ? "#0a5c25" : "#6c757d", marginBottom: 8, letterSpacing: 0.6 }}>
                New Sell — OTM Put
              </div>
              {prop ? (
                <>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#222", marginBottom: 8 }}>
                    Strike: {fmtRs(prop.strike)}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 12, color: "#555" }}>
                    <span><strong>Option LTP:</strong> {fmtRs(prop.ltp)}<span style={{ color: "#888", marginLeft: 4 }}>(all extrinsic — OTM)</span></span>
                    <span style={{ color: "#28a745", fontWeight: 600 }}>
                      <strong>Extra extrinsic collected:</strong> +{fmtRs(prop.extrinsic_gain)}/share
                    </span>
                    {prop.delta != null && <span><strong>Delta:</strong> {fmtN(prop.delta, 3)}</span>}
                  </div>
                </>
              ) : (
                <div style={{ fontSize: 12, color: "#888", marginTop: 8 }}>
                  No valid OTM candidate satisfies both conditions.
                </div>
              )}
            </div>
          </div>

          {/* P&L impact summary */}
          <div style={{ background: "#f8f6ff", border: "1px solid #d4bfff", borderRadius: 8, padding: "12px 16px", marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "#6f42c1", marginBottom: 10 }}>
              Effect on Potential Profit (incl. booked P&L)
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <div>
                <div style={{ fontSize: 11, color: "#888" }}>Before Roll</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: meta.current_potential_profit >= 0 ? "#28a745" : "#dc3545" }}>
                  {meta.current_potential_profit != null ? `₹${Number(meta.current_potential_profit).toLocaleString("en-IN")}` : "—"}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: "#888" }}>P&L Change</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: pnlDelta >= 0 ? "#28a745" : "#dc3545" }}>
                  {pnlDelta != null ? `${pnlDelta >= 0 ? "+" : ""}₹${Math.abs(pnlDelta).toLocaleString("en-IN")}` : "—"}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: "#888" }}>After Roll</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: meta.post_roll_potential >= 0 ? "#28a745" : "#dc3545" }}>
                  {meta.post_roll_potential != null ? `₹${Number(meta.post_roll_potential).toLocaleString("en-IN")}` : "—"}
                </div>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          {row.status === "PENDING" && (
            <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
              {prop && (
                <button
                  onClick={handleApprove}
                  disabled={submitting || acting}
                  style={{ padding: "7px 20px", borderRadius: 8, border: "none", background: "#6f42c1", color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer", opacity: (submitting || acting) ? 0.6 : 1 }}
                >
                  {submitting ? "Approving…" : "Approve Roll-Down"}
                </button>
              )}
              <button onClick={onReject} disabled={acting}
                style={{ padding: "7px 16px", borderRadius: 8, border: "1px solid #ccc", background: "#fff", color: "#333", fontSize: 13, cursor: "pointer" }}>
                Reject
              </button>
              <button onClick={() => onSnooze?.(60)} disabled={acting}
                style={{ padding: "7px 16px", borderRadius: 8, border: "1px solid #ccc", background: "#fff", color: "#333", fontSize: 13, cursor: "pointer" }}>
                Snooze 1h
              </button>
              <button onClick={() => onSnooze?.(480)} disabled={acting}
                style={{ padding: "7px 16px", borderRadius: 8, border: "1px solid #ccc", background: "#fff", color: "#333", fontSize: 13, cursor: "pointer" }}>
                Snooze 8h
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── RollDownCallCard ─────────────────────────────────────────── */

function RollDownCallCard({ row, expanded, onToggle, onReject, onSnooze, onOptimisticApprove, acting, checked, onCheck, username, showAccount, flash, fetchAll }) {
  const isActivelySnoozed = row.status === "PENDING"
    && row.snooze_until
    && new Date(row.snooze_until.replace(" ", "T") + "Z") > new Date();
  const displayStatus = isActivelySnoozed ? "SNOOZED" : row.status;
  const sm = STATUS_META[displayStatus] || STATUS_META.PENDING;

  const [submitting, setSubmitting] = useState(false);

  const meta   = row.metadata || {};
  const cur    = meta.current_call   || {};
  const prop   = meta.proposed_call  || {};
  // IFP (weak-hedge/CSM-breach) rolldowns stash an {existing_ladder, proposed_ladder}
  // structural comparison (gap %, tail P&L, ladder) instead of the flat
  // proposed_ladder array the PH stock-accumulation rolldown below builds —
  // see docs/ROLLDOWN_WEAK_HEDGE_FULL_RESTORE_PLAN.md.
  const isIfpLadder = !!meta.existing_ladder;
  const existingIfp  = meta.existing_ladder || {};
  const proposedIfp  = meta.proposed_ladder || {};
  const ladder = isIfpLadder ? (proposedIfp.ladder || []) : (meta.proposed_ladder || []);
  const ltp    = meta.underlying_ltp;
  const refAvg = meta.reference_avg_price;

  // Pre-compute constants for Net Profit column
  const _callQty    = (prop.lots || 1) * (meta.lot_size || 1);
  const _callLtp    = prop.ltp    || 0;
  const _callStrike = prop.strike || 0;
  const _putLtp     = meta.put_ltp    || 0;
  const _putStrike  = meta.put_strike || 0;
  const _putQty     = meta.csp_enabled && !meta.csp_error ? (meta.lot_size || 1) : 0;

  const fmtRs = v => v != null ? `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}` : "—";
  const fmtN  = (v, d = 3) => v != null ? Number(v).toFixed(d) : "—";

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      await axios.post("/automation/suggestions/approve", { id: row.id, username });
      onOptimisticApprove?.();
      flash("Roll-down approved — orders queued");
      fetchAll();
    } catch {
      flash("Approval failed.", false);
    } finally {
      setSubmitting(false);
    }
  };

  const thS = { padding: "6px 10px", textAlign: "left", fontWeight: 600, fontSize: 11, borderBottom: "2px solid #dee2e6", color: "#555" };
  const tdS = { padding: "5px 10px", fontSize: 12, borderBottom: "1px solid #f0f0f0" };

  return (
    <div style={{ ...card, border: "2px solid #fd7e14", outline: checked ? "2px solid #007bff" : "none" }}>
      {/* ── Header ── */}
      <div style={{ ...cardTop }}>
        {row.status === "PENDING" && (
          <input type="checkbox" checked={!!checked} onChange={onCheck} onClick={e => e.stopPropagation()}
            style={{ marginRight: 10, width: 15, height: 15, cursor: "pointer", flexShrink: 0, accentColor: "#007bff" }} />
        )}
        {row.status !== "PENDING" && <div style={{ width: 25, flexShrink: 0 }} />}
        <div style={{ display: "flex", alignItems: "center", flex: 1, gap: 12, cursor: "pointer" }} onClick={onToggle}>
          <div style={{ ...actionIcon, background: "#fff3cd", color: "#fd7e14", fontSize: 22 }}>↻</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: "#856404" }}>
              Roll Down Hedge — {row.symbol}
              {meta.rule_name && (
                <span style={{ marginLeft: 10, fontSize: 11, fontWeight: 500, color: "#888", background: "#f5f0e8", border: "1px solid #e8dcc8", borderRadius: 4, padding: "1px 7px", verticalAlign: "middle" }}>
                  {meta.rule_name.replace(/_/g, " ")}
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: "#555", marginTop: 2 }}>
              {showAccount && row.username && (
                <span style={{ marginRight: 8, padding: "1px 7px", borderRadius: 8, background: "#e9ecef", color: "#495057", fontSize: 11, fontWeight: 600 }}>
                  {row.username}
                </span>
              )}
              Close <strong>{cur.symbol || "existing call"}</strong>
              {cur.strike != null && ` @ ₹${cur.strike}`}
              {cur.hedge_pct != null && (
                <span style={{ marginLeft: 6, color: "#6c757d" }}>
                  ({Number(cur.hedge_pct).toFixed(1)}% hedge)
                </span>
              )}
              {" → "}
              Sell <strong>{prop.symbol || "new call"}</strong>
              {prop.strike != null && ` @ ₹${prop.strike}`}
              {prop.hedge_pct != null && (
                <span style={{ marginLeft: 8, color: "#0055aa", fontWeight: 600 }}>
                  · {Number(prop.hedge_pct).toFixed(1)}% hedge
                </span>
              )}
            </div>
          </div>
          <div style={{ textAlign: "right", minWidth: 140 }}>
            <span style={{ ...statusBadge, background: sm.bg, color: sm.color }}>{sm.label}</span>
            <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>{toIST(row.created_at)}</div>
          </div>
          <div style={{ marginLeft: 12, color: "#ccc", fontSize: 18 }}>{expanded ? "▲" : "▼"}</div>
        </div>
        <NavButtons symbol={row.symbol} />
      </div>

      {/* ── Expanded body ── */}
      {expanded && (
        <div style={cardBody}>
          <DownsideFloorPanel meta={row.metadata} />

          {/* Execution failure reason */}
          {row.status === "FAILED" && row.error_message && (
            <div style={{ background: "#fde2e2", border: "1px solid #dc3545", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#dc3545" }}>
              <strong>Failed:</strong> {row.error_message}
            </div>
          )}

          {/* Pre-compute error */}
          {meta.precompute_error && (
            <div style={{ background: "#fde2e2", border: "1px solid #dc3545", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#dc3545" }}>
              Could not compute new structure: {meta.precompute_error}
            </div>
          )}

          {/* Market context */}
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap", fontSize: 12, color: "#555", marginBottom: 14, paddingTop: 6 }}>
            {ltp    != null && <span><strong>Underlying LTP:</strong> {fmtRs(ltp)}</span>}
            {refAvg != null && <span><strong>Reference Avg:</strong>  {fmtRs(refAvg)}</span>}
            {meta.lot_size  != null && <span><strong>Lot Size:</strong> {meta.lot_size}</span>}
            {meta.gap_pct   != null && <span><strong>Gap %:</strong> {Number(meta.gap_pct).toFixed(2)}%</span>}
          </div>

          {/* Call comparison: current vs proposed */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
            {/* Current call — square off */}
            <div style={{ background: "#fde2e2", border: "1px solid #f5c6cb", borderRadius: 8, padding: "12px 14px" }}>
              <div style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, color: "#842029", marginBottom: 8, letterSpacing: 0.6 }}>
                Square Off (BUY)
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#222", marginBottom: 6 }}>{cur.symbol || "—"}</div>
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12, color: "#555" }}>
                <span><strong>Strike:</strong> {fmtRs(cur.strike)}</span>
                <span><strong>LTP:</strong>    {fmtRs(cur.ltp)}</span>
                <span><strong>Lots:</strong>   {cur.lots ?? "—"}</span>
                <span><strong>Delta:</strong>  {fmtN(cur.delta)}</span>
                {cur.hedge_pct != null && (
                  <span style={{ fontWeight: 600, color: "#6c757d" }}>
                    <strong>Hedge:</strong> {Number(cur.hedge_pct).toFixed(1)}%
                  </span>
                )}
                {cur.unrealised != null && (
                  <span style={{ fontWeight: 700, color: cur.unrealised >= 0 ? "#28a745" : "#dc3545" }}>
                    <strong>Unrealised:</strong> {cur.unrealised >= 0 ? "+" : "-"}₹{Math.abs(cur.unrealised).toLocaleString("en-IN")}
                  </span>
                )}
              </div>
            </div>

            {/* Proposed call — new short */}
            <div style={{ background: "#d4edda", border: "1px solid #b1dfbb", borderRadius: 8, padding: "12px 14px" }}>
              <div style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, color: "#0a5c25", marginBottom: 8, letterSpacing: 0.6 }}>
                New Hedge (SELL)
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#222", marginBottom: 6 }}>{prop.symbol || "—"}</div>
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12, color: "#555" }}>
                <span><strong>Strike:</strong>  {fmtRs(prop.strike)}</span>
                <span><strong>LTP:</strong>     {fmtRs(prop.ltp)}</span>
                <span><strong>Lots:</strong>    {prop.lots ?? "—"}</span>
                <span><strong>Delta:</strong>   {fmtN(prop.delta)}</span>
                {prop.hedge_pct != null && (
                  <span style={{ fontWeight: 600, color: "#0055aa" }}>
                    <strong>Hedge:</strong> {Number(prop.hedge_pct).toFixed(1)}%
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* IFP (weak-hedge / CSM-breach): existing vs proposed structural comparison —
              the futures ladder itself doesn't change (same lot count), only the call
              strike, so this shows step gap / tail P&L rather than a stock/futures
              accumulation table. Matches ADD_CALL_LOT_ON_WEAK_HEDGE's card. */}
          {isIfpLadder ? (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
              <div style={{ background: "#f8f9fa", border: "1px solid #dee2e6", borderRadius: 8, padding: "12px 14px" }}>
                <div style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, color: "#555", marginBottom: 8, letterSpacing: 0.6 }}>
                  Existing
                </div>
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12, color: "#555" }}>
                  <span><strong>Lots:</strong> {existingIfp.call_lots ?? "—"}</span>
                  <span><strong>Step Gap:</strong> {existingIfp.gap_pct != null ? `${Number(existingIfp.gap_pct).toFixed(2)}%` : "—"}</span>
                  <span><strong>Avg Price:</strong> {fmtRs(existingIfp.reference_avg_price)}</span>
                  {existingIfp.tail_pnl != null && (
                    <span style={{ fontWeight: 700, color: existingIfp.tail_pnl >= 0 ? "#28a745" : "#dc3545" }}>
                      <strong>Tail P&amp;L:</strong> {existingIfp.tail_pnl >= 0 ? "+" : "-"}₹{Math.abs(Math.round(existingIfp.tail_pnl)).toLocaleString("en-IN")}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ background: "#d4edda", border: "1px solid #b1dfbb", borderRadius: 8, padding: "12px 14px" }}>
                <div style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, color: "#0a5c25", marginBottom: 8, letterSpacing: 0.6 }}>
                  Proposed
                </div>
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12, color: "#555" }}>
                  <span><strong>Lots:</strong> {proposedIfp.call_lots ?? "—"}</span>
                  <span><strong>Step Gap:</strong> {proposedIfp.gap_pct != null ? `${Number(proposedIfp.gap_pct).toFixed(2)}%` : "—"}</span>
                  <span><strong>Avg Price:</strong> {fmtRs(proposedIfp.reference_avg_price)}</span>
                  {proposedIfp.tail_pnl != null && (
                    <span style={{ fontWeight: 700, color: proposedIfp.tail_pnl >= 0 ? "#28a745" : "#dc3545" }}>
                      <strong>Tail P&amp;L:</strong> {proposedIfp.tail_pnl >= 0 ? "+" : "-"}₹{Math.abs(Math.round(proposedIfp.tail_pnl)).toLocaleString("en-IN")}
                    </span>
                  )}
                </div>
              </div>
              {!proposedIfp.tail_pnl && !meta.precompute_error && (
                <div style={{ gridColumn: "1 / -1", background: "#fff3cd", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#856404" }}>
                  Structural detail (step gap / tail P&amp;L) couldn't be computed for the proposed strike — the swap itself is still valid, just shown without this extra context.
                </div>
              )}
              {proposedIfp.stock_excluded && (
                <div style={{ gridColumn: "1 / -1", background: "#fde2e2", border: "1px solid #f5c6cb", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#842029" }}>
                  ⚠️ <strong>Stock excluded from this calculation.</strong> {proposedIfp.stock_excluded_note || "The existing stock position's loss made a blended calculation infeasible, so this ladder was built treating the position as pure futures+options."}
                </div>
              )}
            </div>
          ) : null}

          {/* IFP ladder detail — the accumulation ladder itself doesn't change
              (same futures/stock plan, only the call strike swaps), so existing
              and proposed share identical steps except for the final net_pnl
              column (priced against each side's own call). Avg Price/After
              Step here is the PROJECTED blended average once the ladder fully
              completes (today's real position blended with the remaining
              planned buy-steps below) — not today's literal cost basis. */}
          {isIfpLadder && (existingIfp.ladder?.length > 0 || proposedIfp.ladder?.length > 0) && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
              {[
                { label: "Existing Ladder", data: existingIfp, tint: "#555" },
                { label: "Proposed Ladder", data: proposedIfp, tint: "#0a5c25" },
              ].map(({ label, data, tint }) => (
                <div key={label}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: tint, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
                    {label}
                  </div>
                  {data.ladder?.length > 0 ? (
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11.5 }}>
                      <thead>
                        <tr style={{ background: "#f5f7fa" }}>
                          <th style={thS}>Step</th>
                          <th style={thS}>Buy Price</th>
                          <th style={thS}>Qty</th>
                          <th style={thS}>Total Qty</th>
                          <th style={thS}>Avg After Step</th>
                          <th style={thS}>Hedge %</th>
                          <th style={thS}>Net P&amp;L</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.ladder.map((s, i) => (
                          <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                            <td style={tdS}>{s.step ?? i + 1}</td>
                            <td style={tdS}>{fmtRs(s.buy_price)}</td>
                            <td style={tdS}>{s.qty}</td>
                            <td style={tdS}>{s.total_qty}</td>
                            <td style={{ ...tdS, fontWeight: 600 }}>{fmtRs(s.avg_price)}</td>
                            <td style={tdS}>{s.projected_hedge_pct != null ? `${Number(s.projected_hedge_pct).toFixed(1)}%` : "—"}</td>
                            <td style={{ ...tdS, fontWeight: 700, color: (s.net_pnl ?? 0) >= 0 ? "#28a745" : "#dc3545" }}>
                              {s.net_pnl != null ? `${s.net_pnl >= 0 ? "+" : "-"}₹${Math.abs(Math.round(s.net_pnl)).toLocaleString("en-IN")}` : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div style={{ fontSize: 12, color: "#999" }}>No ladder detail available.</div>
                  )}
                </div>
              ))}
            </div>
          )}

          {!isIfpLadder && ladder.length > 0 ? (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#555", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
                New Accumulation Ladder
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 14 }}>
                <thead>
                  <tr style={{ background: "#f5f7fa" }}>
                    <th style={thS}>Step</th>
                    <th style={thS}>Buy Price</th>
                    <th style={thS}>% from LTP</th>
                    <th style={thS}>Qty</th>
                    <th style={thS}>Total Qty</th>
                    <th style={thS}>Avg After Step</th>
                    <th style={{ ...thS, color: "#17a2b8" }}>Net P&amp;L at Buy Price (incl. Booked)</th>
                  </tr>
                </thead>
                <tbody>
                  {ladder.map((s, i) => {
                    const pct      = ltp ? ((s.buy_price - ltp) / ltp * 100) : null;
                    // Mark-to-market at this step's OWN buy price — see the matching
                    // comment on the SELL_INITIAL_CALL card.
                    const stockPnl   = (s.buy_price - s.avg_price) * s.total_qty;
                    const callIntrinsic = _callStrike > 0 ? Math.max(0, s.buy_price - _callStrike) * _callQty : 0;
                    const callPnl    = _callLtp * _callQty - callIntrinsic;
                    const putIntrinsic = _putQty > 0 ? Math.max(0, _putStrike - s.buy_price) * _putQty : 0;
                    const putPnl     = _putQty > 0 ? (_putLtp * _putQty - putIntrinsic) : 0;
                    // Include P&L from closing the current call (cur.unrealised)
                    const bookedPnl  = cur.unrealised || 0;
                    const netPnl     = stockPnl + callPnl + putPnl + bookedPnl;
                    const pnlClr   = netPnl >= 0 ? "#28a745" : "#dc3545";
                    const fmtPnl   = v => `${v >= 0 ? "+" : "-"}₹${Math.abs(Math.round(v)).toLocaleString("en-IN")}`;
                    return (
                      <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                        <td style={tdS}>{s.step ?? i + 1}</td>
                        <td style={{ ...tdS, fontWeight: 600 }}>{fmtRs(s.buy_price)}</td>
                        <td style={{ ...tdS, color: pct == null ? "#999" : pct >= 0 ? "#28a745" : "#dc3545" }}>
                          {pct != null ? `${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%` : "—"}
                        </td>
                        <td style={tdS}>{s.qty}</td>
                        <td style={tdS}>{s.total_qty}</td>
                        <td style={tdS}>{fmtRs(s.avg_price)}</td>
                        <td style={{ ...tdS, fontWeight: 700, color: pnlClr }}>{fmtPnl(netPnl)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          ) : !meta.precompute_error && (
            <div style={{ background: "#e8f4e8", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#155724", marginBottom: 14 }}>
              No additional accumulation steps — position already at target lots.
            </div>
          )}

          {/* Staleness note */}
          <div style={{ fontSize: 11, color: "#aaa", marginBottom: 12 }}>
            Structure computed at queue time using existing strategy settings. Executor recomputes fresh data on approval.
          </div>

          {/* Action buttons */}
          {row.status === "PENDING" && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                onClick={handleApprove}
                disabled={submitting || !!meta.precompute_error}
                style={{ ...btnApprove, opacity: (submitting || !!meta.precompute_error) ? 0.5 : 1 }}
              >
                {submitting ? "Executing…" : "✓ Approve Roll Down"}
              </button>
              <button onClick={onReject} disabled={submitting} style={btnReject}>
                ✕ Reject
              </button>
              <button onClick={() => onSnooze(60)} disabled={submitting} style={btnSnooze}>Snooze 1h</button>
              <button onClick={() => onSnooze(480)} disabled={submitting} style={btnSnooze}>Snooze 8h</button>
            </div>
          )}

          {row.status !== "PENDING" && (
            <div style={{ fontSize: 13, color: "#555", marginTop: 4 }}>
              {row.status === "COMPLETED" ? "Roll-down executed successfully."
                : row.status === "REJECTED" ? "Rejected."
                : row.status}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AddCallLotCard({ row, expanded, onToggle, onReject, onSnooze, onOptimisticApprove, acting, checked, onCheck, username, showAccount, flash, fetchAll }) {
  const isActivelySnoozed = row.status === "PENDING"
    && row.snooze_until
    && new Date(row.snooze_until.replace(" ", "T") + "Z") > new Date();
  const displayStatus = isActivelySnoozed ? "SNOOZED" : row.status;
  const sm = STATUS_META[displayStatus] || STATUS_META.PENDING;

  const [submitting, setSubmitting] = useState(false);

  const meta     = row.metadata || {};
  const existing = meta.existing_ladder || {};
  const proposed = meta.proposed_ladder || {};
  const ladder   = proposed.ladder || [];
  const ltp      = meta.underlying_ltp;

  const _callStrike = proposed.call_strike;
  const _callLtp    = proposed.call_ltp || 0;
  const _callQty    = (proposed.call_lots || 0) * (meta.lot_size || 1);

  const fmtRs    = v => v != null ? `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}` : "—";
  const fmtPnlRs = v => v != null ? `${v >= 0 ? "+" : "-"}₹${Math.abs(Math.round(v)).toLocaleString("en-IN")}` : "—";

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      await axios.post("/automation/suggestions/approve", { id: row.id, username });
      onOptimisticApprove?.();
      flash("Add call lot approved — order queued");
      fetchAll();
    } catch {
      flash("Approval failed.", false);
    } finally {
      setSubmitting(false);
    }
  };

  const thS = { padding: "6px 10px", textAlign: "left", fontWeight: 600, fontSize: 11, borderBottom: "2px solid #dee2e6", color: "#555" };
  const tdS = { padding: "5px 10px", fontSize: 12, borderBottom: "1px solid #f0f0f0" };

  const gapChanged = existing.gap_pct != null && proposed.gap_pct != null && existing.gap_pct !== proposed.gap_pct;

  return (
    <div style={{ ...card, border: "2px solid #fd7e14", outline: checked ? "2px solid #007bff" : "none" }}>
      {/* ── Header ── */}
      <div style={{ ...cardTop }}>
        {row.status === "PENDING" && (
          <input type="checkbox" checked={!!checked} onChange={onCheck} onClick={e => e.stopPropagation()}
            style={{ marginRight: 10, width: 15, height: 15, cursor: "pointer", flexShrink: 0, accentColor: "#007bff" }} />
        )}
        {row.status !== "PENDING" && <div style={{ width: 25, flexShrink: 0 }} />}
        <div style={{ display: "flex", alignItems: "center", flex: 1, gap: 12, cursor: "pointer" }} onClick={onToggle}>
          <div style={{ ...actionIcon, background: "#fff3cd", color: "#fd7e14", fontSize: 22 }}>+</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: "#856404" }}>
              Add Call Lot — {row.symbol}
              {meta.rule_name && (
                <span style={{ marginLeft: 10, fontSize: 11, fontWeight: 500, color: "#888", background: "#f5f0e8", border: "1px solid #e8dcc8", borderRadius: 4, padding: "1px 7px", verticalAlign: "middle" }}>
                  {meta.rule_name.replace(/_/g, " ")}
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: "#555", marginTop: 2 }}>
              {showAccount && row.username && (
                <span style={{ marginRight: 8, padding: "1px 7px", borderRadius: 8, background: "#e9ecef", color: "#495057", fontSize: 11, fontWeight: 600 }}>
                  {row.username}
                </span>
              )}
              Sell <strong>{meta.added_lots ?? 1}</strong> more lot(s) of <strong>{meta.current_call_symbol || "current call"}</strong>
              {" "}(same strike) — {meta.current_call_lots ?? 0} → {(meta.current_call_lots ?? 0) + (meta.added_lots ?? 1)} lots
            </div>
          </div>
          <div style={{ textAlign: "right", minWidth: 140 }}>
            <span style={{ ...statusBadge, background: sm.bg, color: sm.color }}>{sm.label}</span>
            <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>{toIST(row.created_at)}</div>
          </div>
          <div style={{ marginLeft: 12, color: "#ccc", fontSize: 18 }}>{expanded ? "▲" : "▼"}</div>
        </div>
        <NavButtons symbol={row.symbol} />
      </div>

      {/* ── Expanded body ── */}
      {expanded && (
        <div style={cardBody}>
          <DownsideFloorPanel meta={row.metadata} />

          {/* Execution failure reason */}
          {row.status === "FAILED" && row.error_message && (
            <div style={{ background: "#fde2e2", border: "1px solid #dc3545", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#dc3545" }}>
              <strong>Failed:</strong> {row.error_message}
            </div>
          )}

          {/* Market context */}
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap", fontSize: 12, color: "#555", marginBottom: 14, paddingTop: 6 }}>
            {ltp != null && <span><strong>Underlying LTP:</strong> {fmtRs(ltp)}</span>}
            {_callStrike != null && <span><strong>Call Strike:</strong> {fmtRs(_callStrike)} (unchanged)</span>}
            {meta.lot_size != null && <span><strong>Lot Size:</strong> {meta.lot_size}</span>}
            {meta.current_expiry_potential_profit != null && (
              <span style={{ fontWeight: 700, color: meta.current_expiry_potential_profit >= 0 ? "#28a745" : "#dc3545" }}>
                <strong>Potential P&amp;L:</strong> {fmtPnlRs(meta.current_expiry_potential_profit)}
                {meta.min_profit_csm != null && (
                  <span style={{ marginLeft: 4, fontWeight: 500, color: "#856404" }}>
                    (CSM floor {fmtPnlRs(meta.min_profit_csm)})
                  </span>
                )}
              </span>
            )}
          </div>

          {/* Existing vs proposed: lots / step gap / avg price / tail P&L */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
            <div style={{ background: "#f8f9fa", border: "1px solid #dee2e6", borderRadius: 8, padding: "12px 14px" }}>
              <div style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, color: "#555", marginBottom: 8, letterSpacing: 0.6 }}>
                Existing
              </div>
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12, color: "#555" }}>
                <span><strong>Lots:</strong> {existing.call_lots ?? "—"}</span>
                <span><strong>Step Gap:</strong> {existing.gap_pct != null ? `${Number(existing.gap_pct).toFixed(2)}%` : "—"}</span>
                <span><strong>Avg Price:</strong> {fmtRs(existing.reference_avg_price)}</span>
                {existing.tail_pnl != null && (
                  <span style={{ fontWeight: 700, color: existing.tail_pnl >= 0 ? "#28a745" : "#dc3545" }}>
                    <strong>Tail P&amp;L:</strong> {fmtPnlRs(existing.tail_pnl)}
                  </span>
                )}
              </div>
            </div>

            <div style={{ background: "#d4edda", border: "1px solid #b1dfbb", borderRadius: 8, padding: "12px 14px" }}>
              <div style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, color: "#0a5c25", marginBottom: 8, letterSpacing: 0.6 }}>
                Proposed (after adding {meta.added_lots ?? 1} lot{(meta.added_lots ?? 1) > 1 ? "s" : ""})
              </div>
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12, color: "#555" }}>
                <span><strong>Lots:</strong> {proposed.call_lots ?? "—"}</span>
                <span style={gapChanged ? { fontWeight: 700, color: "#0055aa" } : undefined}>
                  <strong>Step Gap:</strong> {proposed.gap_pct != null ? `${Number(proposed.gap_pct).toFixed(2)}%` : "—"}
                  {gapChanged && (
                    <span style={{ marginLeft: 4, fontSize: 11, color: "#856404" }}>
                      (lowered from {Number(existing.gap_pct).toFixed(2)}%)
                    </span>
                  )}
                </span>
                <span><strong>Avg Price:</strong> {fmtRs(proposed.reference_avg_price)}</span>
                {proposed.tail_pnl != null && (
                  <span style={{ fontWeight: 700, color: proposed.tail_pnl >= 0 ? "#28a745" : "#dc3545" }}>
                    <strong>Tail P&amp;L:</strong> {fmtPnlRs(proposed.tail_pnl)}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Proposed futures ladder */}
          {ladder.length > 0 ? (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#555", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
                Proposed Futures Ladder
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 14 }}>
                <thead>
                  <tr style={{ background: "#f5f7fa" }}>
                    <th style={thS}>Step</th>
                    <th style={thS}>Buy Price</th>
                    <th style={thS}>% from LTP</th>
                    <th style={thS}>Qty</th>
                    <th style={thS}>Total Qty</th>
                    <th style={thS}>Avg After Step</th>
                    <th style={{ ...thS, color: "#17a2b8" }}>Tail P&amp;L at ₹{_callStrike} Strike</th>
                  </tr>
                </thead>
                <tbody>
                  {ladder.map((s, i) => {
                    const pct = ltp ? ((s.buy_price - ltp) / ltp * 100) : null;
                    const rowPnl = _callStrike != null
                      ? (_callStrike - s.avg_price) * s.total_qty + _callLtp * _callQty
                      : null;
                    return (
                      <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                        <td style={tdS}>{s.step ?? i + 1}</td>
                        <td style={{ ...tdS, fontWeight: 600 }}>{fmtRs(s.buy_price)}</td>
                        <td style={{ ...tdS, color: pct == null ? "#999" : pct >= 0 ? "#28a745" : "#dc3545" }}>
                          {pct != null ? `${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%` : "—"}
                        </td>
                        <td style={tdS}>{s.qty}</td>
                        <td style={tdS}>{s.total_qty}</td>
                        <td style={tdS}>{fmtRs(s.avg_price)}</td>
                        <td style={{ ...tdS, fontWeight: 700, color: rowPnl == null ? "#999" : rowPnl >= 0 ? "#28a745" : "#dc3545" }}>
                          {rowPnl != null ? fmtPnlRs(rowPnl) : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          ) : (
            <div style={{ background: "#fde2e2", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#842029", marginBottom: 14 }}>
              No proposed ladder data available.
            </div>
          )}

          {/* Staleness / setting-change note */}
          <div style={{ fontSize: 11, color: "#aaa", marginBottom: 12 }}>
            Structure computed at queue time using existing strategy settings. Executor recomputes fresh data on approval.
            {gapChanged && " Approving this will permanently lower the strategy's Minimum Ladder Step Gap setting to match the proposed gap above."}
          </div>

          {/* Action buttons */}
          {row.status === "PENDING" && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button onClick={handleApprove} disabled={submitting} style={{ ...btnApprove, opacity: submitting ? 0.5 : 1 }}>
                {submitting ? "Executing…" : "✓ Approve Add Lot"}
              </button>
              <button onClick={onReject} disabled={submitting} style={btnReject}>
                ✕ Reject
              </button>
              <button onClick={() => onSnooze(60)} disabled={submitting} style={btnSnooze}>Snooze 1h</button>
              <button onClick={() => onSnooze(480)} disabled={submitting} style={btnSnooze}>Snooze 8h</button>
            </div>
          )}

          {row.status !== "PENDING" && (
            <div style={{ fontSize: 13, color: "#555", marginTop: 4 }}>
              {row.status === "COMPLETED" ? "Lot added successfully."
                : row.status === "REJECTED" ? "Rejected."
                : row.status}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SituationBriefingCard({ row, expanded, onToggle, onReject, acting, username, showAccount, flash, fetchAll }) {
  const isActivelySnoozed = row.status === "PENDING"
    && row.snooze_until
    && new Date(row.snooze_until.replace(" ", "T") + "Z") > new Date();
  const displayStatus = isActivelySnoozed ? "SNOOZED" : row.status;
  const sm = STATUS_META[displayStatus] || STATUS_META.PENDING;

  const [submitting, setSubmitting] = useState(false);

  const meta    = row.metadata || {};
  const trigger = meta.trigger  || {};
  const delta   = meta.delta_state || {};
  const plan    = meta.plan    || {};
  const disp    = plan.display || {};
  const rm      = RULE_META[plan.rule] || {};

  const fmt  = v => v != null ? `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 0 })}` : "—";
  const fmtN = (v, decimals = 4) => v != null ? Number(v).toFixed(decimals) : "—";

  const handleAccept = async () => {
    setSubmitting(true);
    try {
      await axios.post("/automation/suggestions/approve", { id: row.id, username });
      flash(`Executing: ${plan.label || "Auto rebalance"}`);
      fetchAll();
    } catch {
      flash("Execution failed.", false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ ...card, border: "2px solid #007bff", outline: "none" }}>
      {/* Header */}
      <div style={{ ...cardTop, cursor: "pointer" }} onClick={onToggle}>
        <div style={{ display: "flex", alignItems: "center", flex: 1, gap: 12 }}>
          <div style={{ ...actionIcon, background: "#cce5ff", color: "#004085", fontSize: 22 }}>⚡</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: "#004085" }}>
              Auto Rebalance — {row.symbol}
              {showAccount && row.username && (
                <span style={{ marginLeft: 10, padding: "1px 7px", borderRadius: 8, background: "#e9ecef", color: "#495057", fontSize: 11, fontWeight: 600, verticalAlign: "middle" }}>
                  {row.username}
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: "#555", marginTop: 2 }}>
              {trigger.direction === "UP"
                ? `Price up ${Math.abs(trigger.pct_from_reference || 0).toFixed(2)}% → broke upper band`
                : trigger.direction === "DOWN"
                ? `Price down ${Math.abs(trigger.pct_from_reference || 0).toFixed(2)}% → broke lower band`
                : "Price band breached"}
              {delta.imbalance != null && ` · Delta imbalance ${Number(delta.imbalance).toFixed(3)}`}
              {rm.letter && (
                <span style={{ marginLeft: 8, fontWeight: 600, color: rm.color }}>
                  · Rule {rm.letter} selected
                </span>
              )}
            </div>
          </div>
          <div style={{ textAlign: "right", minWidth: 140 }}>
            <span style={{ ...statusBadge, background: sm.bg, color: sm.color }}>{sm.label}</span>
            <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>{toIST(row.created_at)}</div>
          </div>
          <div style={{ marginLeft: 12, color: "#ccc", fontSize: 18 }}>{expanded ? "▲" : "▼"}</div>
        </div>
        <NavButtons symbol={row.symbol} />
      </div>

      {expanded && (
        <div style={cardBody}>

          {/* Trigger context */}
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: 12, color: "#555", marginBottom: 14, paddingTop: 4 }}>
            {trigger.current_ltp        && <span><strong>LTP:</strong> ₹{trigger.current_ltp}</span>}
            {trigger.delta_neutral_price && <span><strong>Delta-neutral price:</strong> ₹{trigger.delta_neutral_price}</span>}
            {trigger.upper_band         && <span><strong>Upper band:</strong> ₹{trigger.upper_band}</span>}
            {trigger.lower_band         && <span><strong>Lower band:</strong> ₹{trigger.lower_band}</span>}
            {trigger.band_width_pct     && <span><strong>Band width:</strong> {trigger.band_width_pct}%</span>}
            {delta.heavier_side         && <span><strong>Heavier:</strong> {delta.heavier_side}</span>}
            {delta.threshold            && <span><strong>Δ threshold:</strong> {Number(delta.threshold).toFixed(3)}</span>}
          </div>

          {/* Decision card */}
          {plan.rule && plan.rule !== "error" && (
            <div style={{ background: rm.bg || "#f8f9fb", border: `1px solid ${rm.color || "#dee2e6"}`, borderRadius: 8, padding: "12px 16px", marginBottom: 14 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: rm.color || "#333", marginBottom: 6 }}>
                Rule {rm.letter}: {plan.label}
              </div>

              {/* Rule a/d — close details */}
              {(plan.rule === "a" || plan.rule === "d") && (
                <div style={{ display: "flex", gap: 20, flexWrap: "wrap", fontSize: 12, color: "#444" }}>
                  {disp.profit_estimate != null && (
                    <span><strong>Est. profit:</strong> <span style={{ color: "#28a745", fontWeight: 600 }}>{fmt(disp.profit_estimate)}</span></span>
                  )}
                  {disp.new_delta_diff  != null && <span><strong>New delta diff:</strong> {fmtN(disp.new_delta_diff)}</span>}
                  {disp.decay_pct       != null && <span><strong>Decay:</strong> {(disp.decay_pct * 100).toFixed(1)}%</span>}
                  {disp.new_upper_band  != null && <span><strong>New upper band:</strong> ₹{disp.new_upper_band}</span>}
                  {disp.new_lower_band  != null && <span><strong>New lower band:</strong> ₹{disp.new_lower_band}</span>}
                  {disp.delta_neutral_price != null && <span><strong>Delta-neutral:</strong> ₹{disp.delta_neutral_price}</span>}
                </div>
              )}

              {/* Rule b — sell lighter side */}
              {plan.rule === "b" && (
                <div style={{ display: "flex", gap: 20, flexWrap: "wrap", fontSize: 12, color: "#444" }}>
                  {disp.option_type  && disp.strike != null && (
                    <span><strong>Contract:</strong> {disp.option_type} {disp.strike}</span>
                  )}
                  {disp.contract_delta  != null && <span><strong>Delta:</strong> {fmtN(disp.contract_delta)}</span>}
                  {disp.new_delta_diff  != null && <span><strong>New delta diff:</strong> {fmtN(disp.new_delta_diff)}</span>}
                  {disp.new_upper_band  != null && <span><strong>New upper band:</strong> ₹{disp.new_upper_band}</span>}
                  {disp.new_lower_band  != null && <span><strong>New lower band:</strong> ₹{disp.new_lower_band}</span>}
                  {disp.delta_neutral_price != null && <span><strong>Delta-neutral:</strong> ₹{disp.delta_neutral_price}</span>}
                </div>
              )}

              {/* Rule c — extend band */}
              {plan.rule === "c" && (
                <div style={{ display: "flex", gap: 20, flexWrap: "wrap", fontSize: 12, color: "#444" }}>
                  {disp.breach_direction && <span><strong>Breach side:</strong> {disp.breach_direction}</span>}
                  {disp.extension_pct    != null && (
                    <span>
                      <strong>Extension:</strong> {disp.extension_pct}%
                      {disp.extension_capped && <span style={{ color: "#dc3545", marginLeft: 4 }}>(capped at break-even)</span>}
                    </span>
                  )}
                  {disp.new_upper_band   != null && <span><strong>New upper band:</strong> ₹{disp.new_upper_band}</span>}
                  {disp.new_lower_band   != null && <span><strong>New lower band:</strong> ₹{disp.new_lower_band}</span>}
                  {disp.delta_neutral_price != null && <span><strong>Delta-neutral:</strong> ₹{disp.delta_neutral_price}</span>}
                </div>
              )}
            </div>
          )}

          {plan.rule === "error" && (
            <div style={{ background: "#fde2e2", border: "1px solid #dc3545", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#dc3545" }}>
              Plan computation failed: {plan.label}
            </div>
          )}

          {/* Accept / Dismiss */}
          {row.status === "PENDING" && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                onClick={handleAccept}
                disabled={submitting || plan.rule === "error"}
                style={{ ...btnApprove, opacity: (submitting || plan.rule === "error") ? 0.5 : 1 }}
              >
                {submitting ? "Executing…" : "✓ Accept"}
              </button>
              <button onClick={onReject} disabled={submitting} style={btnReject}>
                ✕ Dismiss
              </button>
            </div>
          )}

          {/* Completed view */}
          {row.status !== "PENDING" && (
            <div style={{ ...metaBox, marginTop: 0 }}>
              <div style={{ fontSize: 13, color: "#555" }}>
                {row.status === "COMPLETED"
                  ? `Executed: ${plan.label || (meta.selected_option || {}).label || "—"}`
                  : row.status === "REJECTED" ? "Dismissed"
                  : row.status}
              </div>
            </div>
          )}

        </div>
      )}
    </div>
  );
}

/* ── Sub-components ───────────────────────────────────────────── */

function StatTile({ label, value, color, highlight }) {
  return (
    <div style={{ ...tile, boxShadow: highlight ? `0 0 0 2px ${color}` : tile.boxShadow }}>
      <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}

/* ── SquareOffStocksCard ──────────────────────────────────────── */

function SquareOffStocksCard({ row, expanded, onToggle, onReject, onSnooze, onOptimisticApprove, acting, checked, onCheck, username, showAccount, flash, fetchAll }) {
  const isActivelySnoozed = row.status === "PENDING"
    && row.snooze_until
    && new Date(row.snooze_until.replace(" ", "T") + "Z") > new Date();
  const displayStatus = isActivelySnoozed ? "SNOOZED" : row.status;
  const sm = STATUS_META[displayStatus] || STATUS_META.PENDING;

  const [submitting, setSubmitting] = useState(false);
  const meta = row.metadata || {};

  const fmtRs = v => v != null ? `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 0 })}` : "—";
  const fmtRs2 = v => v != null ? `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}` : "—";
  const trColor = v => v == null ? "#555" : v >= 0 ? "#28a745" : "#dc3545";

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      await axios.post("/automation/suggestions/approve", { id: row.id, username });
      onOptimisticApprove?.();
      flash("Partial sell approved — orders queued");
      fetchAll();
    } catch {
      flash("Approval failed.", false);
    } finally {
      setSubmitting(false);
    }
  };

  const thS = { padding: "6px 10px", textAlign: "left", fontWeight: 600, fontSize: 11, borderBottom: "2px solid #dee2e6", color: "#555" };
  const tdS = { padding: "5px 10px", fontSize: 12, borderBottom: "1px solid #f0f0f0" };
  const tdR = { ...tdS, textAlign: "right" };

  const ladder = meta.new_ladder || [];

  return (
    <div style={{ ...card, border: "2px solid #6f42c1", outline: checked ? "2px solid #007bff" : "none" }}>
      {/* Header */}
      <div style={{ ...cardTop }}>
        {row.status === "PENDING" && (
          <input type="checkbox" checked={!!checked} onChange={onCheck} onClick={e => e.stopPropagation()}
            style={{ marginRight: 10, width: 15, height: 15, cursor: "pointer", flexShrink: 0, accentColor: "#007bff" }} />
        )}
        {row.status !== "PENDING" && <div style={{ width: 25, flexShrink: 0 }} />}
        <div style={{ display: "flex", alignItems: "center", flex: 1, gap: 12, cursor: "pointer" }} onClick={onToggle}>
          <div style={{ ...actionIcon, background: "#f3e8ff", color: "#6f42c1", fontSize: 22 }}>📉</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: "#4a235a" }}>
              Partial Stock Sell — {row.symbol}
              {meta.rule_name && (
                <span style={{ marginLeft: 10, fontSize: 11, fontWeight: 500, color: "#888", background: "#f5f0f8", border: "1px solid #d8c8e8", borderRadius: 4, padding: "1px 7px", verticalAlign: "middle" }}>
                  {meta.rule_name.replace(/_/g, " ")}
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: "#555", marginTop: 2 }}>
              {showAccount && row.username && (
                <span style={{ marginRight: 8, padding: "1px 7px", borderRadius: 8, background: "#e9ecef", color: "#495057", fontSize: 11, fontWeight: 600 }}>
                  {row.username}
                </span>
              )}
              Sell <strong>{meta.sell_qty ?? "?"} shares</strong>
              {meta.sell_pct != null && <span style={{ color: "#6c757d" }}> ({meta.sell_pct}%)</span>}
              {" · Keep "}
              <strong>{meta.remaining_qty ?? "?"} shares</strong>
              {meta.profit_booked != null && (
                <span style={{ marginLeft: 8, color: "#28a745", fontWeight: 600 }}>
                  · Book {fmtRs2(meta.profit_booked)} profit
                </span>
              )}
            </div>
          </div>
          <div style={{ textAlign: "right", minWidth: 140 }}>
            <span style={{ ...statusBadge, background: sm.bg, color: sm.color }}>{sm.label}</span>
            <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>{toIST(row.created_at)}</div>
          </div>
          <div style={{ marginLeft: 12, color: "#ccc", fontSize: 18 }}>{expanded ? "▲" : "▼"}</div>
        </div>
        <NavButtons symbol={row.symbol} />
      </div>

      {/* Expanded body */}
      {expanded && (
        <div style={cardBody}>
          {row.status === "FAILED" && row.error_message && (
            <div style={{ background: "#fde2e2", border: "1px solid #dc3545", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#dc3545" }}>
              <strong>Failed:</strong> {row.error_message}
            </div>
          )}

          {/* Market context */}
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap", fontSize: 12, color: "#555", marginBottom: 14, paddingTop: 6 }}>
            {meta.underlying_ltp != null && <span><strong>LTP:</strong> {fmtRs2(meta.underlying_ltp)}</span>}
            {meta.avg_price != null && <span><strong>Avg Cost:</strong> {fmtRs2(meta.avg_price)}</span>}
            {meta.current_qty != null && <span><strong>Current Holdings:</strong> {meta.current_qty} shares</span>}
          </div>

          {/* Sell plan & tail risk */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div style={{ background: "#fff8f0", border: "1px solid #e8cfa0", borderRadius: 8, padding: "10px 14px" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#856404", marginBottom: 8, textTransform: "uppercase" }}>Sell Plan</div>
              <div style={{ fontSize: 13, display: "flex", flexDirection: "column", gap: 5 }}>
                <div><strong>Sell:</strong> {meta.sell_qty ?? "?"} shares ({meta.sell_pct ?? "?"}%)</div>
                <div><strong>Keep:</strong> {meta.remaining_qty ?? "?"} shares</div>
                {meta.profit_booked != null && (
                  <div style={{ color: "#28a745" }}><strong>Est. Profit Booked:</strong> {fmtRs2(meta.profit_booked)}</div>
                )}
              </div>
            </div>
            <div style={{ background: "#f0f8f0", border: "1px solid #a8d5a8", borderRadius: 8, padding: "10px 14px" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#155724", marginBottom: 8, textTransform: "uppercase" }}>Tail Risk at Expiry</div>
              <div style={{ fontSize: 13, display: "flex", flexDirection: "column", gap: 5 }}>
                <div style={{ color: trColor(meta.current_tail_risk) }}>
                  <strong>Current:</strong> {fmtRs(meta.current_tail_risk)}
                </div>
                <div style={{ color: trColor(meta.new_tail_risk) }}>
                  <strong>After sell:</strong> {fmtRs(meta.new_tail_risk)}
                </div>
                {meta.min_profit_csm != null && (
                  <div style={{ color: "#6c757d", fontSize: 11 }}>Floor (min_profit_csm): {fmtRs(meta.min_profit_csm)}</div>
                )}
              </div>
            </div>
          </div>

          {/* New call context */}
          {(meta.new_call_symbol || meta.new_call_strike) && (
            <div style={{ background: "#f8f9fa", border: "1px solid #dee2e6", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 11, color: "#555", marginBottom: 6, textTransform: "uppercase" }}>Active Call (unchanged)</div>
              <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
                {meta.new_call_symbol && <span><strong>Symbol:</strong> {meta.new_call_symbol}</span>}
                {meta.new_call_strike != null && <span><strong>Strike:</strong> {fmtRs2(meta.new_call_strike)}</span>}
                {meta.new_call_ltp    != null && <span><strong>LTP:</strong> {fmtRs2(meta.new_call_ltp)}</span>}
                {meta.new_avg_price   != null && <span><strong>New Avg (at full lot):</strong> {fmtRs2(meta.new_avg_price)}</span>}
              </div>
            </div>
          )}

          {/* New ladder */}
          {ladder.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#555", marginBottom: 8, textTransform: "uppercase" }}>
                New Ladder from {meta.remaining_qty} Shares
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: "#f8f9fa" }}>
                      <th style={thS}>Step</th>
                      <th style={{ ...thS, textAlign: "right" }}>Buy Price</th>
                      <th style={{ ...thS, textAlign: "right" }}>Gap %</th>
                      <th style={{ ...thS, textAlign: "right" }}>Qty</th>
                      <th style={{ ...thS, textAlign: "right" }}>Total Qty</th>
                      <th style={{ ...thS, textAlign: "right" }}>Avg Price</th>
                      <th style={{ ...thS, textAlign: "right" }}>Investment</th>
                      <th style={{ ...thS, textAlign: "right" }}>Lots</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ladder.map((step, i) => {
                      const prevPrice = i === 0 ? meta.underlying_ltp : ladder[i - 1].buy_price;
                      const gapPct = prevPrice && step.buy_price
                        ? ((step.buy_price - prevPrice) / prevPrice * 100)
                        : null;
                      return (
                        <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#f9f9f9" }}>
                          <td style={tdS}>{step.step}</td>
                          <td style={tdR}>{fmtRs2(step.buy_price)}</td>
                          <td style={{ ...tdR, color: gapPct == null ? "#6c757d" : gapPct >= 0 ? "#28a745" : "#dc3545" }}>
                            {gapPct != null ? `${gapPct >= 0 ? "↑" : "↓"}${gapPct >= 0 ? "+" : ""}${gapPct.toFixed(2)}%` : "—"}
                          </td>
                          <td style={tdR}>{step.qty}</td>
                          <td style={tdR}>{step.total_qty}</td>
                          <td style={tdR}>{fmtRs2(step.avg_price)}</td>
                          <td style={tdR}>{fmtRs2(step.investment)}</td>
                          <td style={tdR}>{Number(step.stock_lots).toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Actions */}
          {row.status === "PENDING" && (
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 6 }}>
              <button onClick={handleApprove} disabled={submitting || acting}
                style={{ padding: "8px 20px", background: submitting ? "#ccc" : "#28a745", color: "#fff", border: "none", borderRadius: 6, fontWeight: 600, cursor: submitting ? "default" : "pointer", fontSize: 13 }}>
                {submitting ? "Approving…" : `Sell ${meta.sell_qty ?? ""} Shares`}
              </button>
              <button onClick={onReject} disabled={acting}
                style={{ padding: "8px 14px", background: "#dc3545", color: "#fff", border: "none", borderRadius: 6, fontWeight: 600, cursor: "pointer", fontSize: 13 }}>
                Reject
              </button>
              <button onClick={() => onSnooze(60)} disabled={acting}
                style={{ padding: "8px 14px", background: "#fff", border: "1px solid #ccc", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>
                Snooze 1h
              </button>
              <button onClick={() => onSnooze(480)} disabled={acting}
                style={{ padding: "8px 14px", background: "#fff", border: "1px solid #ccc", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>
                Snooze 8h
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── RolloutDhssStrangleCard ─────────────────────────────────── */

function RolloutDhssStrangleCard({ row, expanded, onToggle, onReject, onSnooze, onOptimisticApprove, acting, checked, onCheck, username, showAccount, flash, fetchAll }) {
  const isActivelySnoozed = row.status === "PENDING"
    && row.snooze_until
    && new Date(row.snooze_until.replace(" ", "T") + "Z") > new Date();
  const displayStatus = isActivelySnoozed ? "SNOOZED" : row.status;
  const sm = STATUS_META[displayStatus] || STATUS_META.PENDING;
  const [submitting, setSubmitting] = useState(false);

  const meta = row.metadata || {};

  const fmtRs  = v => v != null ? `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}` : "—";
  const fmtN   = (v, d = 2) => v != null ? Number(v).toFixed(d) : "—";
  const fmtPct = v => v != null ? `${Number(v).toFixed(2)}%` : "—";

  const lotSize    = meta.lot_size    || 1;
  const lots       = meta.lots_per_side || 1;
  const totalQty   = lots * lotSize;

  const ceDistPct  = meta.proposed_call_dist_pct;
  const peDistPct  = meta.proposed_put_dist_pct;

  const parseDte = (expStr) => {
    if (!expStr) return null;
    const [m, d, y] = expStr.split("/");
    const exp = new Date(+y, +m - 1, +d);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return Math.max(0, Math.round((exp - today) / 86400000));
  };
  const nextDte = parseDte(meta.next_expiry);

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      await axios.post("/automation/suggestions/approve", { id: row.id, username });
      onOptimisticApprove?.();
      flash("Rollout approved — strategy updating to next expiry");
      fetchAll();
    } catch { flash("Approval failed.", false); }
    finally { setSubmitting(false); }
  };

  const thS = { padding: "6px 10px", textAlign: "left", fontWeight: 600, fontSize: 11, borderBottom: "2px solid #dee2e6", color: "#555" };
  const thR = { ...{ padding: "6px 10px", fontWeight: 600, fontSize: 11, borderBottom: "2px solid #dee2e6", color: "#555" }, textAlign: "right" };
  const tdS = { padding: "6px 10px", fontSize: 12, borderBottom: "1px solid #f0f0f0" };
  const tdR = { ...{ padding: "6px 10px", fontSize: 12, borderBottom: "1px solid #f0f0f0" }, textAlign: "right" };

  return (
    <div style={{ ...card, border: "2px solid #17a2b8", outline: checked ? "2px solid #007bff" : "none" }}>
      {/* ── Header ── */}
      <div style={cardTop}>
        {row.status === "PENDING" && (
          <input type="checkbox" checked={!!checked} onChange={onCheck} onClick={e => e.stopPropagation()}
            style={{ marginRight: 10, width: 15, height: 15, cursor: "pointer", flexShrink: 0, accentColor: "#007bff" }} />
        )}
        {row.status !== "PENDING" && <div style={{ width: 25, flexShrink: 0 }} />}
        <div style={{ display: "flex", alignItems: "center", flex: 1, gap: 12, cursor: "pointer" }} onClick={onToggle}>
          <div style={{ ...actionIcon, background: "#d1ecf1", color: "#0c5460", fontSize: 22 }}>↻</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: "#0c5460" }}>
              Rollout DHSS Strangle — {row.symbol}
            </div>
            <div style={{ fontSize: 12, color: "#555", marginTop: 2 }}>
              {showAccount && row.username && (
                <span style={{ marginRight: 8, padding: "1px 7px", borderRadius: 8, background: "#e9ecef", color: "#495057", fontSize: 11, fontWeight: 600 }}>
                  {row.username}
                </span>
              )}
              {meta.current_expiry && meta.next_expiry ? (
                <span>
                  <strong>{meta.current_expiry}</strong>
                  <span style={{ margin: "0 6px", color: "#17a2b8", fontWeight: 700 }}>→</span>
                  <strong>{meta.next_expiry}</strong>
                  {nextDte != null && <span style={{ marginLeft: 6, color: "#888" }}>({nextDte}d to expiry)</span>}
                </span>
              ) : (
                <span style={{ color: "#888" }}>New expiry loading…</span>
              )}
              {meta.spot_ltp != null && (
                <span style={{ marginLeft: 10, color: "#555" }}>Spot {fmtRs(meta.spot_ltp)}</span>
              )}
            </div>
          </div>
          <div style={{ textAlign: "right", minWidth: 140 }}>
            <span style={{ ...statusBadge, background: sm.bg, color: sm.color }}>{sm.label}</span>
            <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>{toIST(row.created_at)}</div>
          </div>
          <div style={{ marginLeft: 12, color: "#ccc", fontSize: 18 }}>{expanded ? "▲" : "▼"}</div>
        </div>
        <NavButtons symbol={row.symbol} />
      </div>

      {/* ── Expanded body ── */}
      {expanded && (
        <div style={cardBody}>

          {row.status === "FAILED" && row.error_message && (
            <div style={{ background: "#fde2e2", border: "1px solid #dc3545", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#dc3545" }}>
              <strong>Failed:</strong> {row.error_message}
            </div>
          )}

          {meta.precompute_error && (
            <div style={{ background: "#fff3cd", border: "1px solid #ffc107", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#856404" }}>
              ⚠️ Could not precompute next-expiry structure: {meta.precompute_error}. Approving will still trigger the rollout.
            </div>
          )}

          {/* Context row */}
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap", fontSize: 12, color: "#555", marginBottom: 14, paddingTop: 8 }}>
            {meta.spot_ltp    != null && <span><strong>Spot LTP:</strong> {fmtRs(meta.spot_ltp)}</span>}
            {meta.lot_size    != null && <span><strong>Lot Size:</strong> {meta.lot_size}</span>}
            {meta.lots_per_side != null && <span><strong>Lots/Side:</strong> {meta.lots_per_side}</span>}
            {nextDte          != null && <span><strong>DTE:</strong> {nextDte}d</span>}
            {meta.total_premium_per_share != null && (
              <span><strong>Premium/Share:</strong> {fmtRs(meta.total_premium_per_share)}</span>
            )}
          </div>

          {/* Proposed legs table */}
          {(meta.proposed_call_symbol || meta.proposed_put_symbol) && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#495057", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
                Proposed New Strangle Legs
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: "#f8f9fa" }}>
                      <th style={thS}>Leg</th>
                      <th style={thS}>Symbol</th>
                      <th style={thR}>Strike</th>
                      <th style={thR}>Dist from Spot</th>
                      <th style={thR}>LTP</th>
                      <th style={thR}>Delta</th>
                      <th style={thR}>Lots</th>
                      <th style={thR}>Total Qty</th>
                      <th style={thR}>Premium</th>
                    </tr>
                  </thead>
                  <tbody>
                    {meta.proposed_call_symbol && (
                      <tr>
                        <td style={{ ...tdS, fontWeight: 700, color: "#dc3545" }}>CALL (CE)</td>
                        <td style={{ ...tdS, fontFamily: "monospace", fontSize: 11, color: "#0055aa" }}>{meta.proposed_call_symbol}</td>
                        <td style={tdR}>{meta.proposed_call_strike != null ? `₹${meta.proposed_call_strike.toLocaleString("en-IN")}` : "—"}</td>
                        <td style={{ ...tdR, color: ceDistPct != null ? (ceDistPct >= 0 ? "#28a745" : "#dc3545") : "#555" }}>
                          {ceDistPct != null ? `${ceDistPct >= 0 ? "+" : ""}${ceDistPct.toFixed(2)}%` : "—"}
                        </td>
                        <td style={tdR}>{fmtRs(meta.proposed_call_ltp)}</td>
                        <td style={tdR}>{fmtN(meta.proposed_call_delta, 3)}</td>
                        <td style={tdR}>{lots}</td>
                        <td style={tdR}>{totalQty}</td>
                        <td style={tdR}>{meta.proposed_call_ltp != null ? fmtRs(meta.proposed_call_ltp * totalQty) : "—"}</td>
                      </tr>
                    )}
                    {meta.proposed_put_symbol && (
                      <tr>
                        <td style={{ ...tdS, fontWeight: 700, color: "#28a745" }}>PUT (PE)</td>
                        <td style={{ ...tdS, fontFamily: "monospace", fontSize: 11, color: "#0055aa" }}>{meta.proposed_put_symbol}</td>
                        <td style={tdR}>{meta.proposed_put_strike != null ? `₹${meta.proposed_put_strike.toLocaleString("en-IN")}` : "—"}</td>
                        <td style={{ ...tdR, color: peDistPct != null ? (peDistPct >= 0 ? "#28a745" : "#dc3545") : "#555" }}>
                          {peDistPct != null ? `${peDistPct >= 0 ? "+" : ""}${peDistPct.toFixed(2)}%` : "—"}
                        </td>
                        <td style={tdR}>{fmtRs(meta.proposed_put_ltp)}</td>
                        <td style={tdR}>{fmtN(meta.proposed_put_delta, 3)}</td>
                        <td style={tdR}>{lots}</td>
                        <td style={tdR}>{totalQty}</td>
                        <td style={tdR}>{meta.proposed_put_ltp != null ? fmtRs(meta.proposed_put_ltp * totalQty) : "—"}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Profit / breakeven summary */}
          {(meta.max_profit != null || meta.call_breakeven != null || meta.put_breakeven != null) && (
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 14 }}>
              {meta.max_profit != null && (
                <div style={{ background: "#d4edda", borderRadius: 8, padding: "8px 14px", fontSize: 12 }}>
                  <div style={{ fontSize: 11, color: "#155724", fontWeight: 600 }}>Max Profit</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#155724" }}>{fmtRs(meta.max_profit)}</div>
                </div>
              )}
              {meta.call_breakeven != null && (
                <div style={{ background: "#fde2e2", borderRadius: 8, padding: "8px 14px", fontSize: 12 }}>
                  <div style={{ fontSize: 11, color: "#721c24", fontWeight: 600 }}>Call Breakeven</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#721c24" }}>{fmtRs(meta.call_breakeven)}</div>
                </div>
              )}
              {meta.put_breakeven != null && (
                <div style={{ background: "#fde2e2", borderRadius: 8, padding: "8px 14px", fontSize: 12 }}>
                  <div style={{ fontSize: 11, color: "#721c24", fontWeight: 600 }}>Put Breakeven</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#721c24" }}>{fmtRs(meta.put_breakeven)}</div>
                </div>
              )}
              {meta.spot_ltp != null && meta.call_breakeven != null && meta.put_breakeven != null && (
                <div style={{ background: "#e2e3e5", borderRadius: 8, padding: "8px 14px", fontSize: 12 }}>
                  <div style={{ fontSize: 11, color: "#383d41", fontWeight: 600 }}>Profit Zone Width</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#383d41" }}>
                    {fmtRs(meta.call_breakeven - meta.put_breakeven)}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Action buttons */}
          {row.status === "PENDING" && (
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 6 }}>
              <button onClick={handleApprove} disabled={submitting || acting}
                style={{ padding: "8px 20px", background: submitting ? "#ccc" : "#17a2b8", color: "#fff", border: "none", borderRadius: 6, fontWeight: 600, cursor: submitting ? "default" : "pointer", fontSize: 13 }}>
                {submitting ? "Approving…" : "Approve Rollout"}
              </button>
              <button onClick={onReject} disabled={acting}
                style={{ padding: "8px 14px", background: "#dc3545", color: "#fff", border: "none", borderRadius: 6, fontWeight: 600, cursor: "pointer", fontSize: 13 }}>
                Reject
              </button>
              <button onClick={() => onSnooze(60)} disabled={acting}
                style={{ padding: "8px 14px", background: "#fff", border: "1px solid #ccc", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>
                Snooze 1h
              </button>
              <button onClick={() => onSnooze(480)} disabled={acting}
                style={{ padding: "8px 14px", background: "#fff", border: "1px solid #ccc", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>
                Snooze 8h
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Helpers ─────────────────────────────────────────────────── */

function toIST(s) {
  if (!s) return "—";
  try {
    return new Date(s.replace(" ", "T") + "Z")
      .toLocaleString("en-IN", { timeZone: "Asia/Kolkata", hour12: false })
      .replace(",", "");
  } catch (_) {
    return s.slice(0, 16);
  }
}

function actionEmoji(type) {
  if (!type) return "⚙";
  if (type === "OPEN_STRADDLE") return "＋";
  if (type === "CLOSE_STRADDLE") return "✕";
  if (type.includes("ROLL")) return "↻";
  if (type.includes("SQUARE_OFF_STOCKS")) return "📉";
  if (type.includes("SQUARE_OFF")) return "✕";
  if (type.includes("BUY")) return "＋";
  return "⚙";
}

/* ── Styles ───────────────────────────────────────────────────── */

const pageTitle   = { fontSize: 22, fontWeight: 700, color: "#2c3e50", margin: 0 };
const toast       = { position: "fixed", top: 20, right: 20, zIndex: 9999, padding: "10px 20px", borderRadius: 8, color: "#fff", fontWeight: 600, fontSize: 14 };
const refreshBtn  = { padding: "8px 16px", borderRadius: 8, border: "1px solid #ccc", background: "#fff", cursor: "pointer", fontSize: 13 };
const statsGrid   = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(90px, 1fr))", gap: 10, marginBottom: 20 };
const tile        = { background: "#fff", borderRadius: 10, padding: "10px 12px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" };
const filterBar   = { display: "flex", gap: 6, flexWrap: "wrap" };
const filterTab   = { padding: "6px 12px", borderRadius: 20, border: "1px solid", cursor: "pointer", fontSize: 12, fontWeight: 500, transition: "all .15s" };
const emptyBox    = { background: "#fff", borderRadius: 12, padding: "60px 20px", textAlign: "center", color: "#888", boxShadow: "0 2px 10px rgba(0,0,0,0.05)" };

const card        = { background: "#fff", borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.06)", overflow: "hidden" };
const cardTop     = { display: "flex", alignItems: "center", padding: "14px 18px", cursor: "pointer" };
const cardBody    = { padding: "0 18px 16px", borderTop: "1px solid #f0f0f0" };

const actionIcon  = { width: 40, height: 40, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 700, flexShrink: 0 };
const statusBadge = { display: "inline-block", padding: "3px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600 };

// Why a DOWNSIDE_FLOOR_* rule fired (docs/FPH_DELTA_BAND_PLAN.md §8.5) — shown
// on the rolldown / add-lot / rollout cards only when the backend attached
// metadata.downside_floor, so weak-hedge suggestions render unchanged.
function DownsideFloorPanel({ meta }) {
  const df = meta?.downside_floor;
  if (!df) return null;
  const fmtRs = v => v != null ? `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 0 })}` : "—";
  const clr = v => v == null ? "#888" : v >= (df.min_profit_csm ?? 0) ? "#28a745" : "#dc3545";
  const cleared = df.rescue_mode === "floor_cleared";
  return (
    <div style={{ ...metaBox, marginTop: 0, borderLeft: `4px solid ${cleared ? "#28a745" : "#e0a800"}` }}>
      <div style={metaTitle}>Downside Profit Floor — {cleared ? "restores Min Profit (CSM)" : "improves, still below Min Profit (CSM)"}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 32px", fontSize: 13 }}>
        <span><strong>Expiry potential incl. booked:</strong>{" "}
          <span style={{ color: clr(df.potential_before) }}>{fmtRs(df.potential_before)}</span> →{" "}
          <span style={{ color: clr(df.potential_after), fontWeight: 700 }}>{fmtRs(df.potential_after)}</span>
        </span>
        <span><strong>Floor:</strong> {fmtRs(df.min_profit_csm)}</span>
        <span><strong>Premium gain:</strong> {fmtRs(df.premium_gain)}</span>
        {df.hedge_pct_after != null && (
          <span><strong>Hedge:</strong> {df.hedge_pct_before}% → {df.hedge_pct_after}%</span>
        )}
      </div>
    </div>
  );
}

const metaBox     = { background: "#f8f9fb", borderRadius: 8, padding: "10px 14px", marginBottom: 12, marginTop: 12 };
const metaTitle   = { fontSize: 11, fontWeight: 600, color: "#6c757d", textTransform: "uppercase", marginBottom: 6 };
const metaPre     = { margin: 0, fontSize: 12, color: "#2c3e50", whiteSpace: "pre-wrap", wordBreak: "break-word" };

const btnApprove  = { padding: "7px 16px", borderRadius: 8, border: "none", background: "#28a745", color: "#fff", cursor: "pointer", fontWeight: 600, fontSize: 13 };
const btnReject   = { padding: "7px 16px", borderRadius: 8, border: "1px solid #dc3545", background: "#fff", color: "#dc3545", cursor: "pointer", fontSize: 13 };
const btnSnooze   = { padding: "7px 14px", borderRadius: 8, border: "1px solid #dee2e6", background: "#f8f9fa", cursor: "pointer", fontSize: 12, color: "#555" };

const navBtnStyle = { padding: "4px 10px", borderRadius: 6, border: "1px solid #dee2e6", background: "#f8f9fa", color: "#495057", cursor: "pointer", fontSize: 11, fontWeight: 600, textDecoration: "none", whiteSpace: "nowrap" };
const bulkBar     = { display: "flex", alignItems: "center", justifyContent: "space-between", background: "#eef3fb", border: "1px solid #c5d8f7", borderRadius: 10, padding: "10px 16px", marginBottom: 12, flexWrap: "wrap", gap: 10 };
const bulkLinkBtn = { background: "none", border: "none", color: "#007bff", cursor: "pointer", fontSize: 13, padding: "0 4px", textDecoration: "underline" };
const th          = { padding: "4px 8px", textAlign: "left", fontWeight: 600 };
const td          = { padding: "5px 8px", color: "#2c3e50" };
